declare module Meevo.Model {
    import BusinessInfoDataService = Meevo.Components.Common.BusinessInfoDataService;

    export interface ISelectItem<T> {
        value: T;
        label: string;
    }

    interface RevertData {
        effectiveLevel?: number;
        revertToValue?: string;
    }

    interface BaseItemRequest {
        locationId?: number;
        languageId?: number;
        errorMessage?: string;
        itemId?: string;
        filterName?: string;
    }

    interface BaseRequest<T> { }

    interface MeevoRequest<T> extends BaseRequest<T> { }

    interface SearchPage<T, TSearch> extends MeevoRequest<Array<T>> {
        criteria?: TSearch;
        pageNumber?: number;
        itemsPerPage?: number;
        sortBy?: string;
        sortDirection?: SortDirection;
        asLocationId?: number;
    }

    interface Override<T> extends MeevoRequest<T> {
        Model: T;
    }

    interface ChildListRequest<T> extends BaseRequest<Array<T>> {
        // @ApiMember(Name="ParentId", Description="Parent Id of the List you want to retrieve", ParameterType="path", DataType="string", IsRequired=true, Verb="Get")
        parentId?: string;
    }

    interface PageRequest<T> extends BaseRequest<Array<T>> {
        // @ApiMember(Name="PageNumber", Description="Page number you want to get", ParameterType="query", DataType="integer", Verb="Get")
        pageNumber?: number;

        // @ApiMember(Name="ItemsPerPage", Description="Number of records to return ", ParameterType="query", DataType="integer", Verb="Get")
        itemsPerPage?: number;

        // @ApiMember(Name="Filter", Description="Search filter string", ParameterType="query", DataType="string", Verb="Get")
        filter?: string;

        // @ApiMember(Name="SortBy", Description="Sort results by columns (comma-delimited) ", ParameterType="query", DataType="string", Verb="Get")
        sortBy?: string;

        // @ApiMember(Name="SortDirection", Description="Sort Direction export enum Value (Asc|Desc)", ParameterType="query", DataType="string", Verb="Get")
        sortDirection?: SortDirection;
    }

    interface GetBase<TModel, TKey> {
        // @ApiMember(Name="Id", Description="Id of the model you want to retrieve", ParameterType="path", DataType="string", IsRequired=true, Verb="Get")
        id?: TKey;

        // @ApiMember(Name="Return", Description="Return Depth of Model (Slim | Full)", ParameterType="query", DataType="string", Verb="Get")
        return?: GetResult;
    }

    interface Get<T> extends GetBase<T, string> { }

    interface SecurityRoleAccess {
        changed?: boolean;
    }

    interface SmartTileGet {
        id?: Guid;
    }

    interface SmartCenterGet extends BaseRequest<Array<SmartTileData>>, IReturn<Array<SmartTileData>> {
        id?: string;
    }

    interface GetEnumRequest extends MeevoRequest<Array<EnumDto>> {
        // @ApiMember(Name="EnumType", Description="EnumType of the list you want to receive", ParameterType="path", DataType="enum", IsRequired=true, Verb="Get")
        enumType?: EnumTypeEnum;
    }

    interface FieldLabels { }

    interface SmartCenterSettings { }

    interface Location {
        locationId?: number;
        storeName?: string;
        storeNumber?: string;
        subDomainUrl?: string;
        subscriptionStatus?: SubscriptionStatusEnum;
        billDay: number;
        parentLocationId?: number;
        defaultLanguageId?: number;
        maxServiceProvidersAllowed?: number;
        meevoEditionType?: MeevoEditionTypeEnum;
        city?: string;
        state?: string;
        zipCode?: string;
        genderSpecificAnonClient?: boolean;
        defaultAppointmentBookColumnZoom?: number;
        defaultAppointmentBookTimeZoom?: number;
        locationTimeZone?: string;
        timeZoneOffset?: Date;
        customerNumber?: string;
        priceGroup_TId?: string;
        priceGroupDisplayName?: string;
        subscriptionActivationDate?: Date;
        subscriptionTerminationDate?: Date;
        subscriptionStartDate?: Date;
        locationStatus?: LocationStatusEnum;
        locationReactivationDate?: Date;
        locationClosedDate?: Date;
        address1?: string;
        address2?: string;
        country?: string;
        latitude?: string;
        longitude?: string;
        taxNumber?: number;
        countryCode?: string;
        phoneNumber?: string;
        emailAddress?: string;
        webAddressUrl?: string;
        businessSize?: string;
        businessTypeEnum?: BusinessTypeEnum;
        cultureInfo?: string;
        areaSalesManager?: string;
        areaInstallManager?: string;
        entityVersion?: number;
        businessInfoEntityVersion?: number;
        externalId?: number;
        salesForceId?: string;
        isCentralOffice?: boolean;
        isCallCenter?: boolean;

        includeDiscount?: boolean;
        includeClientVisitNoteType?: boolean;
        includeGiftCardType?: boolean;
        includeReferralType?: boolean;

        // todo
        businessTypeDisplayName?: string;
        businessTypeId?: string;
        settings?: Array<IBusinessSetting>;
        statusEft?: IBusinessSetting;
        enabledAutomaticBillingService?: IBusinessSetting;
        enabledMembershipProcesses?: IBusinessSetting;

        enableAppointmentNotification?: IBusinessSetting;
        enableOnlineWalkinManager?: IBusinessSetting;
        enableOnlineBooking?: IBusinessSetting;
        enableThirdPartyIntegration?: IBusinessSetting;
        address?: Address;
        smsCount?: number;

        editionPlanId?: number;
        smsPlanId?: number;
        supportPlanId?: number;
    }

    interface ILocationListItemBase {
        id: number;
        storeName: string;
        storeNumber: string;
        address1: string;
        address2: string;
        city: string;
        state: string;
        zipCode: string;
        externalId: string;
        businessTypeName: string;
        currentMarketName: string;
        inTransfering: boolean;
        businessTypeDisplayName?: string;
        locationStatusEnum?: LocationStatusEnum;
        locationStatusDisplayName?: string;
        subscriptionStatus?: SubscriptionStatusEnum;
        subscriptionStatusName?: string;
        dateCreated?: Date;
        subscriptionActivationDate?: Date;
    }

    interface Address {
        id?: string;
        address1?: string;
        address2?: string;
        state?: string;
        city?: string;
        zipCode?: string;
        country?: string;
        isNotDeliverable?: boolean;
    }

    interface Client extends DataMaintenance, IStorageFile {
        // @Ignore()
        address?: Address;

        emailAddress?: string;
        // @Ignore()
        notificationOptions?: CommunicationTypeNotification[];

        // @Ignore()
        phoneNumbers?: Phone[];

        meevoFile?: any;
        clientTypes?: Array<any>;
        dateCreated?: string;
        prefix?: string;
        firstName?: string;
        middleName?: string;
        lastName?: string;
        gender?: GenderEnum;
        pronoun?: PronounEnum;
        birthMonth?: number;
        birthDay?: number;
        anniversaryDate?: string;
        isRestricted?: boolean;
        referredByEnum?: ReferredByEnum | 0;
        referredById?: string;
        referredByFirstName?: string;
        referredByLastName?: string;
        // @Ignore()
        referredByDetail?: ReferralDetail;

        isMinor?: boolean;
        guardianId?: Guid;
        contactName?: string;
        countryCode?: string;
        contactPhone?: string;
        contactEmail?: string;
        onlineBookingAccess?: boolean;
        homeLocationId?: number;

        noConfirmations?: boolean;
        allowEmailNotificationOptIn?: boolean;
        allowSMSNotificationOptIn?: boolean;
        isClientEmailOptInEnabledDefault?: boolean;
        isClientSMSOptInEnabledDefault?: boolean;
        forceGenderSelection?: boolean;
        totalSpentService?: number;
        totalSpentProduct?: number;
        totalCountVisit?: number;
        requireNewClientPhoneNumber?: boolean;
        requireNewClientEmail?: boolean;
        requireNewClientReferralType?: boolean;

        isPrivate: boolean;
        isEmailOptedIn?: boolean;
        isMarketingEmailEnabled?: boolean;
        isEmailSpammed?: boolean;
        isTransient: boolean;
        doNotConfirm: boolean;
        allowChargesCreditOnAccount?: boolean;
        accountSpendingLimit?: DecimalRoundingPlaceEnum;
        effectiveAccountSpendingLimit?: DecimalRoundingPlaceEnum;
        externalUrl?: string;
        clientEmployeeLinkList?: ClientEmployeeLink[];
        displayName: string;
        isRegisteredObUser: boolean;
        primaryPhoneNumberObj?: Phone;
        noSellSharePersonalInfo: boolean;
        familyMembers: any[];
        canStartFamily?: boolean;
        familyExists?: boolean;
        headOfHouseholdId?: Guid | string;
        isHeadOfHousehold?: boolean;
        clientType?: ClientTypeEnum;

        isLockedForEditing: boolean;
        restrictedByLocationId?: number;
        emmaOptedOut?: boolean;
        isNotDeliverable?: boolean;

        allowOnlineMembershipSale?: boolean;
    }

    interface IClietnTransactionHistory {
        // todo: dev
    }

    // @Route("/Resource", "GET POST PUT")
    interface Resource extends DataMaintenanceWithLanguage {
        canBeSplit: boolean;
        capacity: number;
        resourceCategory: ResourceCategoryDetail;
        resourceCategoryId: Guid;
        foregroundColor?: string;
        backgroundColor?: string;
        services: ListItemWithLanguage[];
        selectedServices: any[];
    }

    interface ClientBookingPreferences extends DataMaintenance {
        id?: string;
        frequencyOfVisit?: number;
        services?: ClientBookingPreferencesService[];
    }

    interface ServiceFrequencyOfVisit extends DataDetail {
        frequencyOfVisit?: number;
    }

    interface ClientBookingPreferencesService extends ServiceFrequencyOfVisit {
        frequencyOfVisit?: number;
        serviceGlobalId?: string;
        enableIntelliBooking?: boolean;
        serviceSteps?: ServiceStepOverride[];
        employeeStepOverides?: EmployeeStepOverides[];
    }

    interface EmployeeStepOverides {
        employeeGlobalId?: string;
        serviceGlobalId?: string;
        steps?: ServiceStepOverride[];
    }

    interface ClientServiceStepOverrides {
        clientId?: string;
        serviceGlobalId?: string;
        employeeGlobalId: string;
        displayName?: string;
        steps?: EmployeeStepOverides[]
    }

    interface ClientEmployeeServiceStepOverrides {
        clientId?: string;
        ServiceGlobalId?: string;
        stepOverrides?: EmployeeServiceStepOverride[];
    }

    interface ServiceEmployeeStepOverride {
        serviceId?: string;
        stepOverrides?: EmployeeServiceStepOverride[];
    }


    // @Route("/ResourceCategory", "GET POST PUT")
    interface ResourceCategory extends DataMaintenanceWithLanguage {
        foregroundColor?: string;
        backgroundColor?: string;
        catDisplayName?: string;
        canBeSplit: boolean;
        capacity: number;
        services: ListItemWithLanguage[];
        locationOverride?: LocationOverride;
        selectedServices: any[];

        // TODO: Extract out into interface (i.e. IHasCoModelProperties)
        ownedByLocationName?: string;
        totalLocations?: number;
        availableAtLocations?: number;
    }

    // @Route("/Distributor", "GET POST PUT")
    interface Distributor extends DataMaintenanceWithLanguage, IHasLocationOverride {
        contactName?: string;
        contactPhone?: string;
        webSiteAddress?: string;
        emailAddress?: string;
        accountNumber?: string;
        note?: string;
        address?: Address;

        // TODO: Extract out into interface (i.e. IHasCoModelProperties)
        ownedByLocationName?: string;
        totalLocations?: number;
        availableAtLocations?: number;
    }

    // @Route("/Department", "GET POST PUT")
    interface Department extends DataMaintenanceWithLanguage, IHasLocationOverride { }

    interface DrawerListItem {
        id?: string;
        displayName?: string;
        drawerState?: DrawerStateEnum;
        posStationId?: string;
    }

    interface Drawer extends DataMaintenanceWithLanguage, IHasLocationOverride {
        cashAmount?: number;
        id?: string;
        drawerState?: DrawerStateEnum;
        posStation?: DataDetail;
        posStations?: posDrawer[];
        drawerSession?: DrawerSession;
        printerId?: string;
        printer?: Device;
        privateUserId?: Guid;
        privateUser?: any;
        receiptTypeEnum?: Model.RegisterReceiptTypeEnum;
        merchantAccountId?: Guid;
        merchantAccount?: any;
        merchantAccountTerminalId?: Guid;
        merchantProviderId?: number;
        creditCardTerminalId?: Guid;
        tipEntryMethodEnum?: Model.TipEntryMethodEnum;
        signatureEntryMethodEnum?: Model.SignatureEntryMethodEnum;
        isOverShortClosingNoteRequired?: boolean;
        numberItemizedReceiptsToPrint?: number;
        tenantEntityVersion?: number;
    }

    interface DrawerSession {
        id?: string;
        actualCashInDrawer?: number;
        entityVersion?: number;
        drawerId?: Guid;
        drawerState?: DrawerStateEnum;
        openCashAmount?: number;
        closeCashAmount?: number;
        previousSessionCloseCashAmount?: number;
        cashLeftInDrawer?: number;
        drawerSessionHistory?: DrawerSessionStateHistory[];
        notes?: DrawerSessionNote[];
        payInOutList?: DrawerSessionPayInOut[];
        note?: string;
        aggregatedPaymentTransactions?: PaymentTransaction[];
        summary?: DrawerSessionSummary;
        suppressWarnings?: boolean;
    }

    interface PastTransactionContext {
        pastDateTime: Date;
        drawerId: string;
        drawerSessionId: string;
    }

    interface DrawerSessionListItem {
        id: string;
        drawerId: string;
        drawerDisplayName?: string;
        openCashAmount: number;
        totalOverShort?: number;
        openedDate: Date;
        closedDate?: Date;
        openedById?: string;
        openedByDisplayName?: string;
        closedById?: string;
        closedByDisplayName?: string;
        objectState: Model.ObjectStateEnum;
        hasNote: boolean;
    }

    interface PatchDrawerSession extends DrawerSession {
        updateState?: DrawerStateEnum;
    }

    interface DrawerSessionSummary {
        totalTip?: number;
    }

    interface DrawerSessionStateHistory {
        id: string;
        userDisplayName?: string;
        userId?: string;
        drawerState?: DrawerStateEnum;
        dateUpdated?: string;
    }

    interface DrawerSessionNote {
        id: string;
        drawerState?: DrawerStateEnum;
        note?: string;
    }

    interface DrawerSessionPayInOut extends PayInOut {
        userDisplayName?: string;
        userId?: string;
        dateCreated: string;
        reasonDisplayName: string;
    }

    interface PayInOut {
        id?: string;
        cashAmount: number;
        reason: PayInOutReasons;
        note: string;
        drawerId: string;
        drawerSessionId: string;
        payInOutType: PayInOutEnum;
        payInOutDateTime?: string;
        paymentCategory: PaymentCategoryEnum;
        checkNumber?: string;
    }

    interface PaymentTransaction extends SalePayment {
        paymentTypeDisplayName: string;
        paymentCategory?: PaymentCategoryEnum;
        transactionId?: number; // transaction id
        userId: string;
        userDisplayName: string;
        saleDate: string;
        transactionDateTime?: string;
        clientId?: string;
        clientType: ClientTypeEnum;
        clientDisplayName: string;
        paymentId?: string; // check number, credit card number
        actualAmount?: number;
        hasChangeDueNoTip?: boolean;
        hasChangeDue?: boolean;
        isTipLeft?: boolean;
        isSelected?: boolean;
    }

    interface PaymentTransactionCriteria extends Model.SearchCriteria {
        drawerId?: string;
        paymentTypeId?: string;
    }

    interface Denomination {
        currency?: CurrencyEnum;
        moneyAmount: number;
        bundleQuantity: number;
        isCashRoundingInterval?: boolean;
        isCashRoundingDefault?: boolean;
    }

    interface DenominationAmount extends Denomination {
        quantity?: number;
        amount?: number;
    }

    interface DrawerPreferences {
        lastUsedDrawerId?: Guid;
        lastUsedPrinterId?: Guid;
        lastUsedCreditCardTerminalId?: Guid;
    }

    interface DrawerHistoryCriteria extends Meevo.Model.SearchCriteriaWithObjectState {
        drawerId?: string;
        drawerState?: Model.DrawerStateEnum | string;
        startDate?: Date | string;
        endDate?: Date | string;
        location?: any;
        paymentTypeId?: string;
    }

    interface posDrawer extends DataDetail { }

    interface FloorLocation extends DataMaintenanceWithLanguage {
        shortcut?: string;
    }

    interface GiftCardType extends DataMaintenanceWithLanguage {
        isOwner?: boolean;
        isPromo?: boolean;
        isPromoLiability?: boolean;
        validFromDate?: Date;
        validToDate?: Date;
        weeksValidFor?: number;
        defaultPrice?: number;
        valuePrice?: number;
        allowTowardTax?: boolean;
        allowTowardsTips?: boolean;
        serviceDetail?: Object;
    }

    interface DrawerManager extends DataMaintenanceWithLanguage, IHasLocationOverride {
        id: string;
    }

    // @Route("/Manufacturer", "GET POST PUT")
    interface Manufacturer extends DataMaintenanceWithLanguage, IHasLocationOverride {
        contactName?: string;
        countryCode?: string;
        contactPhone?: string;
        webSiteAddress?: string;
        emailAddress?: string;
        locationNote?: string;
        address?: Address;

        // TODO: Extract out into interface (i.e. IHasCoModelProperties)
        ownedByLocationName?: string;
        totalLocations?: number;
        availableAtLocations?: number;
        categoryId?: string;
    }

    interface Tax {
        id?: Guid;
        taxPolicy?: TaxPolicyEnum;
        currency?: CurrencyEnum;
        roundingMode?: Meevo.Model.RoundingModeEnum;
        decimalPlaceRounding?: Meevo.Model.DecimalRoundingPlaceEnum;
        cashRoundingInterval?: number;
        cashRoundingMode?: Meevo.Model.CashRoundingModeEnum;
        taxTypes?: TaxType[];
        taxItems?: TaxItem[];
        isTaxIncludedInPrice?: boolean;
        isTransferFeeTaxable?: boolean;
        isTaxOnTax?: boolean;
        taxAppliedFromTaxTypeId?: string;
        taxAppliedOntoTaxTypeId?: string;
        taxSettings: TaxSettings;
    }

    interface Aveda {
        id: string;
        objectState?: ObjectStateEnum;
        distributor: AvedaDistributor;
        distributors: AvedaDistributor[];
        clientId: string;
        hasValidToken: boolean;
        authorizeUrl: string;
        externalInventoryAccessType?: ExternalInventoryAccessTypeEnum;
    }

    interface AvedaDistributor extends DataDetail {
        isAvedaOrderingEnabled: boolean;
        isCoOwned?: boolean;

    }

    interface ExternalOrderingAccount {
        id: string;
        objectState?: ObjectStateEnum;
        clientId: string;
        hasValidToken: boolean;
        authorizeUrl: string;
        externalInventoryAccessType?: ExternalInventoryAccessTypeEnum;
    }

    interface SalonInteractive extends ExternalOrderingAccount  {
        distributor: SalonInteractiveDistributor;
        distributors: SalonInteractiveDistributor[];
    }

    interface SalonInteractiveDistributor extends DataDetail {
    }

    interface TaxType extends DataDetail {
        isActive: boolean;
        sortOrder?: number;
        taxTypeFromDb?: number;
    }

    interface TaxItem extends DataDetail {
        taxItemType?: TaxItemTypeEnum;
        isDefault: boolean;
        taxItemRates?: TaxItemRate[];
        isActive: boolean;
        sortOrder: number;
        taxItemFromDb?: number;
    }

    interface TaxItemRate {
        taxItem_LId?: Guid | string;
        taxType_LId?: Guid | string;
        taxRate?: number;

        displayName?: string;
        isDefault?: boolean;
        isActive?: boolean;
    }

    interface PayInOutReasons extends DataMaintenanceWithLanguage, IHasLocationOverride {
        payInOutType?: PayInOutEnum;
    }

    interface ProductDistributor extends DataDetail {
        isCoOwned?: boolean;
    }

    interface Manufacturer extends DataDetail { }

    interface ProductSubCategory extends DataDetail { }

    interface Product extends DataMaintenanceWithLanguage, IHasLocationOverride {
        manufacturer?: Manufacturer;
        manufacturerId?: Guid;
        productSubCategory?: ProductSubCategory;
        productSubCategoryId?: Guid;
        distributors?: ProductDistributor[];
        distributor?: ProductDistributor;
        upcCode?: string;
        size?: string;
        // isBarcodedByManufacturer?: boolean;
        isBarcodedByManufacturer?: boolean;
        productType?: ProductTypeEnum;
        orderQuantity?: number;
        orderPoint?: number;
        defaultOrderMultiplesOf?: number;
        onHandQuantity?: number;
        defaultWholeSalePrice?: number;
        markupPercentage?: number;
        retailPrice?: number;
        tax?: number;
        isIncludedInAverageTicketCalculations?: boolean;
        shouldIgnoreAutomaticDiscounts?: boolean;
        variants?: Variant[];
        //include and exclude location
        ownedByLocationName?: string;
        totalLocations?: number;
        availableAtLocations?: number;
        unitEnum?: UnitEnum;
        usageTracking?: boolean;
        partialUsed?: boolean;
    }
    interface BulkUpdateSchedule {
        __type?: string;
        id: string;
        selectedEntities?: ProductBulkUpdateEntity[];
        objectState: ObjectStateEnum;
        status: BulkUpdateStatusEnum;
        scheduleType: BulkUpdateTypeEnum;
        bulkUpdateParameters?: ProductBulkUpdateParameters;
    }
    interface ProductBulkUpdate extends BulkUpdateSchedule {
    }

    interface BulkUpdateEntity {
        __type?: string;
        id: Guid;
        displayName: string;
        variantId?: Guid;
        isCOOwned?: boolean;
    }
    interface ProductBulkUpdateEntity extends BulkUpdateEntity {
    }

    interface ProductBulkUpdateParameters {
        __type?: string;
        showVariants: boolean;
        allItemsSelected: boolean;
        //Product Updates:
        deleteProducts?: boolean;
        addDistributor?: boolean;
        addDistributorModel?: Distributor;
        addDistributorId?: string;
        removeDistributor?: boolean;
        removeDistributorModel?: Distributor;
        removeDistributorId?: string;
        changeManufacturer?: boolean;
        manufacturer?: Manufacturer;
        manufacturerId?: string;
        changeProductCategory?: boolean;
        productSubCategory?: ProductSubCategory;
        productSubCategoryId?: string;
        //Variant Updates:
        isDiscontinued?: boolean;
        discontinuedChangeBehavior?: DiscontinueBehaviorEnum;
        isNotForSale?: boolean;
        notForSaleChangeBehavior?: NotForSaleBehaviorEnum;
        isDefaultOverride?: boolean;
        isNotForSaleOverride?: boolean;
        isDiscontinuedOverride?: boolean;
    }

    interface ProductUsage extends Product {
        variantUsageSettings?: VariantUsageSetting[];
        //variants?: VariantUsageSetting[];
        partialused?: boolean;
        productUnitOfMeasureEnum?: UnitEnum;
        enableProductUsageTracking?: boolean;
        productUsageDisplayOnReceipt?: boolean;
        productUsageType?: ProductUsageAmountTypeEnum;
        isInsidePackageTaxExempted?: boolean;
        insidePackageTaxItem_LId?: string;
    }

    interface VariantUsageSetting extends Variant {
        unitOfMeasureQuantity?: number;
        hasUsage?: boolean;
        chargeType?: ProductVariantChargeTypeEnum;
        chargeAmount?: number;
    }


    interface ProductCompensation extends Model.DataMaintenance {
        productCompensationSetting?: ProductCompensationSetting[];
        employeeSpecificEnrollmentCompensation?: EmployeePackageTypeCompensationSetting[];
        employeeSpecificRecurringCompensation?: EmployeePackageTypeCompensationSetting[];
    }

    interface ProductCompensationSetting {
        productCommissionSetting?: CommissionsSettingEnum;
        fee?: number;
        isPercent?: boolean;
        isEnabled?: boolean;
    }

    interface ProductVariantAttribute {
        variantType: VariantTypeEnum,
        sortOrder: number;
        displayName: string[];
        id: Guid | string;
    }

    interface Variant extends DataMaintenanceWithLanguage {
        //id?: Guid;
        displayName: string;
        barCode: string;
        sku: string;
        markUp: number;
        retail: number;
        wholeSale: number;
        //objectState: number;
        isDiscontinued: Boolean;
        variantAttributes: variantAttributes[];
        notForSale: boolean;
        sortOrder: number;
        floorLocation: DataDetail;
        floorLocations: DataDetail[];
        startOnHand: number;
        actualCount: number;
        productInventoryType: number;
        categoryId: string;
        manufacturerId: string;
        variantDistributorIds?: VariantDistributorIdItem[];
        variantDisplayName: string;
        onHand?: number;
        selected?: boolean;
        numberOfLabels?: number;
        lastPurchaseOrderState: PurchaseOrderStateEnum;
        lastPurchaseOrderDate: Date;
        salesDateRange: any;
        salesStartDate: Date;
        salesEndDate: Date;
        sales: Record<string, VariantSalesData[]>;
        activity: VariantActivity;
        variantStockLevels: any[];
    }

    interface VariantDistributorIdItem {
        variantId?: Guid;
        distributorId?: Guid;
        displayName: string;
    }

    interface VariantSalesData {
        saleMonth: string;
        quantity: number;
        amount: number;
    }

    interface VariantActivity {
        lastOrderedDate: Date;
        lastReceivedDate: Date;
        lastCountedDate: Date;
        lastSoldDate: Date;
    }

    interface StockLevelList {
        stockLevel: StockLevel[];
    }

    interface StockLevel {
        entityVersion?: number;
        id: Guid;
        productInventoryType: ProductInventoryTypeEnum;
        productPackageType?: ProductPackageTypeEnum;
        multiplesOf?: number;
        modelQuantity?: number;
        orderPoint?: number;
        onHand: number;
        productVariantName: string;
        onHandInUnits?: number;
        unitOfMeasureQuantity?: number;
        enableProductUsageTracking?: boolean;
        productUsageType?: ProductUsageAmountTypeEnum;
    }

    interface variantAttributes {
        id: Guid;
        variantType: number;
        sortOrder: number;
        displayName: string[];
    }

    enum DayOfWeek {
        sunday,
        monday,
        tuesday,
        wednesday,
        thursday,
        friday,
        saturday,
    }

    interface ProductVariantBundle extends DataDetail {
        quantity: number;
        retail?: number;
        totalRetail?: number;
        productInventoryType?: ProductInventoryTypeEnum;
        isOnHandBelowOrderPoint?: boolean;
    }

    interface ProductBundle extends DataMaintenanceWithLanguage, IHasLocationOverride {
        productSubCategory?: ProductSubCategory;
        productSubCategoryId?: Guid;

        variantBundles: ProductVariantBundle[];
        variantBundle?: ProductVariantBundle;
        variants: Variant[];
        totalBundleValue?: number;
        ownedByLocationName?: string;
        totalLocations?: number;
        availableAtLocations?: number;
        bundlePrice?: number;
        discountPercentage?: number;
        hasBeenSold: boolean;
        isCustomProductBundle: boolean;
        customBundleGroups: CustomBundleGroup[];
    }
    interface CustomBundleGroup {
        customBundleGroupId?: string;
        requiredQuantity: number;
        customProductVariantBundles: ProductVariantBundle[];
        customProductVariantBundle: ProductVariantBundle;
    }
    interface EmployeeTemplate {
        // @Ignore()
        templateSecurityRoleDetail?: DataDetail;
        // @Ignore()
        templateSmartCenterDetail?: DataDetail;
    }

    interface DataMaintenance {
        suppressWarnings?: boolean;
    }

    interface AppointmentSchedule {
        clientId?: string;
        entityVersion?: number;
        id?: string;
        hasScheduleStarted?: boolean;
        objectState?: ObjectStateEnum;
        startDate?: Date;
        startTime?: string;
        dayOfMonth?: number;
        daysOfWeek?: DayOfWeek[];
        frequency?: number;
        frequencyUnit?: DateTimeUnitEnum;
        occurrenceInInterval?: SequencePositionEnum;
        endDate?: string;
        totalOccurrences?: number;
        client?: any;
        isPerpetual: boolean;
        appointmentCategory?: any;
        appointmentCategoryId?: string;
        services?: AppointmentServiceSchedule[];
        doNotConfirm?: boolean;
        isReadOnly?: boolean;
        defineStartTimesManually?: boolean;
        dateUpdated?: string
    }

    // Needs to be in custom so that the type of startDate can be of type date as that's what the md datepicker works with
    interface AppointmentServiceSchedule {
        employeeId?: string;
        id?: string;
        serviceId?: string;
        steps?: AppointmentServiceScheduleStep[];
        service?: any;
        employee?: any;
        employeeDisplayName?: string;
        employeeFirstName?: string;
        employeeLastName?: string;
        employeePerformsService?: boolean;
        serviceDisplayName?: string;
        serviceShortCut?: string;
        serviceNotes?: ServiceNote[];
        employeeType?: EmployeeTypeEnum;
        isRequested?: boolean;
        serviceAddOns?: any[];
        manualStartTime?: Date;
    }

    interface Template {
        newTemplateDetail?: TemplateListItem;
    }

    interface BlockType {
        newAddLocationDetail?: LocationListItem;
        defaultMinutes?: number;
    }

    interface SystemSettings {
        ReportsUrl?: string;
    }

    interface IMeevoSession {
        elevatedSessions?: { [index: string]: ElevatedSession };
        locationName?: string;
        needsToChangePassword?: boolean;
        daysRemainingForPasswordChange?: number;
        sessionTimeoutMinutes?: number;
        userHostAddress?: string;
        sessionTimeoutRedirectUrl?: string;
        isAuthenticated?: boolean;
        logoutUrl?: string;
        homeLocationId?: number;
        coLocationId?: number;
        callCenterLocationId?: number;
        isSingleLocation?: boolean;
    }

    interface IBusinessType {
        // todo
        businessType_TId: Guid;
        displayName: string;
        id: Guid | string;
    }

    interface IBusinessTypeDetails {
        externalId: string;
        isForAllLocations: boolean;
        objectState: ObjectStateEnum;
        objectCreationType: ObjectCreationTypeEnum;
        id: Guid | string;
        locationId: number;
        displayName: string;
    }

    interface BlockSchedule extends DataMaintenance {
        id?: string;
        blockType?: BlockTypeListItem;
        blockTypeId?: string;
        employees?: EmployeeListItem[];
        resources?: any;
        resourceIds?: any;
        employeeIds?: string[];
        entityVersion?: number;
        objectState?: ObjectStateEnum;
        reason?: string;
        startDate?: Date;
        startTime?: string;
        dayOfMonth?: number;
        daysOfWeek?: DayOfWeek[];
        frequency?: number;
        frequencyUnit?: DateTimeUnitEnum;
        occurrenceInInterval?: SequencePositionEnum;
        endDate?: string;
        totalOccurrences?: number;
        durationInMinutes: number;
        isAllEmployees?: boolean;
        isAllResources?: boolean;
        displayReasonOnBook?: boolean;
    }

    interface AppointmentRequest {
        id?: string;
        payingClientId?: string;
        payingClient?: ClientListItem;
        dateTimeBands?: AppointmentRequestDateTimeBand[];
        notifyViaText?: boolean;
        services?: AppointmentRequestService[];
        suppressWarnings?: boolean;
        locale: string;
        timeFormat: number;
        steps?: any[];
        isOnlineBookingRequest?: boolean;
        note?: string;
        appointmentCategoryId?: string;
        appointmentCategoryDisplayName?: string;
        requireSameStartTime?: boolean;
        requireSameResource?: boolean;
        waitListId?: string;
    }

    interface AppointmentRequestDateTimeBand {
        startDate?: Date;
        endDate?: Date;
        startTime?: string;
        endTime?: string;
        daysOfWeek?: DayOfWeek[];
        startTimeTemp?: any;
        endTimeTemp?: any;
        scanTimeType?: Model.ScanTimeTypeEnum;
        selectedPreset?: any;
    }

    interface AppointmentRequestService {
        id?: string;
        serviceId?: string;
        service?: Service;
        employeeId?: string;
        employee?: Employee;
        genderPreference?: EmployeeGenderSearchEnum;
        steps?: AppointmentServiceScheduleStep[];
        stepOverrideStage?: any;
        serviceAddOns?: any[];
        hasServiceAddOn?: boolean;
        employees?: EmployeeListItem[];
        priceLevel?: any;
        employeeCatId?: string;
        employeeCat?: EmployeeCategoryListItem;
        clientId?: string;
        client?: ClientListItem;
        employeeCriteria?: any;
    }

    interface AppointmentRequestServiceStep {
        durationInMinutes?: number;
        serviceStepId?: string;
        serviceStepNumber?: number;
        serviceStepType?: ServiceStepTypeEnum;
        serviceStepDisplayName?: string;
        serviceStepTypeDisplayName?: string;
    }

    interface QuantityValueType {
        quantity?: number;
    }

    interface InventoryCount {
        id: Guid;
        displayName: string;
        productInventoryType: ProductInventoryTypeEnum;
        dateCreated: Date;
        dateUpdated: Date;
        state: InventoryCountStateEnum;
        entityVersion: number;
        lineItems: InventoryCountLineItems[];
        userName: string;
        iscounted: boolean;
        inventoryCountOnly: boolean;
        floorLocationId?: Guid;
        allInConflict?: boolean;
        orderItemsBy?: string;
        distributorIds: Guid[];
        manufacturerIds: Guid[];
        productCatIds: Guid[];
        showProdNotCntSince?: string;
        decimalPrecision?: number;
    }

    interface ProductUsageCollection {
        floor_LId?: Guid,
        sortOrder: number,
        productUsageType: ProductUsageAmountTypeEnum,
        quantity: number;
        productInventoryType: ProductInventoryTypeEnum,
        variant_TId?: Guid;
    }

    interface InventoryCountLineItems {
        id: Guid;
        variantId: Guid;
        startOnHand?: number;
        startOnHandDateTime?: Date;
        endOnHand: number;
        actualCount: number;
        actualCountUnits: number;
        productName: string;
        displayName: string;
        isCounted: boolean;
        barCode?: string;
        objectState: ObjectStateEnum;
        imageURL?: string;
        productInventoryType: ProductInventoryTypeEnum;
        sku: string;
        onHand: number;
        onHandDateTime?: Date;
        product_TId: Guid;
        floorLocationId?: Guid;
        isOverride?: boolean;
        productVariantsPerFloorLocation: ProductVariantPerFloorLocation[];
        finalCount?: number;
        finalCountUnits?: number;
        isDeleted?: boolean;
        wholeSale?: number;
        isAutosave: boolean;
        entityVersion?: string;
        manufacturer?: string;
        subCategory?: string;
        category?: string;
        supplyCategoryEnum?: SupplyCategoryEnum;
        isDirty: boolean;
        enableProductUsageTracking?: boolean;
        productUnitOfMeasureEnum?: UnitEnum;
        productUsageType?: ProductUsageAmountTypeEnum;
        unitOfMeasureQuantity?: number;
        onHandRetail?: number;
        onHandShopSupply?: number;
        inventoryCountLineItemModalList?: ProductUsageCollection[];
        totalActualCountDisplay?: number;
        decimalPrecision?: number;
        gainLoss?: number;
    }
    interface ProductVariantPerFloorLocation {
        floorLocationId?: Guid;
        variant_TId?: Guid;
        perFloorLocationActualCount: number;
        perFloorLocationActualCountUnits: number;
    }

    interface PurchaseOrder {
        id: Guid;
        productInventoryType: ProductInventoryTypeEnum;
        distributorId: Guid;
        dateCreated: Date;
        dateUpdated: Date;
        purchaseOrderState: PurchaseOrderStateEnum;
        entityVersion: number;
        lineItems: PurchaseOrderDetails[];
        userName: string;
        objectState: ObjectStateEnum;
        notes: PurchaseOrderNote;
        itemCount: number;
        itemTotal: number;
        totalPrice: number;
        autoGeneratePONumber: boolean;
        manufacturerId: Guid;
        supplyCategoryEnum?: SupplyCategoryEnum;
        purchaseOrderNumber?: string;
        distributorReferenceNumber?: string;
        dateSubmitted?: Date;
        isAvedaOrderingEnabled?: boolean;
        createdByDisplayName?: string;
        sortOption?: string;
        totalDiscount?: number;
        latestLineItemReceivedDate?: Date;
        manufacturerIds: Guid[];
        productSubCategoryIds: Guid[];
        supplyCategoryIds: SupplyCategoryEnum[];
        salonInteractiveDistributorId?: number;
        externalCartUrl?: string
    }

    interface PurchaseOrderDetails {
        id: string;
        displayName: string;
        variantId: Guid;
        orderQuantity: number;
        lineTotal: number;
        onHand: number;
        unitPrice: number;
        wholeSale: number;
        suggestedQty: number;
        orderPoint: number;
        attributes: variantAttributes;
        willSave: boolean;
        productPackageType: ProductPackageTypeEnum;
        multiplesOf: number;
        productId: Guid;
        modelQuantity: number;
        sku: string;
        barCode: string;
        variantDisplayName: string;
        productInventoryType: ProductInventoryTypeEnum;
        variantDistributorDisplayName: string;
        isAvedaOrderingEnabled: boolean;
        purchaseOrderLineItemResponseCode?: PurchaseOrderLineItemResponseCodeEnum;
        orderOfEntry?: number;
        dateAdded?: Date;
        isSelected?: boolean;
        discounts?: PurchaseOrderDiscount[];
        discountAmount?: number;
        numberOfLabels?: number;
        received?: number;
        supplyCategory?: SupplyCategoryEnum;
        isNew?: boolean;

        onHandDecimalPrecision?: number;
        unitOfMeasureQuantity?: number;
        productUnitOfMeasureEnum?: number;
        enableProductUsageTracking?: boolean;
        productUsageType?: ProductUsageAmountTypeEnum;
    }

    interface PurchaseOrderNote {
        id: Guid;
        purchaseOrderId: string;
        note: string;
        objectState: ObjectStateEnum;
        poObjectState: ObjectStateEnum;
    }

    interface PurchaseOrderReceival extends PurchaseOrder {
        purchaseOrderId?: Guid;
        summarizeHeader: boolean;
        tax: number;
        shippingAndHandling: number;
        previousTax: number;
        referenceNumber: string;
        userDisplayName: string;
        receivalLineItems: PoReceivalLineItem[];
    }

    interface PoReceivalLineItem {
        poReceivableId?: Guid;
        isTaxable: number;
        receiveQuantity: number;
        currentReceiveQuantity: number;
        receivableLineTotal: number;
        previousReceivableLineTotal: number;
        orderQuantity: number;
        productPackageType: ProductPackageTypeEnum;
        variantId: Guid | string;
        variantDisplayName: string;
        productDisplayName: string;
        multiplesOf: number;
        purchaseOrderLineTotal: number;
        unitPrice: number;
        productInventoryType: ProductInventoryTypeEnum;
        orderOfEntry?: number;
        supplyCategory?: SupplyCategoryEnum;
        variantDistributorDisplayName: string;
    }

    interface PurchaseOrderDiscount {
        type: Model.PurchaseOrderDiscountTypeEnum;
        discountValue: number;
        discountAmount: number;
        isSpread?: boolean;
    }

    interface PurchaseOrderSubmissionResponse {
        purchaseOrder: PurchaseOrder;
        statusCode: number;
        statusMessage: string;
    }

    interface InventoryTransfer {
        id: Guid;
        displayName: string;
        transferNumber: string;
        referenceNumber: string;
        transferringLocationId: number;
        transferStatus: TransferStatusEnum;
        receivingLocationId: number;
        receivalStatus?: ReceivalStatusEnum;
        haveNotes: boolean;
        shippingHandling: number;
        tax: number;
        totalValue: number;
        lineItems: InventoryTransferLineItem[];
       // objectState: ObjectStateEnum;
        entityVersion?: number;
        transferringLocation: string;
        receivingLocation: string;
        transferStatusBW?: number;
        isTransferInvalid?: boolean;
       // invalideStatusBW: boolean;
        isCo: boolean;
        isFinalUnitPriceCalculated: boolean;

    }
    interface InventoryTransferLineItem {
        id: Guid;
        transferLineItemId: Guid;
        displayName: string;
        productId: Guid;
        productDisplayName: string;
        transferQuantity: number;
        lineTotal: number;
        onHand: number;
        estimatedUnitPrice: number;
        finalUnitPrice?: number;
        unitPrice: number;
        productInventoryType: ProductInventoryTypeEnum;
        lineNumber: number;
        currentOnHand?: number;
        sku: string;
        barCode: string;
        receivingLocationOnHand?: number;
        receivingLocationModelQuantity?: number;
        receivingLocationOrderPoint?: number;
        receivingLocationMultiplesOf?: number;
        unitOfMeasureQuantity?: number;
        productUnitOfMeasureEnum?: number;
        enableProductUsageTracking?: boolean;
        productUsageType?: ProductUsageAmountTypeEnum;
    }
    interface InventoryTransferNote {
        id: Guid;
        note: string;
        invenoryTransferId: Guid;
        userId: Guid;
        viewByOtherLocation: boolean;
        locationId: number;


    }

    interface IWalkinAppointmentBase {
        clientId: Guid;
        note: string;
        isSMSEnabled: boolean;
        bookingMethod: BookingMethodEnum;
        statusEnum?: WalkInStatusEnum;
        appointmentServiceTransitionState?: AppointStateEnum;
        appointmentServiceFinalState?: AppointFinalStateEnum;
        suppressWarnings?: boolean;
    }

    interface IWalkinAppointmentSave extends IWalkinAppointmentBase {
        moveToTop: boolean;
        services: Array<IWalkinAppointmentService>;
    }

    interface IWalkinAppointmentService {
        id?: Guid;
        serviceId: Guid;
        employeeId: Guid;
        startDateTime: string;
        endDateTime: string;
        employeeGenderSearchEnum: EmployeeGenderSearchEnum;
        nonRequest: boolean;
        entityVersion?: number;
        serviceAddOns?: Array<IWalkinAppointmentAddOn>;
    }

    interface IWalkinAppointmentAddOn {
        addOnServiceId: Guid;
        appointmentServiceId: Guid;
    }

    interface IWalkInSettings {
        logoUrl: string;
        backgroundUrl: string;
        walkInConfirmationWindowWaitTimeInSec: number;
        enableClientEmailLookupForWalkIn: boolean;
        appointmentNotesEnabled: boolean;
        walkInDisplayMonitorShowTotalTime: boolean;
        walkInMostPopularServiceId: Guid | string;
        promotionalContents?: Array<IPromotionalContent>;
        waitListHeaderColor: string;
        waitListHeaderFontColor: string;
        waitListMainFontColor: string;
        termsAndConditions: ISettingsTerms;
        notifications?: ISettingsNotifications;
        systemNotifications?: ISettingsNotifications;
        notificationStates?: ISettingsNotificationStates;
        notificationPositionX: number;
        notificationClosingMins: number;
        walkInHours?: Array<ISettingsHoursItem>;
        standardBusinessHours?: Array<ISettingsHoursItem>;
        publicOnlineWaitListUrl?: string;
        customerNameFormat: CustomerNameDisplayFormatEnum;
        businessName?: string;
        mileageLimitX?: number;
        walkinEnableCustomerOnlineSignupFor: boolean;
        businessLogo?: IStorageFile;
        backgroundImage?: IStorageFile;
        promotionalContent1?: IStorageFile;
        promotionalContent2?: IStorageFile;
        promotionalContent3?: IStorageFile;
        promotionalContent4?: IStorageFile;
        walkInEmployee?: EmployeeListItem;
        walkInServices?: Service;
        hideAnyGenderOptions: boolean;
        promotion1Title?: string;
        promotion1Description?: string;
        promotion2Title?: string;
        promotion2Description?: string;
        promotion3Title?: string;
        promotion3Description?: string;
        hideWaitTimeEstimate?: boolean;
        showAddressFields?: boolean;
        enableWaitListTextNotifications?: boolean;
        settings: Array<Setting>;
        enableClientArrivalNotification?: boolean;
        defaultFirstAvailableEmployee?: boolean;
        enableIntegratedRegister?: boolean;
        customConfirmationPageMessage?: string;
        appointmentBookFullMessage?: string;
        showOnlyClockedInEmployees?: boolean;
        showKioskCurrentWaitList?: boolean;
        hideOtherWaitlistClients?: boolean;
        maximumNumberOfWalkinGuests: number;
    }

    interface ISettingsHoursItem {
        isEnabled: boolean;
        weekdayName: WeekdayNameEnum;
        weekdayNameDisplay: string;
        startTime: string;
        endTime: string;
    }

    interface PhoneNumberVerification
    {
        tenantId: number;
        locationId: number;
        phoneNumber: string;
        countryCode: string;
        emailAddress: string;
        address: Address;
        fullAddress: string;
        webAddressUrl: string;
        webAddressUrl2: string;
        contactFirstName: string;
        contactLastName: string;
        employerIdentificationNumber: string;
        tollFreeNumber: string;
        tollFreeNumberSid: string;
        customerProfileSid: string;
        trustHubSid: string;

        doingBusinessAs: string;
        businessType: string;
        businessRegistrationNumber: string;
        businessRegistrationAuthority: string;
        businessRegistrationCountry: string;
        optinConfirmationMessage: string;
        helpMessageSample: string;
        optInKeywords: string;
        privacyPolicyUrl: string;
        termsAndConditionsUrl: string;
        ageGatedContent?: boolean;
    }

    interface ISettingsNotifications {
        notificationWelcomeText: string;
        notificationPosition1Text: string;
        notificationPositionXText: string;
        notificationMissedText: string;
        notificationClosingXText: string;
        customConfirmationPageMessage: string;
        appointmentBookFullMessage: string;
    }

    interface ISettingsNotificationStates {
        notificationWelcomeState: ObjectStateEnum;
        notificationPosition1State: ObjectStateEnum;
        notificationPositionXState: ObjectStateEnum;
        notificationMissedState: ObjectStateEnum;
        notificationClosingXState: ObjectStateEnum;
    }

    interface ISettingsTerms {
        objectStateEnum: ObjectStateEnum;
        termsAndConditionsTypeEnum: TermsAndConditionsTypeEnum;
        entityVersion: number;
        termsAndConditionsText?: string;
        termsAndConditionsURL?: string;
    }

    interface IPromotionalContent {
        advertisementImageUrl: string;
        accentColor: string;
        displayName: string;
        description: string;
    }

    interface Sale {
        id?: number;
        saleDate?: Date;
        paymentLines: SalePayment[];
        saleLines: SaleLine[];
        subTotal: number;
        total: number;
        changeDue: number;
        cashRounding?: number;
        tipLines: TipLine[];
        tipPercentage?: number;
        tipAmount?: number;
        useDiscountedPrice?: boolean;
        saleStage: SaleStageEnum;
        tip: number;
        tipRefund?: number;
        baseTax?: number;
        baseTaxIncluded?: number;
        finalTax?: number;
        finalTaxIncluded?: number;
        tax: number;
        taxIncluded: number;
        taxes?: SaleLineTax[];
        holdType?: HoldTypeEnum;
        onHoldIds: any;
        onHoldEntityVersion?: number;
        mergeClientIds: string[];
        mergeOnHoldId?: Guid;
        suppressWarnings?: boolean;
        skipClientAccountValidation?: boolean;
        transactionDateTime?: string;
        transactionId?: string;
        noLinkMergeClientIds: string[];
        totalDiscounts?: number;
        noSale?: boolean;
        totalPrepaidTip: number;
        containsAvailableAutomaticDiscounts?: boolean;
        totalAmountRedeemed?: number;
        totalPrepaidTipRedeemed?: number;
        removedAutomaticDiscountIds?: Guid[];
        merchantTerminalId?: Guid | string;
        externalTerminalId?: string;
        merchantAccountId?: Guid;
        isProductTaxDeclined?: boolean;
        isServiceTaxDeclined?: boolean;
        isClientAccountUsageInvalid?: boolean;
        appointNotes?: appointNote[];
        saleNotes?: saleNote[];
        hasPurchasedItems?: boolean;
        computedGuid?: string;
        changedClientIds?: IPairOfRegisterClient[];
        automaticDiscountIdToBeApplied?: string;
        isItemSelectionComplete?: boolean;
        deviceTipBypass?: boolean;
        deviceTip?: number;
        deviceTipPercentage?: number;
        deviceServiceTotal?: number;
        discountBarCodeToBeApplied?: string;
        voidHold?: boolean;
        recalculateSaleInProgress?: boolean;
        recalculateTipInProgress?: boolean;
        hasRebookCandidates?: boolean;
        isComingFromHold?: boolean;
        selfCheckoutClientId?: string;
        selfPayments?: any[];
        locale: string;
        timeFormat: number;
        pastTransactionDateTime?: Date;
        pastTransactionDrawerId?: string;
        pastTransactionDrawerSessionId?: string;
        holdLockedDisplayName?: string;
        clientAccountBalances?: any[];
        isRefundFromTransaction?: boolean;
        isTemp?: boolean;
        syncAppointmentUpdates?: boolean;
        externalTipTransactionId?: string;
        externalTips?: any[];
        tipConvenienceFee?: number;
        tipConvenienceFeeRefund?: number;
        externalTipEmployeeIds?: string[];
        surchargeTotal?: number;
        isTippyKioskMode?: boolean;
    }

    interface IPairOfRegisterClient {
        newClient: IRegisterClient;
        oldClient: IRegisterClient;
    }

    interface SaleOnHold {
        id: Guid;
    }

    interface IRegisterClient {
        client?: Model.SaleClient;
        clientId?: string;
        clientType: Model.ClientTypeEnum;
        clientDisplayName?: string;
        appointmentId?: string;
    }

    interface SaleLine extends IRegisterClient {
        id?: Number;
        employee?: EmployeeListItem;
        employeeId?: string;
        item?: SaleLineItem;
        quantity: number;
        basePrice?: number;
        finalPrice?: number;
        baseItemPrice?: number;
        finalItemPrice?: number;
        charges?: SaleLineCharge[];
        baseCharge?: number;
        finalCharge?: number;
        baseItemCharge?: number;
        finalItemCharge?: number;
        basePriceCharge?: number;
        finalPriceCharge?: number;
        baseItemPriceCharge?: number;
        finalItemPriceCharge?: number;
        clientId: string;
        saleType: SalesTypeEnum;
        isExpanded?: boolean;
        isDetailExpanded?: boolean;
        needsCollapsing?: boolean;
        price?: number;
        taxes?: SaleLineTax[];
        isDirty?: boolean;
        discountLines?: DiscountLine[];
        consolidatedDiscountLines?: ConsolidatedDiscountLine[];
        prepaidTip?: number;
        prepaidTipRedeemed?: number;
        packageRedemptionPath?: Model.PackageRedemptionPath;
        redeemedPackages?: any[];
        packageRedeemed?: boolean;
        isDeleteWithPackageRedeemed?: boolean;
        packageRedemptionType?: Model.PackageRedemptionTypeEnum | 0;
        isNew?: boolean;
        quantitySelected?: number;
        quantityRequired?: number;
        isAddOn?: boolean;
        removedAutomaticDiscountIds?: string[];
        newQuantity?: number;
        originalQuantity?: number;
        isRefund?: boolean;
        tax?: number;
        baseTax?: number;
        finalTax?: number;
        baseTaxIncluded?: number;
        finalTaxIncluded?: number;
        originalBaseTax?: number;
        originalFinalTax?: number;
        originalTaxes?: SaleLineTax[];
        originalBaseCharge?: number;
        originalFinalCharge?: number;
        originalBaseItemCharge?: number;
        originalFinalItemCharge?: number;
        originalCharges?: SaleLineCharge[];
        saleId?: number;
        saleDate?: string;
        locationId?: number;
        refundSaleId?: number;
        refundSaleLineId?: Number;
        refundSaleDate?: string;
        refundLocationId?: number;
        refundTransactionId?: string;
        employeeDisplayName?: string;
        itemDisplayName?: string;
        itemId?: string;
        transactionId?: string;
        newFinalItemPrice?: number; // only client side; binded to price field in expanded smart receipt content;
        newFinalItemCharge?: number; // only client side; binded to price field in expanded smart receipt content;
        newRefundItemPrice?: number; // only client side; binded to price field in expanded smart receipt content;
        originalBaseItemPrice?: number;
        originalFinalItemPrice?: number;
        originalBaseItemPriceCharge?: number;
        originalFinalItemPriceCharge?: number;
        hideInReceipt?: boolean;
        performAutoRedeem?: boolean;
        secondEmployeeId?: string;
        secondEmployee?: EmployeeListItem;
        secondEmployeeSplitCommission?: number;
        employeeSplitCommission?: number;
        fullCommissionItemPrice?: number;
        fullCommissionPrice?: number;
        originalFullCommissionPrice?: number;
        isFullCommissionApplicable?: boolean;
        computedGuid?: string;
        isFullCommissionApplicableManuallyChecked?: boolean;
        showPoleMessage?: SaleLineStatusEnum;
        isProductTaxDeclined?: boolean;
        isServiceTaxDeclined?: boolean;
        onHoldId?: string;
        priceIncludesTax?: boolean;
        teacher?: EmployeeListItem;
        teacherId?: string;
        needsTeacherAssignment?: boolean;
        noRefundReason?: SaleLineNoRefundReasonEnum;
        isComingFromHold?: boolean;
        hasPromptedForRebook?: boolean;
        referredByDetail?: Meevo.Model.ReferralDetail;
        receiptDisplayName?: string;
        linkedSaleLineIds?: number[];
        isLinkedRefundLine?: boolean; // FRONT END ONLY
        paymentPlanId?: string;
        hasPaymentPlan?: boolean;
        redeemedPackageItems?: any[];
        externalTipTransactionId?: string;
        isTransactionRefund?: boolean;
        authorizationPaymentRequestId?: string;
    }

    interface TransactionEditorSaleLineSecurity {
        isDisabled: boolean;
        canElevate: boolean;
    }

    interface TransactionEditorSaleLine extends SaleLine {
        employeeCriteria: any;
        employee2Criteria: any;
        product: any;
        service: any;
        productCriteria: any;
        item: any;
        productSecurity: TransactionEditorSaleLineSecurity;
        inventoryTypeSecurity: TransactionEditorSaleLineSecurity;
    }

    interface TipLine {
        employeeId: string;
        employee: EmployeeListItem;
        employeeSplitCommission?: number;
        employeeCode?: string;
        employeeFirstName?: string;
        employeeLastName?: string;
        employeeDisplayName?: string;
        originalEmployeeId: string;
        originalEmployee: EmployeeListItem;
        originalEmployeeCode?: string;
        originalEmployeeFirstName?: string;
        originalEmployeeLastName?: string;
        originalEmployeeDisplayName?: string;
        amount: number;
        percentage: number;
        id?: string;
        tempId?: string;
        isRefund?: boolean;
        refundSaleId?: string;
        refundLocationId?: number;
        refundTipLineId?: string;
        refundSaleDate?: Date;
        clientIds?: any[];
        saleId?: string;
        saleDate?: Date;
        locationId?: number;
        transactionId?: string;
        basePrice?: number;
        finalPrice?: number;
        registerClients?: Model.IRegisterClient[];
        prepaidTipMinimum?: number;
        baseTotal?: number;
        finalTotal?: number;
        isServiceEmployee?: boolean;
        serviceTotal?: number;
        externalTipTransactionId?: string;
        convenienceFee?: number;
        externalAmount?: number;
        authorizationPaymentRequestId?: string;
    }

    interface PaymentTypeListItem {
        id?: string;
        displayName?: string;
        entityVersion?: number;
        objectState?: ObjectStateEnum;
        paymentCategoryDisplayName: string;
        paymentCategory: PaymentCategoryEnum;
        paymentSubCategory: PaymentSubCategoryEnum;
        paymentTypeSId: number;
        isRefundable?: boolean;
    }

    interface DiscountLine {
        type?: DiscountTypeEnum | 0;
        amount?: number;
        discountValue?: number;
        predefinedDiscount?: any;
        id?: number;
        saleLineId?: number;
        discountId?: string;
        displayName?: string;
        saleLineDiscountType?: SaleLineDiscountTypeEnum;
        isBuyGet?: boolean;
        isStackable?: boolean;
        isBuyItem?: boolean;
        buyEntityType?: DiscountEntityTypeEnum;
        discountEntityType?: DiscountEntityTypeEnum;
        discountSaleLineGroupId?: string;
        isAutoApply?: boolean;
        isEmployeeExclusive?: boolean;
        entityId?: string;
        membershipBenefitTemplateId?: string;
        employees?: Array<EmployeeListItem>;
        clientClientTypes?: Array<ClientClientTypeListItem>;
        isFullCommissionApplicable?: boolean;
        buy_Amount?: number;
        buy_Quantity?: number;
        get_Quantity?: number;
        requiredQuantity?: number;
        isMembershipClientDiscount?: boolean;
        isNonMembershipClientDiscount?: boolean;
        clientTransactionStateDate?: Date;
        bogoBasedOnTypeEnum?: DiscountBasedOnEnum;
        showPoleMessage?: SaleLineStatusEnum;
        availableUsageLeft?: number;
        isPending?: boolean;
        isSpread?: boolean;
        isAllowRedeemBuyGet?: boolean;
        isItemOnSaleOverride?: boolean;
        discountValueSpread?: number;
        isOffer?: boolean;
    }

    interface ConsolidatedDiscountLine extends DiscountLine {
        totalDiscounts?: number;
        totalDiscountsFormatted?: string;
    }

    interface DiscountListItem {
        // ListItemBase
        id: string;
        locationId: number;
        displayName: string;

        // ListItem
        objectState: ObjectStateEnum;
        objectCreationType: ObjectCreationTypeEnum;

        // CoListItem
        ownedByLocationName: string;
        ownedByLocationId: number;
        atLocationName: string;
        atLocationId: number;
        totalLocations: number;
        availableAtLocations: number;

        discountEntityType: DiscountEntityTypeEnum;
        buyEntityType: DiscountEntityTypeEnum;
        bogoBasedOnType: Meevo.Model.BOGOBasedOnTypeEnum;
        isBuyGet: boolean;
        isStackable: boolean;
        details: Model.IDiscount;
    }

    interface SaleLineDiscountBogo {
        saleLineId: number;
        discountId: Guid | string;
        usedQuantity: number;
        usedAmount: number;
        usedSaleLocationId: number;
        usedSaleDate: string;
        usedSaleId: number;
        usedSaleLineId: number;
        discountSaleLineGroupId: Guid | string;
    }

    interface PackageRedemptionPath {
        clientId?: Guid;
        clientPackagesCreated?: any[];
        clientPackageServicesUsed?: any[];
        clientPackageProductsUsed?: any[];
        clientPackageProductUnitsUsed?: any[];
        clientPackagesUsed?: any[];
        createsMerge?: boolean;
        createsSplit?: boolean;
        numberOfSteps?: number;
        redeemQuantity?: number;
        serviceId?: Guid;
        usesExpiredPackage?: boolean;
        usesAvailablePackage?: boolean;
        usesSwap?: boolean;
        totalSwapFee?: number;
    }

    interface Device {
        id?: string;
        displayName: string;
        deviceName: string;
        ribbonSize: number;
        drawer?: Guid;
        ipAddress: string;
        objectState: ObjectStateEnum;
        brandId: number;
        port: number;
        entityVersion?: number;
        terminalId?: Guid;
        devicePrinterId?: Guid;
        devicePrinter: any;
        deviceCardId?: Guid;
        deviceCard: any;
        useNetworkPrinter?: boolean;
        customerDisplayStandbyLine1: string;
        customerDisplayStandbyLine2: string;
        isCustomerDisplayEnabled?: boolean;
        isTrustEnabled?: boolean;
    }

    interface PosHardware {
        deviceType: DeviceTypeEnum;
        devices: Device[];
    }

    interface CreditCardStatus {
        code: number;
        message: string;
        paymentAlertMessage?: string;
        processorMessage: string;
    }

    interface SalePayment {
        __type?: string;
        amount: number;
        taxAmount?: number;
        paymentTypeId?: string;
        paymentCategory?: Model.PaymentCategoryEnum;
        paymentSubCategory?: Model.PaymentSubCategoryEnum;
        paymentTypeDisplayName?: string;
        paymentType?: Model.PaymentTypeListItem;
        updated?: boolean;
        isPending?: boolean;
        transactionSetupId?: string;
        id?: number;
        tempId?: string;
        onHoldId?: string;
        paymentRequest?: Model.PaymentRequest;
        paymentRequestId?: string;
        isAuthorization?: boolean;
        parentId?: string;
        surcharge?: number;
        amountBeforeSurcharge?: number;
        // processing
        isDeclined?: boolean;
    }

    interface SalePaymentDeferredVoidKey extends SalePayment {
        deferredVoidKey?: SalePaymentKey;
    }

    interface SalePaymentKey {
        saleDate?: Date;
        saleId?: number;
        salePaymentId?: number;
        saleTransactionId?: string;
    }

    interface SaleCheckPayment extends SalePayment {
        checkNumber?: string;
    }

    interface SaleClientAccountPayment extends SalePayment {
        clientId?: string;
        clientDisplayName?: string;
        appointmentServiceId?: string;
        serviceShortcut?: string;
    }

    interface SaleGiftCardPayment extends SalePayment {
        giftCardId?: string;
        giftCardNumber?: string;
        clientDisplayName?: string;
        paymentRequestId?: string;
        isPromo?: boolean;
        promoServiceDisplayName?: string;
    }

    interface SaleExternalGiftCardPayment extends SalePayment {
        clientDisplayName?: string;
    }

    interface SaleClientPointPayment extends SalePayment {
        clientId?: string;
        clientDisplayName?: string;
    }

    interface SaleCreditCardPayment extends SalePayment {
        paymentRequestId?: string;
        creditCardNumber?: string;
        creditCardType?: string;
        creditCardExpirationDate?: string;
        tipAmount?: number;
        paymentRequest?: Model.PaymentRequest;
        requestedAmount?: number;
        approvedAmount?: number;
        parentId?: string;
        cardEntryType?: Model.CardEntryTypeEnum;
        signatureData?: string;
        signatureDataFormat?: Model.CreditCardSignatureFormatEnum;
        reverse?: boolean;
        transactionSetupId?: string;
    }

    interface SalePaymentDeferredVoid extends SalePayment {
        voidSaleDate?: Date;
        voidSaleId?: number;
        voidSalePaymentId?: number;
        voidSaleTransactionId: string;
    }

    interface PaymentRequest {
        id?: string;
        paymentRequestStatus?: Model.PaymentRequestStatusEnum;
        creditCardProcessorStatus?: CreditCardStatus;
        creditCardNumber?: string;
        creditCardType?: string;
        creditCardExpirationDate?: string;
        tipAmount?: number;
        amount: number;
        requestedAmount?: number;
        approvedAmount?: number;
        surcharge?: number;
        paymentTypeId?: string;
        paymentCategory?: Model.PaymentCategoryEnum;
        paymentTypeDisplayName?: string;
        paymentType?: Model.PaymentTypeListItem;
        updated?: boolean;
        isPending?: boolean;
        onHoldId?: string;
        paymentRequestVoidStatus?: Model.PaymentRequestStatusEnum;
        paymentRequestVoidType?: number;
        //processorResponse?: Object;
        approvalCode?: string;
        emvEntryMode?: Model.CardEntryTypeEnum;
        signatureData?: string;
        signatureDataFormat?: Model.CreditCardSignatureFormatEnum;
        emvApplicationPreferredName?: string;
        emvApplicationLabel?: string;
        emvApplicationIdentifier?: string;
        emvTags?: string;
        processorTransactionId?: string;
        tempId?: Guid | string;
        merchantTerminalId?: Guid | string;
        externalTerminalId?: Guid | string;
        paymentRequestVoidCreditCardProcessorStatus?: CreditCardStatus;
        parentId?: string;
        merchantAccountId?: string;
        processorResponseCode?: Model.ProcessorResponseCodeEnum;
        performTransactionQuery?: boolean;
        holdType?: HoldTypeEnum;
        processorTransactionSetupId?: string;
        isAuthorization?: boolean;
        externalTipAmount?: number;
        externalTipConvenienceFee?: number;
    }

    interface PaymentPlanPayment extends SalePayment{
        paymentPlanId?: string
    }

    interface SaleLineItem {
        // needed to support subclasses on the server-side; value is "type, namespace" to get deserialized correctly on the server-side
        __type?: string;
        id?: string;
        displayName?: string;
        shortcut?: string;
        originalId?: string;
        clientFirstName?: string;
        clientLastName?: string;
        clientDisplayName?: string;
        firstClientType?: ClientTypeEnum;
        hasMultiClientOrType?: boolean;
        doNotAllowTips?: boolean;
        entityVersion?: number;
        paymentPlanEntityVersion?: number;
        productUsage?: Meevo.Model.ISaleLineServiceProductUsage;
    }

    interface ServiceSaleLineItem extends SaleLineItem {
        appointmentId?: string;
        appointmentServiceId?: string;
        temp?: ServiceSaleLineItem;
        appointmentServiceDate?: string;
        addOnAppointmentServiceId?: string;
        addOnServiceId?: string;
        cancellationTypeId?: string;
        appointmentEntityVersion?: number;
        usedClientPackageServiceId?: string;
        usedClientPackageId?: string;
        refundClientPackageId?: any;
        isRefundClientPackage?: boolean;
        hasCheckoutCustomControls?: boolean;
        customControlsAnswers?: any;
        paymentPromoGiftCardId?: string;
        doNotAllowTips?: boolean;
        isRebooked?: boolean;
        clientVisitNoteTypeId?: string;
        isUnlimitedService?: boolean;
        depositAmount?: number;
        employeeId?: string;
        clientId?: string;
        clientType?: Model.ClientTypeEnum;
        isAddOn?: boolean;
        appointmentServiceSaleDetails?: Model.AppointmentServiceSaleDetails;
    }

    interface AppointmentServiceSaleDetails{
        payingClientId?: string;
        payingClientDisplayName?: string;
    }

    interface ProductSaleLineItem extends SaleLineItem {
        productId?: string;
        sku?: string;
        barCode?: string;
        variantAttributes?: any[];
        isResell?: boolean;
        productDisplayName?: string;
        variantDisplayName?: string;
        isProductBundle?: boolean;
        productInventoryType?: ProductInventoryTypeEnum;
    }

    interface SaleLineTax {
        baseAmount?: number;
        finalAmount?: number;
        saleLineId?: number;
        taxRateId?: string;
        taxTypeId?: string;
        taxTypeDisplayName?: string;
        isIncludedInPrice?: boolean;
    }

    interface SaleLineChargeTax extends SaleLineTax {
        serviceChargeId?: string;
    }

    interface SaleLineCharge {
        baseAmount?: number;
        finalAmount?: number;
        saleLineId?: number;
        serviceChargeId?: string;
        chargeType?: Model.ChargeTypeEnum;
        chargeAmount?: number;
        isPercent?: boolean;
        taxes?: SaleLineChargeTax[];
        originalTaxes?: SaleLineChargeTax[];
    }

    interface MembershipSaleLineItem extends SaleLineItem {
        paymentAccounts?: any[];
        isTaxExempt?: boolean;
        autoRenewType?: Model.AutoRenewTypeEnum;
        membershipNumber?: string;
        clientPackages?: any[];
    }

    interface ClientAccountSaleLineItem extends SaleLineItem {
        originalBalance?: number;
        note?: string;
        clientFirstName?: string;
        clientLastName?: string;
        clientDisplayName?: string;
        appointmentServiceId?: string;
        serviceDisplayName?: string;
        serviceShortcut?: string;
    }

    interface MembershipPaymentSaleLineItem extends SaleLineItem {
        clientMembershipBillFeeType?: number;
        clientMembershipBillStatus?: number;
        clientMembershipId?: string;
        entityVersion?: number;
        freezeSetting?: boolean;
        freezeStartDate?: string;
        freezeReactivationDate?: string;
        freezeReason?: string;
        isTaxExempt?: boolean;
        membershipName?: string;
        paymentDate?: Date;
        billPackages?: any[];
    }

    interface PaymentPlanPaymentSaleLineItem extends SaleLineItem {
        paymentPlanEntityVersion?: number;
        paymentDate?: Date;
        saleTransactionId?: string;
    }

    interface PaymentPlanReductionSaleLineItem extends SaleLineItem {

    }

    interface PackageSaleLineItem extends SaleLineItem {
        clientPackage?: any;
        packageCreationType?: PackageCreationTypeEnum;
        packageNumber?: string;
        unlimitedTotal?: number;
    }

    interface GiftCardSaleLineItem extends SaleLineItem {
        clientGiftCard?: any;
        remainingAmount?: number;
        remainingRefundAmount?: number;
        giftCardNumber?: string;
        giftCardTypeId?: string;
        giftCardTypeDisplayName?: string;
        autoGenerateGiftCardNumber?: boolean;
        note?: string;
        isGiftCardNumberUnmasked?: boolean;
    }

    interface GiftCardBulkSaleLineItem extends GiftCardSaleLineItem {
        giftCardNumberStarting?: string;
        giftCardNumberEnding?: string;
    }

    interface PackageServiceSwapFeeSaleLineItem extends ServiceSaleLineItem {
        clientPackageServiceId?: string;
        swapGroupId?: string;
    }

    interface RecurringPayment { }

    interface PaymentAccount {
        id?: Guid;
        merchantAccountId?: string;
        accountNumber?: string;
        routingNumber?: string;
        externalPaymentAccountId?: string;
        creditCardLastFour?: string;
        creditCardExpirationDate?: Date;
        paymentAccountType?: PaymentAccountTypeEnum;
        zipCode?: string;
        directManualEntry?: boolean;
        forceManualEntry?: boolean;
        cardType?: string;
        clientGlobalId?: string;
        isMembershipPaymentAccount?: boolean;
        isClientPaymentAccount?: boolean;
        isQueuedForAutoUpdate?: boolean;
        isAccountUpdaterEnabled?: boolean;
        merchantAccountObjectState?: ObjectStateEnum;
        merchantProviderId?: number;
        isHsaFsa?: boolean;
    }

    interface GiftCardExternalBin {
        tenantId?: number;
        prefix?: string;
        dateCreated?: Date;
        dateUpdated?: Date;
        objectState?: ObjectStateEnum;
    }

    interface GiftCardExternalBinContainer {
        externalBins?: Array<GiftCardExternalBin>;
    }

    interface IUserDefinedControl {
        control_TId?: Guid;
        tab_TId?: Guid;
        chartDefinition_TId?: Guid;
        isMainTab?: boolean;
        tabName?: string;
        tenantId?: number;
        locationId?: number;
        controlName: string;
        displayName: string;
        promptValue?: string;
        entityVersion?: number;
        controlTypeEnum: ControlTypeEnum;
        appAreaEnum: AppAreaEnum;
        objectStateEnum: ObjectStateEnum;
        items: Array<IUserDefinedControlValue>;
        shortcut: string;
        isRequired: boolean;
        isSearchable?: boolean;
        oncePerDaySubmission: boolean;
        createdByUserId?: Guid;
        updatedByUserId?: Guid;
        dateCreated?: string;
        dateUpdated?: string;
        sortOrder?: number;
        businessTypeName?: string;
        businessType_TId?: Guid;
        controlServiceName?: string;
        isUsed?: boolean;
        answers?: Array<IUserDefinedControlAnswer>;
        isIncludedInEmailNotification?: boolean;
        isShowBookingOnline?: boolean;
        tabTypeEnum?: number;
        fileGuid?: string;
        customDataControlResourceObject?: Meevo.Model.IStorageFile;
        service_TId?: string;
        services?: string[];
        serviceListIds?: Guid[];
        controlServices?: IUserDefinedControlServices[];
    }

    interface IUserDefinedControlServices {
        appAreaEnum: number;
        businessTypeName: string;
        businessType_TId: string;
        controlServiceName: string;
        controlServiceShortCut: string;
        controlService_TId: Guid;
        control_TId: string;
        objectStateEnum: number;
        service_TId: string;
        tenantId: number;
    }

    interface IUserDefinedControlValue {
        control_TId?: Guid | string;
        item_TId?: Guid | string;
        tenantId?: number;
        locationId?: number;
        controlId?: Guid;
        sortOrder?: number;
        homeLocationId?: number;
        isDefault: boolean;
        isDefaultCopy: boolean;
        displayName: string;
        objectStateEnum?: ObjectStateEnum;
    }

    interface IUserDefinedControlAnswer {
        tenantId: number;
        locationId: number;
        answer_LId: Guid;
        control_TId: Guid;
        displayValue: string;
        entity_Id: Guid;
        appAreaEnum: AppAreaEnum;
        dateCreated: string;
        dateUpdated: string;
        answerItems: Array<IUserDefinedControlAnswerItem>;
        customDataResourceObject?: ICustomDataResource;
        customDataResourceObjectList?: ICustomDataResource[];
    }

    interface IUserDefinedControlAnswerItem {
        tenantId: number;
        locationId: number;
        answer_Item_LId: Guid;
        answer_LId: Guid;
        control_TId: Guid;
        item_TId: Guid;
    }

    interface ICustomDataResource extends IStorageFile {
        resourceType: FileTypes;
        dateUploaded?: Date | string;
    }

    interface IAppAreaTab {
        tab_TId?: Guid;
        service_TId?: Guid;
        businessType_TId?: Guid;
        appAreaEnum: AppAreaEnum;
        isMain: boolean;
        displayName: string;
        title?: string;
        objectStateEnum: ObjectStateEnum;
        entityVersion?: number;
        controls?: Array<IUserDefinedControl>;
        businessTypeName?: string;
        serviceShortcut?: string;
        numberOfControls?: number;
    }

    interface IAppAreaService {
        id: Guid;
        objectStateEnum?: ObjectStateEnum;
        displayName: string;
        shortcut: string;
    }

    interface ScanOpening {
        date: Date;
        startTime: string;
        endTime: string;
        employeeName: string;
        employeeId: Guid;
        serviceId: Guid;
        resourceId: Guid;
        resourceUnitId: Guid;
        genderPreference: number;
        price: number;
        employeePrice: number;
        answers: any[];
        serviceName: string;
    }

    interface ScanOpeningContainer {
        serviceOpenings: ScanOpening[];
        note: string;
    }

    interface CallCenterOpeningTimeContainer {
        time: string;
        openingContainers: ScanOpeningContainer[];
    }

    interface CallCenterOpeningDayContainer {
        day: DayOfWeek;
        date: Date;
        openingTimeContainers: CallCenterOpeningTimeContainer[];
    }

    interface ICustomDataContextConfig {
        use: boolean;
        udfNoun?: Meevo.Model.NounEnum,
        appAreaEnum: AppAreaEnum;
        appAreaTabs: Array<Meevo.Model.IAppAreaTab>;
        $controllersScope: ng.IScope;
    }

    interface ISectionParams {
        sectionId: number;
        displayNameContentKey: string;
        template: string;
        isPrimarySection: boolean;
        handlesOwnCrud: boolean;
        baseEndpoint: string;
        sectionEndpoint: string;
        loggingService: Meevo.Components.Logging.ILoggingService;
        identityService?: Meevo.Components.Common.IIdentityService;
        noun?: Meevo.Model.NounEnum;
        $translate?: angular.translate.ITranslateService;
        appSettings?: Components.Common.IAppSettings;
        $scope?: ng.IScope;
        $q?: ng.IQService;
        $timeout?: ng.ITimeoutService;
        busyService?: Meevo.Components.Busy.IBusyService;
        sectionSecurityKey?: string;
    }

    interface ISectionParamsBaseDataService extends ISectionParams {
        dataService: Meevo.Components.BaseClasses.IDataService;
        modalService?: Meevo.Components.Modal.IModalService;
        usageTrackingLogFn?: (verbEnum: Meevo.Model.VerbEnum, payload?) => void;
        securityChecker?: Meevo.Components.Common.ISecurityService;
    }

    interface ISectionParamsClientMembership extends ISectionParams {
        dataService: Meevo.Components.Common.ClientMembershipDataService;
        modalService?: Meevo.Components.Modal.IModalService;
        clientMembership?: Meevo.Plugins.MembershipManager.IMembershipManagerSearchContext;
        $rootScope?: angular.IRootScopeService;
    }

    interface ISectionParamsCommonDataService extends ISectionParams {
        dataService: Meevo.Components.CommonClasses.ICommonDataService;
    }

    interface ISectionParamsWalkinManagerSettings extends ISectionParams {
        dataService: Meevo.Components.CommonClasses.ICommonDataService;
        modalService?: Meevo.Components.Modal.IModalService;
    }

    interface ISectionParamsMarketPrice extends ISectionParams {
        dataService: Meevo.Components.BaseClasses.IDataService;
        modalService?: Meevo.Components.Modal.IModalService;
        discountsService?: Meevo.Plugins.Discounts.IDiscountsService;
        usageTrackingLogFn?: (verbEnum: Meevo.Model.VerbEnum, payload?) => void;
        tabName?: string;
        contextService?: Meevo.Plugins.MarketPricing.IContextService;
        businessInfoService: BusinessInfoDataService;
    }

    interface ISectionParamsSystemToolbox extends ISectionParams {
        dataService: Meevo.Components.BaseClasses.IDataService;
        modalService?: Meevo.Components.Modal.IModalService;
        usageTrackingLogFn?: (verbEnum: Meevo.Model.VerbEnum, payload?) => void;
        tabName?: string;
    }

    interface ISectionParamsExtendedDataService extends ISectionParams {
        dataService: Meevo.Components.BaseClasses.IDataService;
        modalService?: Meevo.Components.Modal.IModalService;
        trackingService?: Meevo.Components.Tracking.ITrackingService;
        securityService?: Meevo.Components.Common.ISecurityService;
    }

    interface ISectionParamsExtendedCommonDataService extends ISectionParams {
        dataService: Meevo.Components.CommonClasses.ICommonDataService;
        modalService?: Meevo.Components.Modal.IModalService;
        busyService?: Meevo.Components.Busy.IBusyService;
        trackingService?: Meevo.Components.Tracking.ITrackingService;
    }

    interface IReportGeneral {
        description: string;
        displayName: string;
        id: number;
        reportCategoryName?: string;
        shortCut: string;
        securityKey: string;
        permission?: SecurityAccessEnum;
        validationJSON?: string;
        startDayOfWeek?: number;
        customData?: any;
        categoryId: number;
        weight?: number;
        isScheduled?: boolean;
        reportAvailability?: number;
        renderingEnum?: ReportRenderingEnum;
    }

    interface IReport extends IReportGeneral {
        isCustom: boolean;
        objectState: ObjectStateEnum;
        reportAvailability: number;
        reportCatId: number;
        securityAttribId: number;
        securityKey: string;
        storedProc: string;
        template: string;
    }

    interface IReportControl {
        displayName: string;
        id?: number;
        parameters: Array<IReportControlParameters>;
        values: Array<IReportControlValue>;
        securityAttribId?: number;
        securityKey?: string;
        sequence: number;
        labelText?: string;
        reportControlEnumType?: number;
        reportId?: number;
    }

    interface IReportControlParameters {
        clrType?: string;
        controlId?: number;
        dbType?: string;
        name: string;
        sequence: number;
        value?: Array<IReportControlValue>;
    }

    interface IReportControlValue {
        id?: number;
        displayName: string;
        value: string | number;
        sortOrder: number;
    }

    interface ISaleProgram extends DataMaintenance {
        displayName: string;
        startDate: Date;
        endDate: Date;
        originalStartDate: Date;
        originalEndDate: Date;
        objectState: ObjectStateEnum;
        desc: string;
        applyDiscount: boolean;
        products: {
            discountTypeEnum: number;
            discountAmount: number;
            items: any[];
        };
        services: {
            discountTypeEnum: number;
            discountAmount: number;
            items: any[];
        };
    }

    interface IPointPromotion {
        pointPromotion_TId?: Guid | string;
        pointPromotionCampaign_TId: Guid | string;
        pointPromotionType: Meevo.Model.PointPromotionTypeEnum;
        objectState?: ObjectStateEnum;
        points?: number;
        amount?: number;
        multiplier?: number;
        onlyFirstVisit?: boolean;
        overAmount?: number;
        quantityPurchased?: number;
        weekLimit?: number;
        isSpecificItem?: boolean;
        specificItemType?: Meevo.Model.SpecificItemTypeEnum;
        items?: any[];
        isAll?: boolean;
        hideAllSelectedCheckbox?: boolean;
        requisiteNumberOfVisits?: number;
    }

    interface IPointPromotionCampaign extends DataMaintenance {
        pointPromotionCampaignId: Guid | string;
        displayName: string;
        currentDate: Date;
        startDate: Date;
        endDate?: Date;
        originalStartDate: Date;
        originalEndDate?: Date;
        objectState: ObjectStateEnum;
        redeemByDate?: Date;
        redeemByDaysAfterAward?: number;
        clientsIncluded: ClientsIncludedEnum;
        clientTypes: ClientClientTypeListItem[];
        pointPromotions: IPointPromotion[];
    }

    interface IDiscount extends DataMaintenance {
        bogoBasedOnTypeEnum: DiscountBasedOnEnum;
        clientTransactionHistoryStartDate: Date;
        displayName: string;
        startDate: Date;
        endDate: Date;
        barCode: string;
        objectState: ObjectStateEnum;
        desc: string;
        isStackable: boolean;
        isEmployeeExclusive: boolean;
        isAutoApply: boolean;
        redeemableLimit: number;
        discountRedeemableLimitPerTypeEnum: DiscountRedeemableLimitPerTypeEnum;
        requiredQuantity: number;
        discountTypeEnum: DiscountTypeEnum;
        discountAmount: number;
        discountEntityTypeEnum: DiscountEntityTypeEnum;
        discountEntityGranularityTypeEnum: DiscountEntityGranularityTypeEnum;
        discountManufacturer_TId: Guid;
        services: Array<any>;
        productVariants: Array<any>;
        packageTypes: Array<any>;
        memberships: Array<any>;
        giftCardTypes: Array<any>;
        isBuyGet: boolean;
        get_Quantity: number;
        buy_Amount: number;
        buy_Quantity: number;
        buy_EntityTypeEnum: DiscountEntityTypeEnum;
        buy_EntityGranularityTypeEnum: DiscountEntityGranularityTypeEnum;
        buy_Manufacturer_TId: Guid;
        buy_ProductVariants: Array<any>;
        buy_Services: Array<any>;
        buy_Memberships: Array<any>;
        buy_PackageTypes: Array<any>;
        buy_GiftCardTypes: Array<any>;
        isClientHistoryDependent: boolean;
        discountTypeEnumGet: DiscountTypeEnum;
        isRedeemableLimitApplicable: boolean;
        discountRedeemableLimitPerMonth: number;
        isMembershipClientDiscount: boolean;
        isNonMembershipClientDiscount: boolean;
        employees: Array<EmployeeListItem>;
        clientClientTypes: Array<ClientClientTypeListItem>;
        fees: any[];
        serviceIds: string[];
        productVariantIds: string[];
        packageTypeIds: string[];
        membershipIds: string[];
        giftCardTypeIds: string[];
        feeIds: string[];
        buy_ServiceIds: string[];
        buy_ProductVariantIds: string[];
        buy_MembershipIds: string[];
        buy_PackageTypeIds: string[];
        buy_GiftCardTypeIds: string[];
        allSelected: boolean;
        isSpread: boolean;
        isAllowRedeemBuyGet: boolean;
    }

    interface IAddOnGroup extends DataMaintenance {
        displayName: string;
        addOnServices: Array<AddOnServiceListItem>;
        groupServices: Array<AddOnServiceListItem>;
        addOnServicesRequired: Array<AddOnServiceListItem>;
        reqNumOfAddOns?: number;
    }

    interface IAddOnGroupServiceAddOnStepTime extends DataMaintenanceSublistItem {
        asLocationId?: string;
        displayName?: string;
        serviceAddOnStepTimes: IServiceAddOnStepTimes[];
    }

    interface IServiceAddOnStepTimes {
        all?: string;
        show?: boolean;
        expand?: boolean;
        serviceId?: string;
        serviceDisplayName?: string;
        addOnServiceSteps?: IAddOnServiceStep[];
        addOnServiceStepTimes?: IAddOnServiceStepTime[];
    }

    interface IAddOnServiceStep {
        serviceId?: string;
        stepId?: string;
        stepDisplayName?: string;
        serviceStepTypeEnum?: ServiceStepTypeEnum;
        stepNumber?: string;
        minutes?: string;
    }

    interface IAddOnServiceStepTime extends IAddOnService {
        serviceId?: string;
        stepId?: string;
    }

    interface IAddOnService {

        addOnServiceId?: string;
        addOnServiceDisplayName?: string;
        addOnServiceObjectState?: ObjectStateEnum;
        extraTimeForServiceAsAddOnInMin?: string;
        stepId?: string;
        stepDisplayName?: string;
    }

    interface AddOnServiceListItem extends ListItemWithLanguage {
        required?: boolean;
    }

    interface IProductBundle extends DataMaintenance {
        // todo: dev
    }

    interface IMembershipFreezeSetting {
        freezeSetting: FreezeSettingEnum;
        fee: number;
        isPercent: boolean;
        isEnabled?: boolean;
        displayName?: string;
    }

    interface IMembershipMemberSettings {
        memberType: MemberTypeEnum;
        membershipBenefitTemplateId: string;
        membershipBenefitTemplateDetail?: any;
        enrollmentFee: number;
        minEnrollmentFee: number;
        recurringFee: number;
        renewalFee?: number;
        minRenewalFee?: number;
        minRecurringFee: number;
        billNumberOfDaysAfterEnroll: number;
        pifRenewalFee: number;
        minPIFRenewalFee: number;
        monthlyRenewalFee: number;
        minMonthlyRenewalFee: number;
    }

    interface IMembershipPackageSettings {
        memberType: MemberTypeEnum;
        feeType: FeeTypeEnum;
        isUnlimitedServices: boolean;
        packageTypeDetail: {
            id: string;
            displayName: string;
        };
        packageTypeId: string;
        id?: string;
    }

    interface IIntegration extends DataMaintenance {
        tenantId?: number;
        integration_TId?: Guid;
        displayName: string;
        objectStateEnum: ObjectStateEnum;
        isEnable: boolean;
        isCaptureEventData: boolean;
        urlAddress: string;
        externalId?: string;
        createdBy_User_TId?: Guid;
        updatedBy_User_TId?: Guid;
        emailAddresses: Array<string>;
        integrationSettings: Array<IIntegrationSetting>;
        entityVersion?: number;
        statusText: string;
    }

    interface IIntegrationErrorLogSearchCriteria {
        integration_TId: Guid | string;
        integrationSetting_SId?: number;
        integrationStatusEnum?: number;
        isRetryOnly?: boolean;
    }

    interface IIntegrationSetting {
        id?: number;
        displayName: string;
        isInsert: boolean;
        isUpdate: boolean;
        isDelete: boolean;
        enabled?: boolean;
    }

    interface IIntegrationErrorHeader {
        tenantId: number;
        id: Guid;
        integration_TId: Guid;
        integrationSetting_SId: Guid;
        displayName: string;
        eventStatusEnum: IntegrationStatusEnum;
        dateCreated: Date | string;
        dateUpdated: Date | string;
        eventDetail: string;
        eventSource: string;
        attempt: number;
        isEnable: boolean;
        locationId: number;
        integrationQueue_TId: number;
        integrationTrackerLog_TId: number;
        changeAction: string;
        threadId: number;
        queueStatus: IntegrationStatusEnum;
        successDate: Date;
        isRetry: boolean;
    }

    interface IIntegrationErrorLogDetail {
        failed?: Array<IIntegrationErrorLogDetailFailed>;
        successful?: Array<IIntegrationErrorLogDetailSuccessful>;
        payload?: string;
    }

    interface IIntegrationErrorLogDetailFailed {
        tenantId?: number;
        integrationQueueLog_TId: Guid;
        integrationQueue_TId: number;
        HTTPResponseCode: string;
        syncEventDetails: string;
        dateCreated: Date;
        parsedDateCreated: string;
    }

    interface IIntegrationErrorLogDetailSuccessful {
        tenantId?: number;
        integrationQueue_TId: number;
        successDate: Date;
        parsedSuccessDate: string;
    }

    interface ReportScheduler extends DataMaintenanceWithLanguage /*, IHasLocationOverride*/ {
        reportId?: string;
        reportCategory?: string;

        totalBundleValue?: number;
        ownedByLocationName?: string;
        totalLocations?: number;
        availableAtLocations?: number;
        reportDeliveryDayOfMonth?: number;
        reportDeliveryDayOfWeek?: number;
        reportDeliveryTimeOfDay?: Date;
        reportParameterDateRange?: number;

        entityVersion?: number;
        id?: string;
        hasScheduleStarted?: boolean;
        objectState?: ObjectStateEnum;
        startDate?: Date;
        startTime?: string;
        daysOfWeek?: DayOfWeek[];
        frequency?: number;
        reportDeliveryTypeEnum?: DateTimeUnitsEnum;
        monthlySequencePositionEnum?: SequencePositionEnum;
        endDate?: string;
        totalOccurrences?: number;
        isPerpetual: boolean;
        selectedPreset?: Preset;
        employeeRecipientList?: any[];
        employeeCategoryList?: any[];
        userRecipientList?: any[];
        customUserRecipientList?: EmailAddress[];
        reportParamJson?: any; // used on front end as an object
        reportParameterJSON?: string; // used by c# model and returned from Logic as a string
        applySecurity?: boolean
        reportDeliveryFileFormatEnum?: FileFormatEnum;

        // Properties related to selected Preset Callbacks
        selectedPresetCallback?: any;
        forceCallback?: boolean;
    }

    interface Preset {
        name: string;
        status: number;
    }

    interface EmailAddress {
        fullName: string;
        emailAddress?: string;
        phoneNumber?: string;
        phoneCountryCode?: string;
    }

    interface SmartCenter extends DataMaintenanceWithLanguage {
        ownedByLocationName?: number;
        tiles: SmartCenterSmartTile[];
        isAvailableCo?: boolean;
    }

    interface SmartCenterListItem extends ListItem {
        atLocationId?: number;
        atLocationName?: string;
        availableAtLocations?: number;
        displayName?: string;
        id?: string;
        isOwner?: boolean;
        locationId?: number;
        objectCreationType?: number;
        objectState?: number;
        ownedByLocationId?: number;
        ownedByLocationName?: string;
        totalLocations?: number;
    }

    interface SmartTileDefinition {
        id?: number;
        displayName: string;
        desc?: string;
        parameterDefaults: string;
        backgroundColor: string;
        image?: string;
        smartTileTypeEnum: Meevo.Plugins.SmartCenter.TileTypes;
        tileCategory: SmartTIleCategoryEnum;
        hasExtendedData?: boolean;
        smartTileName: string;
        tileAvailabilityLevel?: number;
        showExtendedData?: boolean;
    }

    interface SmartCenterSmartTile {
        id: number;
        smartCenterTileId: Guid;
        rowPosition?: number;
        columnPosition?: number;
        rowSpan: number;
        columnSpan: number;
        parameter: string;
        backgroundColor: string;
        image?: string;
        displayName: string;
        displayNameOverride: string;
        smartTileTypeEnum: Meevo.Plugins.SmartCenter.TileTypes;
        includedEmployee: number;
        parsedParameters: SmartTileParameters;
        smartTileName?: string;
        hasExtendedData?: boolean;
        lastUpdated?: Date;
        tileAvailabilityLevel?: number;
        desc?: string;
        decOverride?: string;
        isForLocationGroup?: boolean;
    }

    interface SmartTileParameter {
        label: string;
        labelKey?: string;
        parameterType: string;
        parameterValue: any;
        excludedValues?: string;
        includedValues?: string;
        isHidden?: boolean;
        parameterDisplayName?: string;
        selections?: DataDetail[];
    }

    interface SmartTileMultiSelectParameter extends SmartTileParameter {
        multiSelectTemplateKey?: string;
        selections?: any[];
    }

    interface SmartTileParameters {
        [name: string]: SmartTileParameter;
    }

    interface ICustomBranding extends DataMaintenance {
        canSaveCustom: boolean;
        bodyDisplayTextFont: string;
        displayLogo: boolean;
        displayLogoImage: Guid;
        displayLogoImageObject: IStorageFile;
        displayTextColor: string;
        displayTextFont: string;
        displayTextFontBold: boolean;
        displayTextFontItalicize: boolean;
        displayTextFontUnderline: boolean;
        customMessage: string;
        customMessageColor: string;
        customMessageFont: string;
        customMessageFontBold: boolean;
        customMessageFontItalicize: boolean;
        customMessageFontUnderline: boolean;
        eGiftDeliveryTextColorTop: string;
        eGiftDeliveryTextColorBottom: string;
        headerBarColor: string;
        headerBarFontColor: string;
        pageHeaderBarColor: string;
        pageHeaderBarFontColor: string;
        displayEGiftDeliveryAccentBar: boolean;
        stockBackgroundImage: IStorageFile;
        customBackgroundImage: Guid;
        customBackgroundImageObject: IStorageFile;
        gradientColorBackgroundImageStart: string;
        gradientColorBackgroundImageEnd: string;
        pageCustomBackgroundImage: Guid;
        pageStockBackgroundImage: IStorageFile;
        pageCustomBackgroundImageObject: IStorageFile;
        pageGradientColorBackgroundImageStart: string;
        pageGradientColorBackgroundImageEnd: string;
        pageDisplayLogo: boolean;
        pageDisplayLogoImage: Guid;
        pageDisplayLogoImageObject: IStorageFile;
        pageDisplayTextColor: string;
        pageDisplayTextFont: string;
        pageDisplayTextFontBold: boolean;
        pageDisplayTextFontItalicize: boolean;
        pageDisplayTextFontUnderline: boolean;
        pageBackgroundHeaderColor: string;
        pageSelectedBackgroundImageOption: number;
        selectedBackgroundImageOption: number;
    }

    interface ISearchColumn {
        header: string;
        fieldName: string;
        sortDirection: SortDirection;
        width: number | string;
    }

    interface IClientPoint extends Meevo.Model.DataMaintenance {
        client_TId: Guid;
        displayName: string;
        firstName: string;
        lastName: string;
        totalAmount: number;
        meevoFile?: MeevoFile;
        pointAdjustmentTypeEnum?: PointAdjustmentTypeEnum;
        remainingAmount?: number;
    }

    interface IClientPointHistoryItem {
        clientPoint_LId: Guid;
        clientPointDisposition_LId?: Guid;
        adjustmentAmount: number;
        expireDate: Date;
        dateCreated: Date;
        remainingAmount?: number;
        pointAdjustmentTypeEnum?: PointAdjustmentTypeEnum;
        adjustmentNote: string;
        userDisplayName: string;
        clientPointCreatedUserId?: Guid;
        clientPointDeletedUserId?: Guid;
        clientPointDateCreated?: Date;
        clientPointDateDeleted?: Date;
        clientPointCreatedUserDisplayName?: string;
        clientPointDeletedUserDisplayName?: string;
        dispositionCreatedUserId?: Guid;
        dispositionDeletedUserId?: Guid;
        dispositionCreatedUserDisplayName?: string;
        dispositionDeletedUserDisplayName?: string;
        dispositionDateCreated?: Date;
        dispositionDateDeleted?: Date;
        locationId?: number;
        storeName?: string;
        // for ui purpose
        isDeleted?: boolean;
        hideDeleteButton: boolean;
    }

    interface IClientPointAdjustmentItem {
        clientPoint_LId: Guid;
        dateCreated: Date;
        pointAdjustmentTypeEnum: PointAdjustmentTypeEnum;
        adjustmentAmount: number;
        transactionId: string;
    }

    interface ISupplyUsageLineItem {
        employeeSupplyUsage_LId?: Guid;
        locationId?: number;
        dateCreated: Date;
        variant_TId: Guid;
        sku: string;
        barCode?: string;
        variantDisplayName: string;
        productDisplayName: string;
        employeeUserDisplayName: string;
        employee_User_TId: Guid;
        quantity: number;
        quantityDisplay: string;
        returnUsageId?: Guid;
        productInventoryTypeEnum: ProductInventoryTypeEnum;
    }

    interface IClientPackage {
        clientPackage_TId: Guid;
        packageNumber: string;
        displayName: string;
        expireDate: Date;
        activeDate: Date;
        isCustom: boolean;
        isGlobalPackage: boolean;
        clientPackageServices: Array<IClientPackageService>;
        clientPackageProducts: Array<IClientPackageProduct>;
        clientPackageProductUsages: Array<IClientPackageProductUsage>;
        clientPackageItems?: Array<IClientPackageItem>;
        transactions?: Array<IClientPackageTransaction>;
        hasTransferToName?: boolean;
        isActive?: boolean;
        isExpired?: boolean;
        hasRemainingItems?: boolean;
        isAvailableForUseWhenExpired?: boolean;
        availableForUseExpireDate?: Date;
        locationId?: number;
        entityVersion?: number;
        remainingValue?: number;
    }

    interface IClientPackageService extends IClientPackageItem {
        service_TId: Guid;
    }

    interface IClientPackageProduct extends IClientPackageItem {
        productVariant_TId: Guid;
    }

    interface IClientPackageProductUsage extends IClientPackageProduct {
        barCode: string;
        sku: string;
        productDisplayName: string;
        variantDisplayName?: string;
        productUsageType: ProductUsageAmountTypeEnum;
        isUsedOverride: boolean;
        fullCommissionPriceEach: number;
        linePaidPriceTotal: number;
        linePaidPriceRemaining: number;
        prePaidTipTotal: number;
        prePaidTipRemaining: number;
        chargeBaseAmountTotal: number;
        chargeBaseAmountRemaining: number;
        chargeFinalAmountTotal: number;
        chargeFinalAmountRemaining: number;
    }

    interface IClientPackageItem {
        clientPackage_TId: Guid;
        displayName: string;
        quantity: number;
        quantityRemaining: number;
        lastUsed?: Date;
        itemId: Guid;
        packageItemType: number;
        linePaidPriceEach?: number;
        valueEach?: number;
        charges?: any[];
        itemType?: Meevo.Model.PackageItemTypeEnum;
    }

    interface IClientPackageTransaction {
        transactionId: string;
        clientService_TId: Guid;
        service_TId: Guid;
        client_TId: Guid;
        displayName: string;
        usedSaleDate: Date;
        usedSaleLocationId: number;
        usedSale_Seq: number;
        usedSaleLine_Seq: number;
        saleTypeEnum: SalesTypeEnum;
        transactionDateTime: Date;
        fullName: string;
        firstName: string;
        lastName: string;
        quantity: number;
        transferredToClientFirstName: string;
        transferredToClientLastName: string;
        isRefund: boolean;
    }

    interface IClientDiscountHistory {
        transactionId: number;
        transactionDateTime: Date;
        employeeFirstName: string;
        employeeLastName: string;
        saleTypeEnum: SalesTypeEnum;
        itemDisplayName: string;
        discountDisplayName: string;
        discountTypeEnum: DiscountTypeEnum;
        discountValue: number;
        storeName: string;
    }

    interface IMiscSupplyDefinition {
        distributors?: Array<Distributor>;
        displayName: string;
        description?: string;
        supplyCategoryEnum: SupplyCategoryEnum;
        sku: string;
        barCode: string;
        wholeSale: number;
        entityVersion?: number;
        objectState?: ObjectStateEnum;
        homeLocationId?: number;
        languageId?: number;
        locationId?: number;
        objectCreationType?: number;
        id: string;
        productInventoryType: ProductInventoryTypeEnum;
    }

    // todo: dev
    interface IInventoryReturn extends Meevo.Model.DataMaintenance {
        referenceNumber: string;
        distributor: Distributor;
        returnReasonEnum: ReturnReasonEnum;
        tax: number;
        insertItems?: Array<IInventoryReturnLineItem>;
        deleteItems?: Array<IInventoryReturnLineItem>;
        productInventoryType: ProductInventoryTypeEnum;
    }

    interface IInventoryCountProductSearchCriteria {
        productObjectState: ObjectStateEnum;
        productInventoryType: ProductInventoryTypeEnum;
        variantObjectState: Array<ObjectStateEnum>;
        isInventoryTrackable: boolean;
        inventoryCountOnly: boolean;
        id: Guid;
        floorLocationId?: Guid;
        iscounted?: boolean;
        manufacturerIds: (Guid | string)[];
        distributorIds: (Guid | string)[];
        productCatIds: (Guid | string)[];
    }

    interface IInventoryReturnLineItem {
        variant_TId: Guid;
        variantId?: Guid;
        product_TId?: Guid;
        productInventoryType: ProductInventoryTypeEnum;
        onHand: number;
        isDeducted?: boolean;
        returnQuantity?: number;
        lineTotal?: number;
        productName: string;
        displayName?: string;
        productDescription?: string;
        wholeSale?: number;
        isNew?: boolean;
        modelQuantity?: number;
        orderPoint?: number;
        sku: string;
        barCode: string;
        enableProductUsageTracking?: boolean;
        productUnitOfMeasureEnum?: UnitEnum;
        productUsageType?: ProductUsageAmountTypeEnum;
        unitOfMeasureQuantity?: number;
        decimalPrecision?: number;
    }

    interface IAlertCategory {
        id: number;
        objectStateEnum: ObjectStateEnum;
        displayName: string;
        desc: string;
    }

    interface IAlertSetting {
        alertType?: ProactiveAlertTypeEnum;
        cat_SId: number;
        id: number;
        displayName: string;
        desc: string;
        templateJSON: string;
        hasServicingEmployee: boolean;
        isSystemAlert: boolean;
        systemAlertTriggeredType: ProactiveAlertTriggeredTypeEnum;
    }

    interface IAlertUserLineItem {
        id: Guid;
        user_TId: Guid | string;
        employeeCode: string;
        objectStateEnum: ObjectStateEnum;
        employeeEntityType?: EmployeeEntityTypeEnum;
        isEmployee?: boolean;
    }

    interface ISecurityAttributes {
        displayName: string;
        securityKey: string;
        id: number;
        level: number;
        children?: Array<ISecurityAttributes>;
        childKeyIds?: Array<number>;
        sequence: number;
        parentId?: number;
        hierarchyMaterialized: string;
        hierarchySequenceId: string;
        selected?: boolean;
        fullDisplayName?: string;
    }

    interface IAlertJSON {
        alertType?: ProactiveAlertTypeEnum;
    }

    interface IAlert {
        id?: Guid;
        alert_SId: number;
        cat_SId?: number;
        alertType?: ProactiveAlertTypeEnum;
        //alertConfigurations?: IAlertConfigBase;
        objectStateEnum: ObjectStateEnum;
        alertJSON?: string;
        triggeredByList: Array<IAlertUserLineItem>;
        receivedByList: Array<IAlertUserLineItem>;
        notificationMethod?: NotificationMethodEnum;
        isForAll: IsForAllEnum;
        oncePerDay?: boolean;
        sms?: boolean;
        email?: boolean;
        entityVersion?: number;
        hasServicingEmployee: boolean;
    }

    interface IAlertListItem {
        id: string;
        objectStateEnum: ObjectStateEnum;
        notificationMethod: NotificationMethodEnum;
        isForAll: IsForAllEnum;
        cat_SId: number;
        alert_SId: number;
        alert: string;
        user_TId: string;
        employeeCode: string;
        alertCategory: string;
    }

    interface IAlertUserListItem {
        id?: string;
        user_TId: Guid;
        employeeCode: string;
        count?: number;
        alertList?: Meevo.Components.BaseClasses.IDataMaintenanceInfiniteList;
    }

    interface IAlertCatListItem {
        id: string;
        cat_SId: number;
        alertCategory: string;
        count?: number;
        alertList?: Meevo.Components.BaseClasses.IDataMaintenanceInfiniteList;
    }

    interface IAlertLogUserListItem {
        triggeredByUserId: Guid;
        triggeredToUserId: Guid;
        triggeredByUserDisplayName: string;
        alertList?: Meevo.Components.BaseClasses.IDataMaintenanceInfiniteList;
    }

    interface IAlertLogDetailListItem {
        dateTriggered: Date;
        alertId: Guid;
        id: Guid;
        alertDisplayName: string;
        triggeredByUserId: Guid;
        triggeredByUserDisplayName: string;
        isAlertRead: boolean;
    }

    interface IScanRequest {
        scanOrigin: ScanOriginEnum;
        startDate: string;
        endDate: string;
        scanDateType: ScanDateTypeEnum;
        scanTimeType: ScanTimeTypeEnum;
        isRescan: boolean;
        maxOpeningsPerDay: number;
        appointmentBufferMinutes: number;
        maxStartTimeWait: number;
        requireSameStartTime: boolean;
        maxWaitTimeBetweenServices: number;
        scanServices: IScanService[];
        overallRequestedEmployees: Guid[];
        payingClientId: Guid;
        clientId: Guid;
    }

    interface IScanService {
        serviceId: string;
        genderPreferenceEnum: GenderPreferenceEnum;
        clientId: Guid;
    }

    interface IAlertLog {
        htmlMessage: string;
        requestStateJson: string;
    }

    interface IBusinessSetting {
        settingSid?: number;
        value?: number;
        effectiveValue?: string | number;
        id?: number;
        displayName?: string;
        securityKey?: string;
    }

    interface ISandbox {
        id?: number;
        sandboxUrl: string;
        refreshedAmount: number;
        maxRefreshAmount: number;
        expirationDate: Date;
        dateCreated: Date;
        dateUpdated: Date;
        daysRemainig: number;
    }

    interface ISandboxProcessQueue {
        id: number;
        processType: ProcessTypeEnum;
        sandboxRequestStatus: SandboxProcessStatusEnum;
        dateUpdated: Date;
    }

    interface IEGiftsMainSettings {
        defaultTemplateCategoryEnum: EGiftTemplateCatEnum;
        disclaimer?: string;
        displayLogoOnCard: boolean;
        displayPackagePrice: boolean;
        displayServicePrice: boolean;
        eGiftAmount1: number;
        eGiftAmount2: number;
        eGiftAmount3: number;
        eGiftAmount4: number;
        eGiftAmount5: number;
        egiftExpirationDaysNumber: number;
        enableDollarAmountCalculator: boolean;
        enableAmountOptions: boolean;
        enableEGifts: boolean;
        enablePackagesCalculator?: boolean;
        enableServicesCalculator?: boolean;
        freeEgiftExpirationDaysNumber: number;
        hideValueOfGiftCardOnEGiftTemplate: boolean;
        paymentProcessingPreferences?: string;
        locationId?: number;
        groupEnum?: number;
        settings?: Array<any>;
        mailEnable?: boolean;
        handlingAndProcessingFee?: number;
        shippingAndHandlingDays?: number;
        storePickupEnable?: boolean;
        radiusInMiles?: number;
        chargeShippingFee?: boolean;
        privacyStatementUrl?: string;
        displayLogoOnCardImage?: Guid;
        emailEGiftToRecipientAtXHoursOnActivationDate?: number;
        requireClientToSelectLocation?: any;
        selectedLocationWillReceiveFundsFromEgiftSales?: any;
        egiftDisplayCode: string;
        egiftDefaultReferralTypeId: string;
        egiftDefaultReferralTypeDisplayName: string;
        shippingFeeChargeSelected: boolean;
        shippingFeeGiftCardTypeValue: number;
        shippingFeeTransactionTypeValue: string;
        shippingFeeDisplayName: string;
        shippingFeeType: number;
        egiftGoogleAnalyticsTrackingId: string;
        egiftGoogleTagManagerId: string;
        enableNewEgiftExperience: boolean;
        egiftMultiPurchaseFlow: EgiftMultiPurchaseFlow;
    }

    interface IEGiftsSettings {
        egiftUrl: string;
        egiftNewUrl: string;
        settings: IEGiftsMainSettings;
        termsAndConditions: ISettingsTerms;
        egiftDisclaimer: ISettingsTerms;
        displayLogoImageObject?: IStorageFile;
        eGiftTemplatePreview?: Array<any>;
        enableAutoGenerateGiftCardNumberButton: boolean;
        excludeLocationIds?: any;
        shippingFees: IShippingfee[];
        isReferralTypeRequire: boolean;
    }

    interface IShippingfee {
        atLocationId: number;
        availableAtLocations: number;
        description: string;
        displayName: string;
        id: string;
        locationId: number;
        objectCreationType: number;
        objectState: ObjectStateEnum;
        ownedByLocationId: number;
        price: number;
        showInEgift: boolean;
        totalLocations: number;
        position: number;
    }

    interface IMeevoMessengerQuickAnswer {
        quickAnswerId?: Guid;
        tenantId?: number;
        locationId?: number;
        displayName : string;
        body: string;
        orderNumber: number;
    }

    interface IMeevoMessengerSettings {
        autoReplyKind: MeevoMessengerAutoRepliesEnum;
        autoReplyModifier?: Guid;
        tenantId?: number;
        entityVersion?: number;
        locationId?: number;
        userName: string;
        token: string;
        usersToNotify: Guid[];
        userCategoriesIdsList?: Guid[];
        incomingMessageBubbleColor: string;
        isAccountInitialized: boolean;
        replyMessageBubbleColor: string;
        isAutoReplyEnabled: boolean;
        defaultAutoReplyMessage: string;
        outsideHoursAutoReplyMessage: string;
        holidayAutoReplyMessage: string;
        quickAnswers?: IMeevoMessengerQuickAnswer[];
        classification: string;
        useBusinessHours: boolean;
    }

    interface IMeevoMessengerAutoReply {
        autoReplyMessage?: string;
        default?: string;
        editingLabel?: string;
        name: string;
        body?: string;
        type: MeevoMessengerAutoRepliesEnum;
    }

    export interface IMeevoMessengerConversationMessages {
        attachments?: Array<IMeevoMessengerAttachmentItem> & Array<{ unsupportedAttachment: boolean }>;
        tenantId?: number;
        locationId?: number;
        messageId: Guid;
        dateCreated?: Date;
        dateUpdated?: Date;
        isRead?: boolean;
        isInbound: boolean;
        hasAttachment: boolean;
        conversationId?: Guid;
        messageBody: string;
        userIdSender?: string;
        externalMessageId: string;
        displayName?: string;
        isShowTimeLine?: boolean;
        senderUserName: string;
    }

    export interface IMeevoMessengerNotification {
        attachments?: Array<IMeevoMessengerAttachmentItem> & Array<{ unsupportedAttachment: boolean }>;
        dateCreated: Date;
        isRead?: boolean;
        conversationId: Guid;
        messageId: Guid;
        externalMessageId: string;
        isInbound: boolean;
        clientId?: Guid;
        employeeId?: Guid;
        isShowTimeLine?: boolean;
        messageBody: string;
        userIdSender?: Guid;
        hasAttachment: boolean;
        classification: TMeevoMessengerClassification;
    }

    export type TMeevoMessengerClassification = 'TEXTEL' | 'ARRIVALS' | 'CLIENTAPP' | 'SMS';

    export interface IMeevoMessengerNewConversations {
        [key: string]: {
            [key: string]: Array<Guid>;
        };
    }

    export interface IMeevoMessengerAttachmentItem {
        mimeType: string;
        attachment: string;
    }

    interface NoteBase {
        noteText: string;
        noteType: number;
        noteTypeDescription?: string;
    }
    interface appointNote extends NoteBase {
        appointmentId?: number;
        clientIds?: any[];
        displayNames?: any[];
    }
    interface saleNote extends NoteBase {
        saleId?: number;
        saleDate?: string;
        locationId?: number;
    }
    interface IEmmaAccountSettings extends DataMaintenanceWithLanguage {
        accountId: number;
    }

    interface IEmmaSMSAccountSettings {
        tenantId?: number;
        locationId?: number;
        storeName?: string;
        webAddressUrl?: string;
        contactFirstName?: string;
        contactLastName?: string;
        emailAddress?: string;
        phoneNumber?: string;
        countryCode?: string;
        address?: Address;
        taxId?: string;
        legalEntityType?: string;
        businessRegistrationType?: string;
        businessDba?: string;
    }

    export interface IAuditLog {
        modifiedByUserName: string;
        dateUpdated: string;
        entityId: string;
        noun: NounEnum;
        verb: VerbEnum;
    }

    export interface IAppointmentAuditLog extends IAuditLog {
        actions: string[];
    }

    export interface IBlockAuditLog extends IAuditLog {
        entries: IBlockAuditLogEntry[];
        blockTypeDisplayName: string;
        isStandingBlock: boolean;
        blockScheduleId: string;
    }

    interface IBlockAuditLogEntry {
        propertyName: string;
        oldValue: string;
        newValue: string;
        verb: VerbEnum;
    }

    export interface EmailMarketingSettings {
        accountId: number;
        syncStatus: boolean;
        locationLevelSubaccount: boolean;
        yearThreshold: number;
        includeInactive: boolean;
        isTFNVerificationSubmitted?: boolean;
        dateTFNVerificationSubmitted?: Date;
    }

    export interface MarketingSmsUsageCredit {
        displayName?: string;
        totalUsage?: number;
        totalCount?: number;
    }

    export interface PaymentPlan {
        id?: string;
        downPayment?: number;
        numberOfPayments?: number;
        firstPaymentDate?: Date;
        note?: string;
        client?: Model.ClientListItem;
        clientId?: string;
        paymentAccount?: Model.PaymentAccount;
        maxInstantRefundAmount?: number;
        amount?: number;
        hasChanged?: boolean;
        entityVersion?: number;
        hasPendingBill?: boolean;
        paymentAccountId?: string;
        remainingBalance?: number;
    }

    export interface IDashboardCategory {
        id: string;
        displayName: string;
        description: string;
        visible: boolean;
        createdDateTime: string;
        modifiedDateTime: string;
        dashboards?: IDashboard[];
    }
    export interface IDashboard {
        id: string;
        categoryId: string;
        displayName: string;
        createdDateTime: string;
        modifiedDateTime: string;
    }

    export interface DateTimeRange {
        startDateTime: Date,
        endDateTime: Date
    }

    export interface TippyKiosk extends DataMaintenance {
        deviceId?: string;
        originalDeviceId?: string;
        lockedByUserId?: string;
    }

    export interface PackageProductUnitDataDetail extends PackageItemDataDetail {
        productId?: string;
        quantity?: number;
    }

    interface IAlertManagerEmployee {
        id: Guid;
        user_Tid: Guid;
        employeeCode: string;
        objectStateEnum: ObjectStateEnum;
        employeeCategories?: Model.EmployeeCategoryListItem[];
    }

    interface IProductUsageService {
        serviceId: string;
        serviceShortcut: string;
        serviceDisplayName: string;
        variants: IProductUsageVariant[];

        isExpanded: boolean;
        newVariant: any;
        excludedVariants: { id: string }[];

        hasFlatFeeProductUsage?: boolean;
    }

    interface ISaleLineServiceProductUsage extends IProductUsageService {
        isPartialRedemptionRemainder?: boolean;
        preRedemptionUsageSnapshot?: IProductUsageService;
        hasFlatFeeProductUsage?: boolean;
    }

    interface IProductUsageVariant {
        productId: string;
        variantId: string;
        productSku: string;
        productDisplayName: string;
        variantDisplayName: string;
        unitOfMeasure: Meevo.Model.UnitEnum;
        productUsageAmountType: Meevo.Model.ProductUsageAmountTypeEnum;
        defaultUsageAmount?: number;
        usageAmount: number;
        usageCharge: number;
        previousUsageAmount?: number;
        chargeType: Meevo.Model.ProductVariantChargeTypeEnum
    }

    export interface IServiceProductVariantUsage {
        id?: string;
        entityVersion?: string;
        isProductUsageEnabled?: boolean;
        packageServiceRedemptionType?: PackageServiceRedemptionEnum;
    }

}

module Meevo.Model {
    export enum PackageServiceRedemptionEnum {
        Service = 11045829,
        ProductUnit = 11045830,
    }

    export enum SecurityTypeEnum {
        Command = 2060,
        Entity = 2058,
        Feature = 2057,
        Field = 2061,
        Section = 2059,
    }

    export enum SecurityAccessEnum {
        Deny = 2055,
        Hide = 2054,
        Grant = 2056,
    }

    export enum ReportAvailabilityEnum {
        GlobalCentralOffice = 1,
        Location = 2,
        Both = 3,
    }

    export enum SortDirection {
        Ascending,
        Descending,
    }

    export enum GetResult {
        Slim,
        Full,
    }

    export enum TimeClockStateEnum {
        ClockedOut,
        ClockedIn,
        BreakOut,
        BreakIn,
    }

    export enum ObjectCreationTypeEnum {
        System = 231,
        User = 232,
        Diagnostic = 1883,
        Starter = 1885,
        Conversion = 1968,
    }

    export enum ObjectStateEnum {
        Pending = 2029,
        Active = 2026,
        Inactive = 2027,
        Deleted = 2028,
        Merge = 2030,
        Exclude = 2112,
        Purged = 11039628
    }

    export enum DeviceTypeEnum {
        Printer = 2274,
        CreditCard = 2275,
        PosStation = 2290,
    }

    export enum ProductTypeEnum {
        Retail = 212,
        ProfessionalSupply = 213,
    }

    export enum GenderEnum {
        Male = 92,
        Female = 93,
        Other = 94,
        NotSpecified = 2035,
    }

    export enum PronounEnum{
        NotSpecified = 11045507,
        SheHer = 11045508,
        HeHim = 11045509,
        TheyThem = 11045510,
        HeThey = 11045511,
        SheThey = 11045512,
    }

    export enum MaritalStatusEnum {
        Married = 86,
        Single = 87,
        Divorced = 88,
        Widowed = 89,
        Separated = 90,
        DomesticPartner = 91,
        Other = 95,
    }

    export enum ReferredByEnum {
        Client = 1248,
        Employee = 1249,
        Referral = 1250,
        Student = 11045126,
        Teacher = 11045127,
    }

    export enum CostFrequencyEnum {
        PerDay = 1242,
        PerWeek = 1243,
        PerMonth = 1244,
        PerQuarter = 1245,
        PerYear = 1246,
        OneTimeChange = 1247,
        NoCharge = 1873,
    }

    export enum PaymentValueTypeEnum {
        Monetary = 235,
        Nonmonetary = 236,
    }

    export enum SaleLineStatusEnum {
        Added = 0,
        Updated = 1,
    }

    export enum SaleLineNoRefundReasonEnum {
        UnlimitedPackage = 1,
    }

    export enum PayInOutTypeEnum {
        PayIn = 229,
        PayOut = 230,
    }

    export enum PayInOutEnum {
        PayIn = 2204,
        PayOut = 2205,
    }

    export enum PurchaseOrderStateEnum {
        NotReceived = 1643,
        PartiallyReceived = 1651,
        ReceivedInFull = 1642,
    }

    export enum AddressTypeEnum {
        Home = 64,
        Work = 65,
        Other = 66,
    }

    export enum TaxItemTypeEnum {
        Default = 2251,
        Membership = 2282,
        Product = 2198,
        Service = 2197,
    }

    export enum RelationshipTypeEnum {
        Brother = 1620,
        Family = 1621,
        Father = 1622,
        Friend = 1623,
        Mother = 1624,
        Sister = 1625,
        Spouse = 1626,
        DomesticPartner = 1856,
    }

    export enum UnitEnum {
        CubicCentimeter = 11045606,
        FluidOunce = 11045607,
        Gram = 11045608,
        InternationalUnit = 11045609,
        Milliliter = 11045610,
        Ounce = 11045611,
    }

    export enum ProductUsageAmountTypeEnum {
        WholeProduct = 11045615,
        PartialAmount = 11045616,
    }


    export enum PriceLevelEnum {
        Level1 = 2065,
        Level2 = 2066,
        Level3 = 2067,
        Level4 = 2068,
        Level5 = 2069,
        Level6 = 2070,
        Level7 = 2071,
        Level8 = 2072,
        Level9 = 2073,
    }

    export enum EmployeeAppointmentBookViewEnum {
        All = 2127,
        Cascade = 30029,
        MyDay = 2128,
        MyWeek = 2129,
    }

    export enum PhoneTypeEnum {
        Work = 19,
        Fax = 20,
        Mobile = 21,
        Home = 22,
        Other = 23,
    }

    export enum EmailTypeEnum {
        Personal = 96,
        Work = 97,
        Other = 98,
        General = 175,
    }

    export enum CommunicationTypeEnum {
        AppointmentConfirmation = 1795,
        FiveStarSurvey = 30423,
        Marketing = 1796,
        Receipt = 1797,
        AppointmentNoShow = 1798,
        EmployeeCommunication = 1799,
        AchPaymentFailed = 1800,
        GiftCertAboutToExpire = 1801,
        AppointmentReminderOnAdd = 1817,
        AppointmentReminderOnUpdate = 1832,
        AppointmentReminderDaysBefore = 1833,
        AppointmentReminderOnCancel = 2036,
        GameRewardRedemptionNotification = 1890,
        EmployeeAppointmentBooked = 2077,
        EmployeeAppointmentCancelled = 2079,
        EmployeeAppointmentUpdated = 2078,
        SaleCreated = 30023,
        InsuranceReceiptCreated = 30102,
        GenericOptInRequested = 2103,
        ACHPaymentFailed = 1800,
        GenericStop = 2100,
        GenericHelp = 2101,
        GenericInvalidRequest = 2102,
        GenericOptInConfirmed = 2104,
        GenericAppointmentConfirmed = 2105,
        GenericAppointmentRescheduled = 2106,
        AppointmentRescheduled = 2107,
        AppointmentConfirmed = 2108,
        EmailUnsubscribe = 2109,
        AppointmentAddToCalendar = 2113,
        AppointmentRemoveFromCalendar = 2114,
        WaitListOpeningCreated = 2174,
        WaitListConfirmToRemain = 2175,
        WaitListClientRemoved = 2176,
        WaitListPendingExpired = 2182,
        WalkInClientAdded = 2186,
        WalkInPositionOne = 2200,
        WalkInPositionX = 2201,
        WalkInMissed = 2216,
        WalkInClosing = 2217,
        AppointmentPageOnCheckin = 10025,
        AppointmentPageOnRunningLate = 10026,
        AppointmentPageOnNoShow = 10027,
        AppointmentConfirmationError = 10043,
        GenericOptInAndAppointmentConfirm = 10046,
        SecurityOverrideOccured = 20041,
        SecuredFeatureAccessed = 20042,
        GiftCardSoldLessThanValue = 20043,
        ProactiveAlertDailyLog = 20044,
        WalkInSms = 20148,
        EgiftPurchaserEmailForReadyToPickup = 20179,
        GiftReceiptCreated = 30071,
        IntegrationError = 30114,
        PaymentTypeIsUsed = 30320,
        RefundWasGiven = 30321,
        ServiceIsRefunded = 30322,
        ProductIsRefunded = 30323,
        TransactionIsVoided = 30324,
        EmployeeClocksInEarlyOrLate = 30325,
        EmployeeClocksOutEarlyOrLate = 30326,
        ClosingCashIsOverShortMoreThanAmount = 30331,
        GiftCardIsRedeemedMoreThanMonthsOld = 30332,
        SpecificMembershipIsSold = 30333,
        MembershipIsSold = 30334,
        SpecificGiftCardTypeIsSold = 30335,
        GiftCardIsRedeemedByNotOriginRecipient = 30336,
        GiftCardIsRedeemedForAmountGreaterThanTicketTotal = 30337,
        GiftCardIsRedeemedWithGiftCardPurchase = 30338,
        AccessIsDeniedToCertainFeature = 30369,
        AppointmentIsBookedViaOnlineBooking = 30370,
        EmployeeWorkedMoreThanScheduledTime = 30371,
        QuickbooksHasNotBeenSyncedForAmountOfTime = 30503,
        DocsAndForms = 30518,
        AppointmentCheckIn = 11045056,
        SelfPay = 11044963,
        SelfPayReminder = 11044964,
        AppointmentNoShowed = 11045425,
        ReferralThankYou = 11045438,
        ProductPurchaseReminder = 11045439,
        MeGoWelcome = 11045716,
        PostServiceCare = 11045725,
        AppointmentRunningLate = 11045737,
        AppointmentCheckedIn = 11045057,
        DueInForServiceReminder = 11045752,
        MembershipForms = 11045795,
        OnlineMembershipCancelled = 11045800,
        OneTimePassword = 11045819,
        PaymentPlanForm = 11045913,
    }

    export enum CommunicationTypeGroupEnum {
        ClientAppointmentsImmediate = 11045456,
        ClientAppointmentsScheduled = 11045457,
        ClientMiscellaneous = 11045458,
        EmployeeAppointmentsImmediate = 11045459,
        EmployeeAppointmentsScheduled = 11045460,
        EmployeeMiscellaneous = 11045464,
        OptInOptOut = 11045465,
        ProactiveAlerts = 11045466,
        Reports = 11045467,
        ClientInvalidMessages = 11045468,
        WaitList = 11045469,
        WalkIns = 11045470,
        Receipts = 11045471,
        ClientAppointmentsAcknowledgement = 11045472,
    }

    //User type
    export enum CommunicationTypeUserEnum {
        Client = 2088,
        Employee = 2089,
    }

    export enum BulkClientNotificationUpdateEnum {
        RemoveOverrideIfItMatchDefault = 11044968,
        RemoveForAllClient = 11044969,
        AddOverrideForClientsWithoutOne = 11044970,
        ClearOut = 11044971
    }

    export enum BulkUpdateProcessingStatusEnum {
        ToBeProcessed = 11045024,
        Completed = 11045025,
        Error = 11045026
    }

    export enum LevelSettingEnum {
        BusinessType = 2121,
        Location = 2122,
        System = 2119,
        Tenant = 2120,
    }

    export enum OverrideRuleEnum {
        LowestLevel = 2118,
        OffOverride = 2117,
    }

    export enum BusinessSettingCategoryEnum {
        AccountSyncIntegration = 30363,
        AppointmentBook = 2252,
        ClientPoint = 2418,
        Clients = 2253,
        Compensation = 2416,
        Cultural = 30290,
        CustomBranding = 2412,
        EGifts = 30362,
        General = 20001,
        HelpDeskTicket = 30186,
        Integration = 30209,
        LicenseInsurance = 30126,
        Location = 30195,
        Membership = 2374,
        Notification = 2255,
        OnlineBooking = 2257,
        OnlineBookingSite = 20052,
        Register = 2258,
        Security = 2311,
        Tax = 2259,
        Users = 2254,
        WalkInManager = 2256,
        SmartCenter = 2436,
        Period = 10055,
        PaymentPlans = 11045029,
    }

    export enum Currency {
        USD = 2194,
    }

    export enum TaxPolicy {
        USTAX = 2220,
    }

    export enum CommunicationOptedInStateEnum {
        OptedIn = 2086,
        OptedOut = 2087,
        Requested = 2085,
        Scheduled = 11039630,
        Undeliverable = 11045708,
        Deactivated = 11045715,
    }

    export enum DefaultSortTypeEnum {
        FirstNameLastName = 2132,
        LastNameFirstName = 2133,
    }

    export enum ClientVisitCalculatedBasedOnEnum {
        AllTransactions = 2379,
        ServiceAndProduct = 2378,
        ServiceOnly = 2377,
    }

    export enum DateFormatEnum {
        NonUSDateFormat = 30299,
        USDateFormat = 30298,
    }

    export enum TimeFormatEnum {
        TimeFormat12 = 30296,
        TimeFormat24 = 30297,
    }

    export enum DrawerStateEnum {
        Close = 1738,
        Open = 1737,
    }

    export enum SaleRankFilterTypeEnum {
        ClientsBuyingServices = 1973,
        ClientsBuyingProducts = 1974,
        EmployeesSellingServices = 1975,
        EmployeesSellingProducts = 1976,
        ServicesSold = 1977,
        ProductsSold = 1978,
        ClientsBuyingProductsOrServices = 20152,
        EmployeesSellingProductsOrServices = 20153,
        ProductsOrServicesSold = 20154,
        ClientsBuyingGiftCards = 11039644,
        EmployeesSellingGiftCards = 11039645,
        GiftCardsSold = 11039646,
        ClientsBuyingPackages = 11039647,
        EmployeesSellingPackages = 11039648,
        PackagesSold = 11039649,
        ClientsBuyingMemberships = 11039650,
        EmployeesSellingMemberships = 11039651,
        MembershipsSold = 11039652,
        AverageTicket = 11039907,
        Rebooking = 11039908,
        RetailToService = 11039909,
        RetailToTotal = 11039910
    }

    export enum EnumTypeEnum {
        AccountQueryTypeEnum = 1078,
        AccountTypeEnum = 1041,
        AchievementMeasurementEnum = 1135,
        AchievementTypeEnum = 1137,
        AddressTypeEnum = 2,
        AdornerTypeEnum = 39,
        AppointConfirmStateEnum = 33,
        AppointHistoryStateEnum = 38,
        AppointmentIncrementEnum = 1098,
        AppointStateEnum = 34,
        ApprovalTypeEnum = 1114,
        AuthenticationTypeEnum = 41,
        AutoRenewTypeEnum = 1217,
        AvedaVersionEnum = 11044881,
        ExternalInventoryAccessTypeEnum = 11044886,
        BasketRequirementCategoryEnum = 1089,
        BOGOBasedOnTypeEnum = 30057,
        BookingMethodEnum = 32,
        BusinessResourceRequiredEnum = 28,
        BusinessTypeEnum = 49,
        CardEntryTypeEnum = 30083,
        CardPaymentEntryMethodEnum = 1079,
        CategoryTypeEnum = 1139,
        ChallengeMeasurementEnum = 1138,
        ClientTypeEnum = 1157,
        ClientVisitTypeEnum = 1141,
        CommissionCalculationTypeEnum = 30006,
        CommissionPeriodIntervalEnum = 1122,
        CommissionPeriodStateEnum = 1123,
        CommissionProcessorTypeEnum = 1109,
        CommissionRuleTypeEnum = 1108,
        CommissionScaleTypeEnum = 30007,
        CommunicationMethodEnum = 1120,
        CommunicationOptedInStateEnum = 1169,
        CommunicationProtocolEnum = 1129,
        CommunicationStateEnum = 1121,
        CommunicationTypeEnum = 1119,

        ConfirmationTypeEnum = 36,
        CostFrequencyEnum = 1010,
        CreditAuthTypeEnum = 1113,
        DaemonProcessingStateEnum = 1116,
        DailyRecurringFrequencyTypeEnum = 6,
        DataTypeEnum = 35,
        DateTimeUnitEnum = 7,
        DecimalRoundingPlaceEnum = 1211,
        DeletedInactiveFilterSelectionEnum = 1097,
        DiscountAccountingEnum = 1086,
        DiscountApplicationMethodTypeEnum = 1080,
        DiscountAppliesToEnum = 1081,
        DiscountEffectEnum = 1082,
        DiscountEntityGranularityTypeEnum = 1208,
        DiscountEntityTypeEnum = 1207,
        DiscountGroupTypeEnum = 1085,
        DiscountReapplicationEnum = 1092,
        DiscountRedeemableLimitPerTypeEnum = 1205,
        DiscountRuleComputationTypeEnum = 1094,
        DiscountRuleEvalEnum = 1084,
        DiscountTypeEnum = 1206,
        DrawerActionEnum = 1101,
        DrawerStateEnum = 1100,
        DurationTypeEnum = 1134,
        DynamicAccountEntityTypeEnum = 1059,
        ElevatableEnum = 16,
        ElevateDurationEnum = 17,
        EmailTypeEnum = 21,
        EmployeeAppointmentBookViewEnum = 1177,
        EmployeeGenderSearchEnum = 1132,
        EmployeePayTotalCalculationEnum = 1111,
        EmployeeLicenseEnum = 1235,
        EntityRuleTypeEnum = 1107,
        EFTTypeEnum = 1234,
        EquipmentRequiredEnum = 29,
        EthnicityEnum = 30,
        EvaluationTypeEnum = 1112,
        FieldTypeEnum = 40,
        FrequencyTypeEnum = 7,
        GenderEnum = 20,
        GenderPreferenceEnum = 23,
        GlobalTypeEnum = 1126,
        GoalStateEnum = 1136,
        IncludedEmployeeEnum = 1146,
        InventoryAdjustCommitActionEnum = 1053,
        InventoryCountActivityTypeEnum = 1052,
        InventoryCountSheetFilterTypeEnum = 1058,
        InventoryCountSheetItemStateEnum = 1056,
        InventoryCountStateEnum = 1087,
        InventoryCountTypeEnum = 1051,
        ProductInventoryTypeEnum = 1228,
        InventoryManagementUsabilityLevelEnum = 1102,
        ItemCatTypeEnum = 12,
        ItemTypeEnum = 9,
        KeywordTypeEnum = 47,
        LockPurposeEnum = 1096,
        LogicalOperatorEnum = 1064,
        LogicalSelectionTypeEnum = 1065,
        LoginEnabledEnum = 43,
        LogLevelEnum = 1115,
        MaritalStatusEnum = 19,
        MeasurementEnum = 1133,
        MeevoEditionTypeEnum = 1144,
        MembershipCategoryEnum = 1090,
        MonthlyRelativeDayEnum = 4,
        MonthlyRelativeWeekEnum = 5,
        NoteTypeEnum = 31,
        ObjectCreationTypeEnum = 53,
        OvertimeFrequencyEnum = 1128,
        OvertimeTypeEnum = 1142,
        PackageCreationTypeEnum = 1151,
        PackageRedemptionTypeEnum = 1150,
        PackageStyleEnum = 1044,
        PayInOutEnum = 1190,
        PayInOutPaymentTypeEnum = 1103,
        PayInOutTypeEnum = 52,
        PaymentCategoryEnum = 1091,
        PaymentResponseTypeEnum = 1055,
        PaymentTypeEnum = 1048,
        PaymentValueTypeEnum = 55,
        PayrollFrequencyEnum = 30044,
        PerformedByEnum = 27,
        PermissionToConnectEnum = 42,
        PhoneTypeEnum = 1,
        PriceLevelEnum = 1165,
        ProcessingTypeEnum = 1106,
        ProductPackageTypeEnum = 1229,
        UnitEnum = 11044857,
        ProductUsageAmountTypeEnum = 11044858,
        ProductTypeEnum = 48,
        ProductUsageModeEnum = 1095,
        PromoPointEnum = 1149,
        PromoTypeEnum = 1148,
        PronounEnum = 11044834,
        PurchaseOrderLineStatusEnum = 1067,
        PurchaseOrderSheetFilterTypeEnum = 1068,
        PurchaseOrderSheetItemStateEnum = 1070,
        PurchaseOrderSheetRetailFilterEnum = 1069,
        PurchaseOrderStateEnum = 1074,
        ReferredByEnum = 1011,
        RefundTypeEnum = 1042,
        RelationshipTypeEnum = 1071,
        ReportFilterCreationTypeEnum = 1060,
        ReportInstanceTypeEnum = 1061,
        ReportOutputDestinationEnum = 1063,
        ReportOutputFormatEnum = 1062,
        RoundingModeEnum = 1072,
        RuleEnum = 1073,
        SaleRankFilterTypeEnum = 1147,
        SaleStateEnum = 1043,
        SalesTypeEnum = 1125,
        SaleTempStateEnum = 1104,
        ScanDateTypeEnum = 1171,
        ScanTimeTypeEnum = 1172,
        ScheduleCopyFrequencyTypeEnum = 1075,
        ScheduleCopyRuleTypeEnum = 1076,
        ScheduleTypeEnum = 37,
        SecurityAccessEnum = 1162,
        SecurityAllowEnum = 14,
        SecurityAttribTypeEnum = 18,
        SecurityCatTypeEnum = 1105,
        SecurityRestrictToEnum = 15,
        SecurityTypeEnum = 1163,
        SequencePositionEnum = 5,
        ServiceCommissionAdjustmentTypeEnum = 30008,
        ServiceStepTypeEnum = 22,
        SetupModeEnum = 1110,
        SmartTIleCategoryEnum = 20012,
        SoldMethodEnum = 1050,
        StrategyDateSelectionEnum = 46,
        StrategyEmployeeSelectionEnum = 44,
        StrategyRuleEffectEnum = 25,
        StrategyServiceSelectionEnum = 45,
        StrategyTypeEnum = 26,
        SubscriptionStatusEnum = 1127,
        SupplyCategoryEnum = 30029,
        TaxTypeEnum = 1045,
        TemplateTypeEnum = 1145,
        TicketCountTypeEnum = 1143,
        TimeClockActualEnum = 1117,
        TimeClockStateEnum = 1088,
        TimePeriodEnum = 1124,
        TransBalanceTypeEnum = 1131,
        TransEffectReasonEnum = 1046,
        TransTypeEnum = 1047,
        TriPosDeviceTypeEnum = 1152,
        VisibilityEnum = 54,
        WageTypeEnum = 1118,
        WeekdayNameEnum = 51,
        WidgetGenderEnum = 1140,
        EmployeeTypeEnum = 1158,
        ReBookingTypeEnum = 1160,
        BookingTypeEnum = 1159,
        RegisterReceiptTypeEnum = 1204,
        CommunicationTypeUserEnum = 1170,
        NotificationTypeEnum = 1177,
        DefaultSortTypeEnum = 1179,
        EmployeeScanSearchEnum = 1183,
        VariantTypeEnum = 1184,
        CurrencyEnum = 1186,
        TaxPolicyEnum = 1187,
        TaxItemTypeEnum = 1188,
        ClientVisitCalculatedBasedOnEnum = 1232,
        NewClientIndicatorEnum = 1233,
        PastTransactionsEnum = 30004,
        ServiceDeductionRuleEnum = 30016,
        ServiceOrderByEnum = 11044785,
        AccessLevelEnum = 11044790,
        RetailCommissionsPaidOutEnum = 30015,
        ImageGalleryCategoryEnum = 30031,
        MissedOpportunityReasonEnum = 30039,
        MissedOpportunityTimeTypeTypeEnum = 30040,
        AppointFinalStateEnum = 1161,
        CountryEnum = 30059,
        DateFormatEnum = 30061,
        TimeFormatEnum = 30060,
        TipEntryMethodEnum = 30085,
        SignatureEntryMethodEnum = 30086,
        ChargeTypeEnum = 11039658,
        PayrollInputTypesEnum = 11039653,
        PayrollAdpRequestStatusEnum = 11039660,
        WorkActivityTypeEnum = 11039843,
        ShowRebookPromptEnum = 11044716,
        ApplyServiceDeductionsEnum = 11039692,
        AppointmentBookRequestNonRequestSelectionEnum = 11044711,
        AlertsTriggeredByEnum = 11044717,
        AdpIntegrationTypeEnum = 11044754,
        ShowThisPromotionUponLoginEnum = 11044780,
        ElementTypeEnum = 11044781,
        VerticalAlignmentEnum = 11044783,
        HorizontalAlignmentEnum = 11044784,
        LiabilitiesMR035Enum = 11044848,
		CashRoundingModeEnum = 11044908,
    }

    export enum AppointmentBookRequestNonRequestSelectionEnum {
        RequestedByDefault = 11044917,
        NonRequestByDefault = 11044918,
        RequireSelectionRequestNotRequest = 11044919
    }

    export enum TipEntryMethodEnum {
        OnScreen = 30510,
        Terminal = 30511,
        Prompt = 30512,
    }

    export enum SignatureEntryMethodEnum {
        OnScreen = 30513,
        OnReceipt = 30514,
        Prompt = 30515,
        OnTerminal = 11045905,
    }

    export enum RetailCommissionsPaidOutEnum {
        EveryTwoMonths = 30066,
        EveryTwoWees = 30064,
        Monthly = 30065,
        Quarterly = 30067,
        Weekly = 30063,
        Yearly = 30068,
    }

    export enum ServiceDeductionRuleEnum {
        After = 30070,
        Before = 30069,
    }

    export enum ServiceOrderByEnum {
        DisplayName = 11045306,
        Shortcut = 11045307,
    }

    export enum OvertimeFrequencyEnum {
        CommissionPeriod = 1848,
        Daily = 1846,
        Weekly = 1847,
    }

    export enum PayrollFrequencyEnum {
        Weekly = 30198,
        BiWeekly = 30199,
        Monthly = 30200,
        SemiMonthly = 30201,
        FourWeek = 11039756,
        CustomSemiMonthly = 11039855,
        CustomMonthly = 11045159,
        Quarterly = 11045162
   }

    export enum PayrollPeriodStatusEnum {
        Archive = 20066,
        Current = 20064,
        Inactive = 20087,
        Open = 20065
    }

    export enum PayrollPeriodTypeEnum {
        Full = 20088,
        Terminated = 20090,
        Transition = 20089
    }

    export enum PayrollProcessingStatusEnum {
        Completed = 20069,
        Error = 20070,
        Processing = 20068,
        ToBeProcessed = 20067
    }

    export enum OvertimeTypeEnum {
        Advanced = 1948,
        Standard = 1947,
    }

    export enum CountryEnum {
        Australia = 30294,
        Canada = 30293,
        Germany = 30424,
        Japan = 30467,
        Mexico = 30470,
        NewZealand = 30469,
        Norway = 30468,
        Spain = 30425,
        Switzerland = 30295,
        UnitedKingdom = 30292,
        UnitedStates = 30291,
        TrinidadAndTobago = 40002,
        Ireland = 11039654,
        Bahrain = 11044943,
        Austria = 11045049,
        Other = 30526,
        Gibraltar = 11045759,
    }

    export enum CountryIdEnum {
        en_us = CountryEnum.UnitedStates,
        en_au = CountryEnum.Australia,
        en_ca = CountryEnum.Canada,
        de_de = CountryEnum.Germany,
        ja_jp = CountryEnum.Japan,
        es_mx = CountryEnum.Mexico,
        en_nz = CountryEnum.NewZealand,
        nb_no = CountryEnum.Norway,
        es_es = CountryEnum.Spain,
        fr_ch = CountryEnum.Switzerland,
        en_gb = CountryEnum.UnitedKingdom,
        en_tt = CountryEnum.TrinidadAndTobago,
        en_ie = CountryEnum.Ireland,
        ar_bh = CountryEnum.Bahrain,
        de_at = CountryEnum.Austria,
        en_gi = CountryEnum.Gibraltar
    }

    export enum TaxPolicyEnum {
        USTax = 2195,
        CATax = 2196,
        AUTax = 10028,
        CHTax = 10029,
        JPTax = 10030,
        NOTax = 10031,
        NZTax = 10032,
        MXTax = 10033,
        GBTax = 30507,
        TTTax = 40004,
        IETax = 11039655,
        BHTax = 11044954,
        ATTax = 11045050,
        DETax = 11045051,
        ESTax = 11045052
    }

    export enum CurrencyEnum {
        USD = 2194,
        EUR = 30471,
        CAD = 30472,
        AUD = 30473,
        CHF = 30474,
        JPY = 30475,
        NOK = 30476,
        NZD = 30477,
        MXN = 30478,
        GBP = 30509,
        TTD = 40003,
        BHD = 11044953,
        GIP = 11045760
    }

    export enum CardEntryTypeEnum {
        Chip = 30493,
        Contactless = 30494,
        Keyed = 30495,
        Swipe = 30496,
        Unknown = 30492,
    }

    export enum AppointmentWarningsEnum {
        DoubleBook,
        NotSchedule,
        FOV,
    }

    export enum TimeCompareModes {
        StartTime,
        EndTime,
    }

    export enum BulkUpdateStatusEnum {
        Pending = 11045449,
        Processing = 11045450,
        Completed = 11045451,
    }

    export enum BulkUpdateTypeEnum {
        Product = 11045520,
        Variant = 11045521,
    }

    export enum DiscontinueBehaviorEnum {
        SelectDiscontinued = 11045452,
        DeselectDiscontinued = 11045453,
    }

    export enum NotForSaleBehaviorEnum {
        SelectNotForSale = 11045454,
        DeselectNotForSale = 11045455,
    }

    export enum ProductInventoryTypeEnum {
        Other = 2371,
        Retail = 2362,
        RetailOrShopSupply = 2364,
        ShopSupply = 2363,
    }

    export enum PurchaseOrderDiscountTypeEnum {
        PercentOff = 11045134,
        AmountOff = 11045135,
        SpecificPrice = 11045136,
    }

    export enum PurchaseOrderLineItemResponseCodeEnum {
        Succeeded = 11039791,
        Failed = 11039792,
        Discontinued = 11039793,
        None = 11039794
    }

    export enum ExternalInventoryAppEnum {
        Aveda = 11045761,
        SalonInteractive = 11045824
    }

    export enum FonfirmStateEnum {
        AwaitingResponse = 1872,
        Confirmed = 155,
        DoNotConfirm = 154,
        LeftMessage = 157,
        Queued = 1869,
        RespondedReschedule = 1870,
        Unconfirmed = 158,
    }

    export enum SecurityAllowEnum {
        Executable = 196,
        Hidden = 69,
        Read = 72,
        Write = 74,
    }

    export enum AppointDetailStateEnum {
        RunningLate = 163,
        CheckedIn = 164,
        NoShow = 166,
    }

    export enum ServiceStepTypeEnum {
        Servicing = 99,
        Gap = 100,
        Other = 101,
        Resource = 11045065,
    }

    export enum AppointStateEnum {
        Arrived = 180,
        Booked = 195,
        CheckedIn = 164,
        Conflict = 2184,
        NoShow = 166,
        Pending = 165,
        RunningLate = 163,
        WalkInNoShow = 2183,
        WalkInPending = 2151,
        WalkInRescan = 2152,
        WalkOut = 2192,
        StandingConflict = 20071,
    }

    export enum AppointFinalStateEnum {
        Cancelled = 2052,
        CheckedOut = 2051,
        None = 2053,
        Removed = 2083,
        Replaced = 2076,
    }

    export enum GenderPreferenceEnum {
        FemaleOnly = 109,
        FemalePreferred = 106,
        MaleOnly = 108,
        MalePreferred = 107,
        NonbinaryPreferred = 11045411,
        NoPreference = 105,
    }

    export enum AppointmentIncrementEnum {
        Min5 = 1731,
        Min10 = 1732,
        Min15 = 1733,
    }

    export enum SettingEnum {
        AppointmentBookTimeIncrementUsed = 11,
        RequireCancellationReasonWhenDeletingAnAppointment = 12,
        StartAppointmentBookDayOn = 13,
        HideColumnsWithoutSchedules = 29,
        AppointDetailTypeRequired = 58,
        RequireClientPhoneNumber = 98,
    }

    export enum StateEnum {
        Read = 1,
        Edit = 2,
        Add = 4,
        Delete = 5,
    }

    export enum StandingFrequenceyTypeEnum {
        Daily = 24,
        Weekly = 27,
    }

    export enum BlockDefinitionFrequenceyTypeEnum {
        Daily = 24,
        OneTime = 25,
        Weekly = 27,
    }

    export enum BookingMethodEnum {
        BookingAgent = 2178,
        FrontDesk = 151,
        Mobile = 20047,
        MyMeevo = 11045789,
        Online = 152,
        StandingDefinition = 2179,
        WaitList = 2180,
        WaitListManual = 2181,
        WalkinKiosk = 2159,
        WalkinOnline = 2160,
    }

    export enum NumberOfVisits {
        Zero = 1,
        One = 2,
        FiveOrLess = 3,
    }

    export enum AppointNoteTypeEnum {
        Checkin = 2031,
        Register = 2032,
        CheckinRegister = 2033,
        AppointmentBookOnly = 2034,
    }

    export enum ButtonTypeEnum {
        Ok = 1,
        OkCancel = 2,
        YesNo = 3,
        ContinueCancel = 4
    }

    export enum MessageBoxButtonResult {
        Ok = 1,
        Cancel = 2,
        Yes = 3,
        No = 4,
    }

    export enum AppointmentBookDateTypeFilter {
        Day = 1,
        Week = 2,
    }

    export enum ClientHistoryActionEnum {
        AddEmployeeAndService = 1,
        AddEmployee = 2,
        AddService = 3,
    }

    export enum ElevatableEnum {
        Allow = 76,
        Restrict = 77,
    }

    export enum ElevateDurationEnum {
        Screen = 79,
        Transaction = 78,
    }

    export enum AlertPriorityLevelEnum {
        High = 4,
        Good = 3,
        Normal = 2,
        Low = 1,
        None = 0,
    }

    export enum WeekdayNameEnum {
        Friday = 226,
        Monday = 222,
        Saturday = 227,
        Sunday = 228,
        Thursday = 225,
        Tuesday = 223,
        Wednesday = 224,
    }

    export enum MemberStatusEnum    {
        Member = 11045112,
        NonMember = 11045113,
        All = 11045114,
        AllMembers = 11045122
    }

    export enum PastTransactionsEnum {
        All = 30025,
        No = 30024,
        SameDay = 30026,
        WithinThreeDays = 30028,
        WithinTwoDays = 30027,
    }

    export enum MaintenanceFormState {
        Adding,
        Editing,
        Copying,
        Readonly,
        Pending,
    }

    export enum TypeaheadMode {
        Add,
        Edit,
    }

    export enum MaintenanceFormVisualState {
        Search,
        Detail,
        CustomScheduleLocation,
        CustomSchedulePrice,
    }

    export enum AppointmentBookState {
        EditAppointment,
        DeleteAppointment,
        RebookAppointment,
    }

    export enum EmployeeTypeEnum {
        NonRequest = 2044,
        Request = 2043,
    }

    export enum EmployeeRequestType {
        Add,
        Update,
        Disassociate,
        Delete,
    }

    export enum ReBookingTypeEnum {
        Normal = 2050,
        Rebooked = 2048,
        Redo = 2049,
        ReBookedRetroactive = 2306,
    }

    export enum BookingTypeEnum {
        Booked = 2047,
        PreBooked = 2046,
        Walkin = 2045,
    }

    export enum ClientTypeEnum {
        AnonymousFemale = 2040,
        AnonymousMale = 2041,
        AnonymousOther = 2042,
        Client = 2037,
        Employee = 2038,
        Transient = 2039,
    }

    export enum AppointConfirmStateEnum {
        AwaitingResponse = 1872,
        Confirmed = 155,
        DoNotConfirm = 154,
        LeftMessage = 157,
        Queued = 1869,
        RespondedReschedule = 1870,
        Unconfirmed = 158,
        UnableToContact = 20156,
    }

    export enum ConfirmStateEnum {
        AwaitingResponse = 1872,
        Confirmed = 155,
        DoNotConfirm = 154,
        LeftMessage = 157,
        Queued = 1869,
        RespondedReschedule = 1870,
        Unconfirmed = 158,
    }

    export enum ClientValidationTypeEnum {
        UniqueEmailCheck = 0,
        UniquePhoneCheck = 1,
    }

    export enum ClientAlertTypeEnum {
        MissingPhoneNumber = 0,
        MissingEmailAddress = 1,
        MissingContactInfo = 2,
        MissingReferral = 3,
        HasNotes = 4,
        HasActiveGiftCard = 5,
        MissingTextNotifications = 6,
        MissingTextMarketing = 7,
        MissingEmailMarketing = 8
    }

    export enum EthnicityEnum {
        Caucasian = 140,
        AfricanAmerican = 141,
    }

    export enum AuthenticationTypeEnum {
        WindowsAuthentication = 187,
        ActiveDirectory = 188,
        Millennium = 189,
    }

    export enum PermissionToConnectEnum {
        Grant = 190,
        Deny = 191,
    }

    export enum ClientSearchView {
        Default = 0,
        FromClientFind = 1,
        FromDataMaintenance = 2,
    }

    export enum EquipmentRequiredEnum {
        SameAsFirstStep = 136,
        Yes = 137,
        No = 138,
        SameAsPreviousStep = 139,
    }

    export enum BusinessTypeEnum {
        Salon = 214,
        Spa = 215,
        SalonSpa = 216,
        Clinic = 217,
        MediSpa = 218,
        Other = 1776,
    }

    export enum SubscriptionStatusEnum {
        Active = 1840,
        Frozen = 1841,
        Suspended = 1842,
        Cancelled = 1843,
        Terminated = 1844,
        Inactive = 1845,
        Pending = 30187,
        Upgrading = 30258,
    }

    export enum MeevoEditionTypeEnum {
        Standard = 1963,
        PaulMitchell = 1964,
        JcPenney = 2007,
    }

    export enum TimePeriodEnum {
        Today = 1818,
        ThisWeek = 1819,
        NextWeek = 1820,
        ThisMonth = 1821,
        Tomorrow = 1851,
        NextMonth = 1852,
        ThisQuarter = 1853,
        NextQuarter = 1854,
        ThisYear = 1855,
        Yesterday = 1857,
        LastWeek = 1858,
        LastMonth = 1859,
        LastQuarter = 1860,
        LastYear = 1861,
        YearToDate = 1862,
        LastYearToDate = 1863,
    }

    export enum DateTimeUnitEnum {
        Day = 24,
        Month = 26,
        Week = 27,
    }

    export enum DateTimeUnitsEnum {
        Daily = 11039700,
        Monthly = 11039702,
        Weekly = 11039701,
    }

    export enum MonthlyRelativeWeekEnum {
        First = 14,
        Second = 15,
        Last = 16,
        Third = 17,
        Fourth = 18,
    }

    export enum MonthlyRelativeDayEnum {
        Friday = 4,
        Monday = 5,
        WeekendDay = 6,
        Thursday = 7,
        Sunday = 8,
        Wednesday = 9,
        Day = 10,
        Weekday = 11,
        Saturday = 12,
        Tuesday = 13,
    }

    export enum DailyRecurringFrequencyTypeEnum {
        Minute = 1,
        Hours = 2,
        Second = 3,
    }

    export enum FileFormatEnum {
        CSV = 11039704,
        DOCX = 11039705,
        PDF = 11039706,
        XLS = 11039707,
        PPTX = 11039835,
        RTF = 11039836,
        IMAGE = 11039837,
        MHTML = 11039838,
        XPS = 11039839,
        XLSX = 11039840,
    }

    export enum RegisterReceiptTypeEnum {
        EmailReceipt = 2286,
        NoReceipt = 2288,
        PrintEmailReceipt = 30127,
        PrintReceipt = 2287,
        PromptReceipt = 2289,
    }

    export enum NewClientIndicatorEnum {
        NewtotheTenantNoServiceHistory = 2384,
        NewtotheLocation = 2386,
        NewtotheEmployee = 2387,
    }

    export enum ItemTypeEnum {
        Service = 29,
        Product = 76,
        GiftCardType = 32,
        Membership = 33,
        Package = 34,
        PaymentType = 36,
        Referral = 37,
        AppointType = 38,
        AppointReason = 39,
        Discount = 42,
        Department = 43,
        Distributor = 44,
        Equipment = 45,
        Manufacturer = 63,
        MembershipCancelReasons = 51,
        MembershipType = 52,
        Report = 54,
        BusinessResource = 55,
        SecurityRole = 67,
        WorkType = 84,
        BlockType = 103,
        JobFunction = 110,
        ServiceStep = 111,
        Strategy = 125,
        CommissionDefn = 128,
        VisitNoteType = 150,
        WidgetDefinition = 169,
        WidgetDefinitionProperty = 170,
        Desktop = 171,
        DesktopWidget = 172,
        DesktopWidgetProperty = 173,
        Keyword = 206,
        PayInOutType = 238,
        Drawer = 1499,
        PackageType = 1500,
        Workstation = 1502,
        ProductBin = 1517,
        AccountDefinition = 1604,
        DiscountGroup = 1664,
        CommTemplate = 1810,
        GameCharm = 1886,
        GameReward = 1888,
        GameAchievement = 1901,
        Template = 1967,
        SwapGroup = 2004,
        BusinessResourceCategory = 56,
    }

    export enum ItemCatTypeEnum {
        ClientCat = 58,
        EmployeeCat = 59,
        ServiceCat = 60,
        ProductCat = 61,
        EvaluationCat = 62,
        RecommendationCat = 63,
        AppointCat = 85,
        EquipmentCat = 126,
        BusinessResourceCat = 127,
        WidgetCat = 174,
        PaymentTypeCat = 237,
        ReferralCat = 1251,
        Account = 1498,
        ReportCat = 1603,
        GameCharmCat = 1887,
        GameRewardCat = 1889,
    }

    export enum LoginEnabledEnum {
        Enabled = 192,
        Disabled = 193,
    }

    export enum CommunicationProtocolEnum {
        OneWay = 1866,
        TwoWay = 1867,
    }

    export enum CommunicationMethodEnum {
        Email = 1803,
        None = 1880,
        Phone = 1804,
        SMS = 1802,
    }

    export enum ConfirmStateFilterEnum {
        All = 0,
        Confirmed = 1,
        Unconfirmed = 2,
        AwaitingReply = 3,
        RespondedReschedule = 4,
        UnableToContact = 5,
    }

    export enum TileAvailabilityLevelEnum {
        CoOnly = 1,
        LocationOnly = 2,
        BothCoAndLocation = 3
    }

    export enum NotificationModeType {
        Modal = 0,
        Toast = 1,
    }

    export enum DimensionTypeEnum {
        Date = 0,
        Employee = 1,
        Resource = 2,
        ResourceUnit = 3,
    }

    export enum DuplicationOptionEnum {
        Skip = 0,
        Overwrite = 1,
    }

    export enum EmployeeGenderSearchEnum {
        AnyEmployee = 1891,
        AnyFemale = 1893,
        AnyMale = 1892,
        RandomEmployee = 2161,
        SpecificEmployee = 2162,
    }

    export enum PackageItemTypeEnum {
        Service = 0,
        Product,
        ProductUnit
    }

    export class Enum {
        static getValues(e: any) {
            return Object.keys(e)
                .map(v => parseInt(v, 10))
                .filter(v => !isNaN(v));
        }

        static GetEnumList(enumType) {
            return Enum.getValues(enumType).map(v => {
                return { displayName: enumType[v] as string, value: v };
            });
        }
    }

    export class Guard {
        static isSomething(e: any, withFullTruthy: boolean = true): boolean {
            return withFullTruthy ? typeof e && e : e !== null && e !== undefined;
        }

        static isNothing(e: any, withFullTruthy: boolean = true): boolean {
            return !Guard.isSomething(e, withFullTruthy);
        }

        static returnSomething(e: any): any {
            return e === undefined ? null : e;
        }

        static canBeId(val: any) : boolean {
            return ["number", "string"].indexOf(typeof val) > -1
        }
    }

    export enum ScheduleCopyRuleTypeEnum {
        OverwriteAll = 1648,
        OverwriteWorkTime = 1649,
    }

    export enum ScheduleCopyFrequencyTypeEnum {
        FourthWeek = 1647,
        SecondWeek = 1645,
        ThirdWeek = 1646,
        Week = 1644,
    }

    export enum CoSearchType {
        CentralOffice = 0,
        Location = 1,
    }

    export enum CoSearchResultsFilter {
        All = 0,
        LocationOwned = 1,
        LocationOverrides = 2,
    }

    export enum CoAvailableResultsFilter {
        All = 0,
        Unavailable = 1,
        Available = 2,
    }

    export enum AppliedTemplateSectionTypeEnum {
        Main = 1,
        Categories = 2,
        Services = 3,
        Locations = 4,
        Compensations = 5,
        CommissionsOverrides = 6,
        Deduction = 7
    }

    export enum AppliedTemplateSectionUpdateTypeEnum {
        Replace = 0,
        Merge = 1,
    }

    export enum StyleEnum {
        HolidayAppointmentBook = 2126,
        HolidayScheduleEditor = 2125,
        ResourceSchedule = 2124,
        WorkActivityOff = 2123,
    }

    export enum EmployeeCalendarEditMode {
        Edit = 0,
        CopyDay = 1,
        CopyWeek = 2,
        CopyForward = 3,
    }

    export enum FileTypes {
        Image,
        ClientImage = 2157,
        EmployeeImage = 2158,
        SmartTileImage11 = 2419,
        SmartTileImage21 = 2420,
        SmartTileImage12 = 2421,
        SmartTileImage41 = 2422,
        SmartTileImage13 = 2423,
        PaymentTypeImage = 2278,
        WalkinSetting = 2283,
        SelfCheckInSetting = 50107,
        CustomDataFile = 2346,
        CustomDataImage = 2345,
        EgiftTemplate = 30428,
        BusinessInfo = 2393,
        CustomBrandingLogoImage = 2414,
        CustomBrandingBackgroundImage = 2415,
        Product = 2417,
        StockImage = 2412,
        ServiceImage = 30527,
        Document,
        CustomDataControlImage = 2500,
        ClientAppBusinessLogo = 11045292,
        ClientAppLoginScreen = 11045293,
        ClientAppHomeScreen = 11045294,
        ClientAppPromoBackground = 11045295,
        ClientAppPromoImage = 11045296,
        SmartTileImage31 = 11045321,
        VisitNoteImage = 11045322,
        MembershipDefinitionImage = 11045711,
        ClientGalleryImage = 11045831,
        ClientCustomBrandedAppScreen = 11045791
    }

    export enum ScanOriginEnum {
        Default = 0,
        OnlineBooking = 1,
        WalkinManager = 2,
        WalkinManagerRescan = 3,
    }

    export enum ScanDateTypeEnum {
        FirstAvailable = 2090,
        NextWeek = 2154,
        NextWeekend = 2156,
        Specific = 2094,
        ThisWeek = 2093,
        ThisWeekend = 2155,
        Today = 2091,
        Tomorrow = 2092,
    }

    export enum ScanTimeTypeEnum {
        Afternoon = 2097,
        Anytime = 2095,
        Custom = 2099,
        EndsBefore = 2164,
        Evening = 2098,
        Morning = 2096,
        StartsAfter = 2165,
    }

    export enum EmployeeSearchView {
        Default = 0,
        WithEmployeeServicePricing = 1,
        AllEmployeesWithCategories = 2
    }

    export enum ServiceSearchView {
        Default = 0,
        WithEmployeePriceRange = 1,
        LocationOwned = 2,
        CentralOfficeOwned = 3,
        ExcludeProductUnitRedeemedServices = 4
    }

    export enum WalkInStatusEnum {
        WaitingAndPresent = 2136,
        WaitingButNotPresent = 2137,
        CheckedOut = 2163,
    }

    export enum UserExistsAt {
        HomeLocations = 0,
        AllLocations = 1,
        CoOnly = 2,
    }

    export enum WakinAppointmentSearchView {
        Default = 0,
        NoShow = 1,
        InService = 2,
    }

    export enum ClientFoundState {
        None = 0,
        Existing = 1,
        CreatedNew = 2,
    }

    export enum ClientFoundType {
        None = 0,
        Email = 1,
        Phone = 2,
    }

    export enum WaitListStatus {
        Default = 0,
        NoShow = 1,
        InProgress = 2,
    }

    export enum WaitListSortType {
        Priority = 0,
        Client = 1,
    }

    export enum EFTTypeEnum {
        ACH = 2401,
        CreditCard = 2400,
    }

    export enum PaymentTypeEnum {
        ACH = 1458,
        Cash = 1462,
        Check = 1463,
        ClientAccount = 1464,
        Credit = 1460,
        Debit = 1461,
        Gift = 1448,
        GiftTip = 1449,
        GroupPost = 1457,
        OffsiteGift = 1453,
        OffsiteGiftTip = 1455,
        OffsitePackage = 1454,
        OffsitePackageTip = 1456,
        Other = 1518,
        Package = 1450,
        PackageTip = 1451,
        PaymentPlan = 1466,
        PointsRewards = 1465,
        SwissRounding = 1452,
        WaiveEFT = 1459,
    }

    export enum SalesTypeEnum {
        GiftCard = 1830,
        Membership = 2397,
        MembershipPayment = 2399,
        MembershipPaymentEFT = 2398,
        MembershipRenewal = 2398,
        MembershipWaive = 20002,
        Package = 1979,
        PackageMerge = 30125,
        PackageRefund = 30193,
        PackageServiceSwapFee = 30202,
        PackageSplit = 30124,
        PackageTransfer = 30123,
        Product = 1829,
        PromoGiftCard = 20049,
        PromoGiftCardPaymentOffset = 20050,
        EgiftProcessingFee = 20175,
        EgiftShippingFee = 20176,
        ReloadGiftCard = 30062,
        Service = 1828,
        ServiceCancellationFee = 30164,
        ServiceNoShowFee = 30372,
        AssociatedClient = 20003,
        Transfer = 30159,
        ClientAccountCredit = 30374,
        ClientAccountPayment = 30375,
        TicketCharge = 11039885,
        DepositCredit = 11044921,
        BulkGiftCard = 11045099,
        PaymentPlanPayment = 11045309,
        PaymentPlanPaymentEFT = 11045312,
        PaymentPlanReduction = 11045313,
        PaymentSurcharge = 11045704,
    }

    export enum SaleTypeFilterEnum {
        Service = 11045546,
        Product = 11045547,
        GiftCard = 11045556,
        Package = 11045579,
        Membership = 11045580,
        ClientAccountCredit = 11045581,
        ServicesOnly = 11045582,
        ProductsOnly = 11045583,
        TipsOnly = 11045584,
        All = 11045585,
    }

    export enum SaleStateEnum {
        AutoSave = 1627,
        Completed = 1398,
        Created = 1397,
        Hold = 1399,
        Resume = 1400,
        Void = 1401,
    }

    export enum TransTypeEnum {
        CashBack = 1631,
        CashReceived = 1630,
        ChargePerTicketDeduction = 1441,
        ClientAccount = 1545,
        ClientDiscountPlan = 1445,
        CommissionPaid = 1433,
        Coupon = 1443,
        DiscountPointOfSale = 1442,
        EmployeeCharge = 1549,
        EmployeeCommission = 1546,
        EmployeeDiscountPlan = 1444,
        EmployeeSalary = 1547,
        EmployeeTip = 1548,
        Gift = 1427,
        GiftTip = 1428,
        HouseCharge = 1532,
        LaborCostDeduction = 1439,
        Membership = 1432,
        MembershipDiscountPlan = 1446,
        MembershipDues = 1533,
        MembershipEnrollmentFee = 1534,
        MembershipIncome = 1535,
        MembershipRecurringFees = 1536,
        NewGuestDeduction = 1440,
        OverAmount = 1541,
        Package = 1430,
        PackageTip = 1429,
        PayIn = 1543,
        Payment = 1542,
        PayOut = 1544,
        Points = 1540,
        Product = 1425,
        ProductAsset = 1539,
        ProductCostOfGoods = 1538,
        ProductIncome = 1567,
        ProductLose = 1874,
        SalaryPaid = 1434,
        SalePromotion = 1447,
        Service = 1426,
        ServiceIncome = 1566,
        ShopCostDeduction = 1438,
        ShortAmount = 1537,
        Tan = 1431,
        TipPaid = 1435,
    }

    export enum TermsAndConditionsTypeEnum {
        WalkInTermsAndConditions = 2203,
    }

    export enum CustomerNameDisplayFormatEnum {
        FirstInitialCommaLastInitial = 2269,
        FirstInitialCommaLastName = 2271,
        FirstNameCommaLast4ofPhoneNumber = 2268,
        FirstNameCommaLastInitial = 2270,
    }

    export enum ControlTypeEnum {
        DropDown = 2261,
        SingleSelectList = 2189,
        MultiSelectList = 2190,
        Radio = 2262,
        ListBox = 2263,
        CheckBox = 2264,
        TextArea = 2188,
        TextBox = 2187,
        AlertingDatePicker = 2284,
        DatePicker = 2191,
        ImageUpload = 2265,
        DocumentUpload = 2266,
        Signature = 30502,
        Initials = 10023,
        Markup = 11039887
    }

    export enum AppAreaEnum {
        Booking = 2229,
        Checkout = 2233,
        Checkin = 2409,
        Client = 2231,
        Employee = 2230,
        None = 2267,
        Product = 2232,
        Service = 2281,
        DocsAndForms = 10024,
        Charts = 11045841,
    }

    export enum PaymentCategoryEnum {
        AnyPaymentForm = 1705,
        SpecificPaymentForms = 1706,
        ACH = 30093,
        Cash = 2218,
        Check = 2279,
        ClientAccount = 30192,
        CreditDebit = 2219,
        Debit = 2220,
        GiftCard = 2221,
        Other = 2244,
        PointsRewards = 30095,
        TRANSFER = 30094,
        Unknown = 30307,
        CashRounding = 11039605,
        CareCredit = 11045139,
        PaymentPlan = 11045147,
        DeferredVoid = 11045430,
    }

    export enum PaymentSubCategoryEnum {
        ACH = 30096,
        AmericanExpress = 2245,
        Cash = 2222,
        Check = 2280,
        AccountCharge = 30373,
        Debit = 2246,
        DinersClub = 30097,
        Discover = 2225,
        GiftCard = 2247,
        MasterCard = 2224,
        Other = 2248,
        Package = 30098,
        PackageTip = 30099,
        PointsRewards = 30100,
        PromoGiftCard = 20048,
        ThirdPartyGiftCard = 30101,
        Unknown = 30307,
        Visa = 2223,
        CashRounding = 11039606,
        DepositCharge = 11044934,
        CareCredit = 11045140,
        PaymentPlan = 11045148,
        DeclinedCardRefund = 11045431,
    }

    export enum PaymentRequestStatusEnum {
        ACHPending = 20006,
        Approved = 30046,
        Failed = 30047,
        Pending = 30045,
        SignatureVerificationPending = 30508,
    }

    export enum RequestStatusEnum {
        Pending = 0,
        Success = 200,
        AutoUpdate = 201, //Declined
        Failed = 400, //Declined
        Error = 401,
    }

    export enum VariantTypeEnum {
        Color = 2166,
        Height = 2167,
        Length = 2168,
        Quality = 2169,
        Shape = 2170,
        Size = 2171,
        Style = 2172,
        Type = 2173,
        Weight = 2215,
    }

    export enum DiscountTypeEnum {
        SpecialPrice = 2293,
        AmountOff = 2294,
        PercentOff = 2295,
        BuyGet = 2296,
        PromotionalGiftCard = 11045924,
    }

    export enum BasicMarketingClientSelectionEnum {
        Birthday = 30415,
        ClientsThatHaveNoShowed = 30419,
        OverdueClients = 30420,
        ClientsThatHaveReceivedServiceFromEmployee = 30422,
        ClientsWithCertainAmountOfVisits = 30421,
        ClientsWithAnAppointmentBooked = 30417,
        ClientsWithMembership = 20159,
        LastVisitNoFutureAppntmnt = 30416,
        NewClient = 30414,
        ReferralVisits = 30418,
        ActiveClient = 30504,
        AllClients = 10054,
        ClientsWithDiscounts = 11039851,
        ClientsWithGiftCardBalance = 11039852,
        ClientsWithMembershipAboutToExpire = 11039853,
        ClientsWithPoints = 11039854,
        ClientsSpentXamountOnServicesAndProducts = 11044920,
        ClientsAreDueIn = 11045036,
        ClientsPurchasedGiftCard = 11045095,
        ClientsPurchasedPackage = 11045035,
        ClientsPurchasedProductAnotProductB = 11045032,
        ClientsPurchasedServiceNotProduct = 11045034,
        ClientsPurchasedServiceAnotServiceB = 11045031,
        ClientsReceivedPackage = 11045033,
        ClientsWhoBecameAndWhoAreStillDueToPurchase = 11045672,
        ClientsWhoWillBecomeDueToPurchase = 11045673,
        ClientsWhoseMostRecentPurchaseOf = 11045674,
        COClientsWhoBecameAndWhoAreStillDueToPurchase = 11045694,
        COClientsWhoseMostRecentPurchaseOf = 11045695,
    }

    export enum BasicMarketingDateRangeCompareEnum {
        Before = 30391,
        Due = 30390,
        For = 30389,
        GreaterThan = 30388,
        Within = 30387,
        WithinLast = 20164,
        Between = 11045677,
        WasBetween = 11045675,
    }

    export enum BasicMarketingDateRangeSelectionEnum {
        FourteenDays = 30413,
        Last7Days = 30435,
        LastMonth = 30396,
        LastQuarter = 30409,
        LastWeek = 30394,
        NextMonth = 30407,
        NextQuarter = 30410,
        NextWeek = 30406,
        NinetyDays = 30400,
        OneDay = 30411,
        OneEightyDays = 30402,
        OneTwentyDays = 30401,
        PreviousDay = 30434,
        SevenDays = 30412,
        SixtyDays = 30399,
        ThirtyDays = 30398,
        ThisMonth = 30395,
        ThisQuarter = 30408,
        ThisWeek = 30393,
        Today = 30403,
        Tomorrow = 30404,
        TwentyFourHours = 30392,
        Any = 30505,
        ThisYear = 20160,
        LastYear = 20161,
        SinceLastWeek = 20181,
        SinceLastMonth = 20182,
        OneYear = 11039844,
        Custom = 11039891,
        NextYear = 11039857,
    }

    export enum DiscountEntityTypeEnum {
        Fees = 30535,
        GiftCard = 30466,
        Membership = 30497,
        Package = 2300,
        Product = 2297,
        ProductService = 2299,
        Service = 2298,
        RebookedAppointments = 50048
    }

    export enum DiscountBasedOnEnum {
        Quantity = 30285,
        Amount = 30286,
    }

    export enum SaleLineDiscountTypeEnum {
        CancellationFee = 30137,
        ItemOnSale = 30081,
        Manual = 30080,
        MembershipBenefit = 30079,
        NoShowFee = 30367,
        Predefined = 30078,
        WaivePayment = 30162,
        YieldManagement = 11045091,
    }

    export enum DiscountEntityGranularityTypeEnum {
        All = 2301,
        AllMembershipFees = 30536,
        AllPackageSwapUpgradeFees = 30537,
        Specific = 2302,
        SpecificManufacturer = 2303,
    }

    export enum DiscountRedeemableLimitPerTypeEnum {
        Client = 2291,
        Transaction = 2292,
    }

    export enum DiscountBuyGetTypeEnum {
        Buy = 2304,
        Get = 2305,
    }

    export enum AutoRenewTypeEnum {
        Both = 30457,
        Monthly = 2335,
        No = 2333,
        PaidInFull = 2334,
    }

    export enum AvedaVersionEnum {
        AvedaVersion2 = 11045757,
    }

    export enum ExternalInventoryAccessTypeEnum {
        Shared = 11045764,
        Individual = 11045765,
    }

    export enum MembershipLengthEnum {
        Days = 2325,
        Months = 2324,
    }

    export enum BillingTypeEnum {
        Manual = 2326,
        PaidInFull = 2327,
        RecurringPayments = 2328,
    }

    export enum RecurringPaymentScheduleEnum {
        SixMonths = 2331,
        Monthly = 2329,
        Quarterly = 2330,
        Yearly = 2332,
    }

    export enum InventoryCountStateEnum {
        Complete = 1688,
        Deleted = 2373,
        Open = 1686,
        Reviewing = 1687,
    }

    export enum AccruedServiceSharingEnum {
        Anyone = 2319,
        FamilyOnly = 2318,
        NoSharing = 2316,
        OnlyDefinedUsers = 2317,
    }

    export enum FreezeSettingEnum {
        AccruedServiceOnly = 2337,
        FullAccess = 2339,
        MemberBenefitsOnly = 2338,
        NoAccess = 2336,
    }

    export enum DefinedUserTypeEnum {
        Corporate = 2323,
        Family = 2322,
        MultiUser = 2321,
        Single = 2320,
    }

    export enum UserTypeEnum {
        User = 2074,
        TenantAPIUser = 2075,
        MSIUser = 2084
    }

    export enum TenantTypeEnum {
        JcPenney = 1,
        Me = 2
    }

    export enum ProductPackageTypeEnum {
        Case = 2366,
        Each = 2365,
    }

    export enum MerchantAccountProviderEnum {
        Vantiv = 1, /* Worldpay */
        VantivGC = 2, /* WorldpayGC */
        CardConnect = 3,
        PaymentSense = 4,
        ConnectE = 5,
        CareCredit = 6,
        Worldpay = Vantiv,
        WorldpayGC = VantivGC,
        Adyen = 7,
        AdyenCanada = 8,
        MeevoPay = Adyen,
        MeevoPayCanada = AdyenCanada,
    }

    export enum MerchantPlatformEnum
    {
		Vias = 11045798,
		Balance = 11045801,
	}

    export enum DecimalRoundingPlaceEnum {
        Hundredths = 2313,
        TenThousandths = 2315,
        Tenths = 2312,
        Thousandths = 2314,
    }

    export enum RoundingModeEnum {
        CashSwedish = 1634,
        Down = 1633,
        HalfDown = 1636,
        HalfEven = 1637,
        HalfUp = 1635,
        Up = 1632,
    }

    export enum CashRoundingModeEnum {
        RoundUp = 11045939,
        SwedishRounding = 11045940,
        RoundDown = 11045941,
    }

    export enum MemberTypeEnum {
        Primary = 2340,
        Secondary = 2341,
    }

    export enum PageStateEnum {
        CategoryList = 1,
        ReportList,
        Controls,
        Report,
    }

    export enum FeeTypeEnum {
        Enrollment = 2342,
        Recurring = 2343,
        Renewal = 2344,
    }

    export enum StorageTypeEnum {
        Client = 2157,
        CustomDataFile = 2346,
        CustomDataImage = 2345,
        Employee = 2158,
        PaymentType = 2278,
        WalkinSetting = 2283,
        BusinessInfo = 2389,
        StockImage = 2413,
        CustomBrandingLogoImage = 2414,
        CustomBrandingBackgroundImage = 2415,
        Product = 2417,
        SmartTileImage11 = 2419,
        SmartTileImage21 = 2420,
        SmartTileImage12 = 2421,
        SmartTileImage41 = 2422,
        SmartTileImage13 = 2423,
        CustomDataControlImage = 2500,
        SystemFile = 10058,
        EgiftTemplate = 30428,
        Service = 30527,
        SelfCheckinSetting = 50107,
        ClientAppBusinessLogo = 11045292,
        ClientAppLoginScreen = 11045293,
        ClientAppHomeScreen = 11045294,
        ClientAppPromoBackground = 11045295,
        ClientAppPromoImage = 11045296,
        VisitNoteImage = 11045322,
        MembershipDefinitionImage = 11045711,
        ClientGalleryImage = 11045831,
        ClientCustomBrandedAppScreen = 11045791
    }

    export class StorageFileHelper {
        static hasImageUri(m: IStorageFile) {
            return !(this.imageUri(m, '') === '');
        }

        static hasThumbUri(m: IStorageFile) {
            return !(this.thumbUri(m, '') === '');
        }

        static hasSourceUri(m: IStorageFile) {
            return !(this.sourceUri(m, '') === '');
        }

        static imageUri(m: IStorageFile, defaultUri: string) {
            if (m != undefined && m.meevoFile != undefined) {
                return this.searchFile(m, 'Formated', defaultUri);
            }
            return defaultUri;
        }

        static thumbUri(m: IStorageFile, defaultUri: string) {
            if (m != undefined && m.meevoFile != undefined) {
                return this.searchFile(m, 'Thumb', defaultUri);
            }
            return defaultUri;
        }

        static sourceUri(m: IStorageFile, defaultUri: string) {
            if (m != undefined && m.meevoFile != undefined) {
                return this.searchFile(m, 'Source', defaultUri);
            }
            return defaultUri;
        }

        static fileName(m: IStorageFile) {
            if (!m
                || !m.meevoFile
                || !m.meevoFile.fileName
                || !m.meevoFile.files
                || m.meevoFile.files.length === 0) {
                return m.meevoFile.files[0].extension;
            }

            return m.meevoFile.fileName + m.meevoFile.files[0].extension;
        }

        /**
         * search for the storage file URL if model implements FileMetadata
         */
        static searchFile(istoreObject, searchKey: string, defaultUrl: string): string {
            let istoreObj = istoreObject;
            let fileUrl = defaultUrl;
            if (istoreObj == undefined) {
                return defaultUrl;
            }

            if (istoreObj && istoreObj.meevoFile && istoreObj.meevoFile.files && istoreObj.meevoFile.files.length > 0) {
                istoreObj.meevoFile.files.forEach(setting => {
                    if (setting.key === searchKey) {
                        fileUrl = setting.fileUrl;
                        return fileUrl;
                    }
                });
            }
            return fileUrl;
        }
    }

    export enum ClientMembershipSearchStatusEnum {
        AllMembers = 20007,
        CCAboutToExpire = 20013,
        Expired = 20011,
        Grandfathered = 20051,
        InCancellation = 20012,
        Initial90 = 20008,
        InRenewalTerm = 20010,
        WithinInitialTerm = 20009,
        MembershipAboutToExpire = 20120,
        Active = 11045154,
    }

    export enum ClientMembershipIndicatorEnum {
        GreenStar = 0,
        BlueStar = 1,
        BlueStarWithRedX = 2,
        BlueStarYellowI = 3,
        BlueStarWithPause = 4,
        BlueStarWithPackage = 5,
        HollowedBlueStar = 6,
        RedStar = 7,
        RedCircleWithMinus = 8,
        RedCircleWithLine = 9,
        BlueStarRedTriangleI = 10,
        PlatinumStar = 11,
        SoldToday = 12,
        BlueStarWithPauseAndPlus = 13,
    }

    export enum ClientMembershipIndicatorStatusEnum {
        DueToExpire = 30107,
        Error = 30006,
        Expired = 30007,
        Frozen = 30003,
        InCancellation = 30005,
        Initial90 = 30106,
        InRenewal = 30105,
        NonCounting = 30108,
        SoldToday = 20004,
        Suspended = 30004,
        WithinInitialTerm = 30016,
        CCDueToExpire = 30109,
    }

    export enum ClientMembershipStatusEnum {
        Active = 30002,
        CancelBeforeEnrollment = 30110,
        Cancelled = 30008,
        Expired = 30009,
        InActive = 30010,
    }

    export enum ClientMembershipBillStatusEnum {
        Deleted = 30019,
        Error = 30013,
        Failed = 30014,
        NotBilled = 30011,
        Paid = 30012,
        Pending = 30018,
        Refund = 20005,
        SentToTransworld = 2435,
        Suspended = 30017,
        //Waived = 30015,
        WaivedAccrued = 30015,
        WaivedNotAccrued = 30016,
        FreezeFee = 30032,
        Processing = 0,
        RemovedDONOTSHOW = 2456,
    }

    export enum ClientMembershipBillFeeTypeEnum {
        FreezeFee = 30032,
        PaidInFullFreezeFee = 30215,
        RecurringFee = 30033,
        RenewalFee = 30034,
    }

    export enum PaymentAccountStatusEnum {
        Approved = 30051,
        Failed = 30052,
        Pending = 30050,
    }

    export enum PaymentAccountTypeEnum {
        ACHChecking = 30054,
        ACHSaving = 30055,
        Card = 30053,
    }

    export enum IntegrationStatusEnum {
        Open = 0,
        InProcess = 1,
        Success = 2,
        Skip = 3,
        Failed = 4,
    }

    export enum EmployeeLicenseEnum {
        Insurance = 2403,
        License = 2402,
    }

    export enum CommissionCalculationTypeEnum {
        CommissionOnly = 30035,
        SalaryAndCommission = 30037,
        SalaryOrCommission = 30036,
    }

    export enum CommissionScaleTypeEnum {
        Straight = 30038,
        TieredSliding = 30040,
        TieredStraight = 30039,
    }

    export enum ServiceCommissionAdjustmentTypeEnum {
        Commission = 30044,
        LaborCosts = 30042,
        NewClientDeductions = 30043,
        ShopCosts = 30041,
    }

    export enum MembershipCommissionSettingEnum {
        EmployeeSpiff = 30118,
        EnrollmentFee = 30117,
        RecurringFee = 30119,
    }

    export enum CommissionsSettingEnum {
        EmployeeSpiff = 30183,
        EnrollmentFee = 30184,
        RecurringFee = 30185,
    }

    export enum ClientMembershipCancellationStateEnum {
        Cancellation = 1,
        ImmediateCancellation,
        UndoCancellation,
    }

    export enum PointAdjustmentTypeEnum {
        Manual = 30076,
        Register = 30104,
        PointsReset = 11045090,
        Automatic = 11045938,
    }

    export enum PackageRedemptionTypeEnum {
        ExactMatch = 2002,
        Transfer = 2020,
        Merge = 2017,
        Refund = 2006,
        Split = 2016,
        Swap = 2003,
        Void = 2008,
    }

    export enum HoldTypeEnum {
        Billing = 30112,
        Recovery = 30113,
        Register = 30111,
        OnlineBooking = 11044924,
    }

    export enum PackageCreationTypeEnum {
        Custom = 2010,
        Merge = 2012,
        Predefined = 2009,
        Split = 2011,
    }

    export enum ProcessingStatusEnum {
        None,
        Success,
        Declined,
        Approved,
        Restart,
        Failed,
        HostError,
        Timeout,
        FailedVerification,
        CardError,
        UnknownCard,
        UnsupportedCard,
        DeviceError,
        Cancelled,
        ChipError,
        PinPadError,
        PinPadTimeout,
        InvalidLane,
        SwipedCardIsChipCapable,
        ApprovedByMerchant,
        ChipReaderError,
        UseChipReader,
        UseMagneticStripe,
        CardRemoved,
        CardBlocked,
        CardNotSupported,
        BadCard,
        CandidateListEmpty,
        IssuerAuthenticationFailed,
        CardDataEncryptionNotEnabled,
        ApprovedExceptCashback,
        ApplicationBlocked,
        AccountNotFound,
        MerchantAccountNotFound,
        ProcessorCommunicationError,
        RequestTimeout,
        PartialApproved,
        TerminalAlreadyLocked,
    }

    export enum SupplyCategoryEnum {
        Cleaning = 30129,
        KitchenSupply = 30131,
        OfficeSupply = 30130,
        Refreshment = 30132,
        Tools = 11039796,
        MedspaSupply = 11039797,
        NailSupply = 11039798,
        SpaSupply = 11039799,
        SalonSupply = 11039800,
        WaxingSupply = 11039801,
        OtherSupply = 11039802,
        LinensAndApparel = 11039803,
        RetailPackaging = 11039804,
        Housekeeping = 11039805,
        FoodAndBeverage = 11039806
    }

    export const SUPPLY_CATEGORY_DESCRIPTION = {
        [SupplyCategoryEnum.Cleaning]: 'LABEL_CLEANING_SUPPLIES',
        [SupplyCategoryEnum.KitchenSupply]: 'LABEL_KITCHEN_SUPPLIES',
        [SupplyCategoryEnum.OfficeSupply]: 'LABEL_OFFICE_SUPPLIES',
        [SupplyCategoryEnum.Refreshment]: 'LABEL_REFRESHMENTS',
        [SupplyCategoryEnum.Tools]: 'LABEL_TOOLS',
        [SupplyCategoryEnum.MedspaSupply]: 'LABEL_MEDSPA_SUPPLIES',
        [SupplyCategoryEnum.NailSupply]: 'LABEL_NAIL_SUPPLIES',
        [SupplyCategoryEnum.SpaSupply]: 'LABEL_SPA_SUPPLIES',
        [SupplyCategoryEnum.SalonSupply]: 'LABEL_SALON_SUPPLIES',
        [SupplyCategoryEnum.WaxingSupply]: 'LABEL_WAXING_SUPPLIES',
        [SupplyCategoryEnum.OtherSupply]: 'LABEL_OTHER_SUPPLIES',
        [SupplyCategoryEnum.LinensAndApparel]: 'LABEL_LINES_AND_APPAREL',
        [SupplyCategoryEnum.RetailPackaging]: 'LABEL_REATIL_PACKAGING',
        [SupplyCategoryEnum.Housekeeping]: 'LABEL_HOUSEKEEPING',
        [SupplyCategoryEnum.FoodAndBeverage]: 'LABEL_FOOD_AND_BEVERAGE',
    }

    export enum DrawerTypeEnum {
        Register = 2367,
        AppointmentBook = 2368,
        Membership = 2369,
        External = 2370,
        Conversion = 30128,
        Egift = 30578,
        OnlineBooking = 11044923,
        SelfPay = 11044967,
        eCommerce = 11045753
    }

    export enum ReturnReasonEnum {
        Damaged = 30138,
        Expired = 30139,
        Return = 30140,
    }

    export enum MarketPricingEntityEnum {
        Service = 1,
        Product = 2,
        Package = 3,
        Membership = 4,
        MemberBenefitTemplate = 5,
        Location = 6,
        PriceGroup = 7, //for internal usage
    }

    export enum ImageGalleryCategoryEnum {
        Business = 30141,
        Employee = 30142,
        ServiceCategories = 30143,
        Client = 30163,
    }

    export enum IncludedEmployeeEnum {
        AllEmployees = 1969,
        LoggedInEmployee = 1970,
        SpecificEmployee = 50001
    }

    export enum IsForAllEnum {
        FromSpecific = 1,
        ToSpecific = 2,
        FromAll = 4,
        ToAll = 8,
        ToEmployee = 16,
    }

    export enum NotificationMethodEnum {
        Sms = 1,
        Email = 2,
        OnceDay = 4,
    }

    export enum TipAmountTypeEnum {
        Amount = 20016,
        Percentage = 20015,
    }

    export enum ProcessorResponseCodeEnum {
        AuthorizationFailed = 20033,
        BalanceNotAvailable = 20028,
        CallIssuer = 20027,
        CommunicationError = 20036,
        Decline = 20022,
        Default = 20045,
        Duplicate = 20025,
        DuplicateApproved = 20024,
        Error = 20038,
        ExpiredCard = 20023,
        HostError = 20037,
        InvalidAccount = 20031,
        InvalidData = 20030,
        InvalidRequest = 20032,
        NotAllowed = 20034,
        NotDefined = 20029,
        OutOfBalance = 20035,
        PartialApproval = 20021,
        PickUpCard = 20026,
        Success = 20020,
        Returned = 10009,
        Pending = 10010,
        Queued = 10011,
        Unknown = 10012,
        Originated = 10013,
        Settled = 10014,
        MerchantAccountNotFound = 10015,
        SignatureRejected = 2454,
        ApprovedWithSignatureVerificationTimeout = 2455,
    }

    export enum ProactiveAlertTypeEnum {
        NoConfig = 0,
        GiftCardSoldLessThanValue = 1,
        SecuredFeatureAccessed = 2,
        SecurityOverrideOccured = 3,
        SpecificPaymentTypeIsUsed = 4,
        RefundWasGiven = 5,
        SpecificServiceIsRefunded = 6,
        SpecificProductIsRefunded = 7,
        TransactionIsVoided = 8,
        EmployeeClocksInEarlyOrLate = 9,
        EmployeeClocksOutEarlyOrLate = 10,
        ClosingCashIsOverShortMoreThanAmount = 11,
        GiftCardIsRedeemedMoreThanMonthsOld = 12,
        SpecificMembershipIsSold = 13,
        MembershipIsSold = 14,
        SpecificGiftCardTypeIsSold = 15,
        GiftCardIsRedeemedByNotOriginRecipient = 16,
        GiftCardIsRedeemedForAmountGreaterThanTicketTotal = 17,
        GiftCardIsRedeemedWithGiftCardPurchase = 18,
        AccessIsDeniedToACertainFeature = 19,
        AllOrSpecificServiceBookedViaOnlineBooking = 20,
        EmployeeWorksMore = 21,
        QuickbooksHasNotBeenSyncedForAmountOfTime = 22,
        EmployeeHasNotClockedIn = 23,
        AppointmentServiceAreCheckedIn = 24,
        SpecificReportIsRun = 25,
        SpecificServiceWasSold = 26,
        ProductIsLowOnInventory = 29,
        GifcardIsSoldOnline = 34,
        SmsHasReached = 35,
        SpecificPackageIsSold = 36,
        ServiceIsBooked = 37,
        RatingIsSubmitted = 39,
        AppointmentCancelledOnline = 40,
        SpecificProductIsSold = 43,
        AppointmentHasBeenCanceled = 44,
        DocsAndFormsHaveBeenSubmitted = 45,
        WaitListAppointmentIsAdded = 46,
        MarketingSmsHasReached = 47,
        MarketingEmailContactUsage = 48,
    }

    export enum ProactiveAlertTriggeredTypeEnum {
        OnlineBooking = 11044929,
        System = 11044930,
        EGift = 11044931,
        AccountSync = 11044932,
    }

    export enum MissedOpportunityTimeTypeTypeEnum {
        Afternoon = 30175,
        Evening = 30176,
        Morning = 30174,
        Specific = 30177,
    }

    export enum SmartTIleCategoryEnum {
        AppointmentBook = 30302,
        Employee = 30304,
        Membership = 30306,
        Register = 30303,
        System = 30305,
    }

    export enum SmartTileTypeEnum {
        Data = 30073,
        Image = 30075,
        Shortcut = 30074,
    }

    export enum LocationStatusEnum {
        Active = 30188,
        TemporarilyClosed = 30189,
        ClosingPlanned = 30190,
        Closed = 30191,
    }

    export enum BusinessInfoSettingEnum {
        EnableOnlineBooking = 7000,
        EnableAutoBillingService = 3008000,
        EnableMembershipBilling = 3008001,
        EFTToBeProcessedStatusEnum = 3008002,
        EnableAppointmentNotification = 3009000,
        EnableThirdPartyIntegration = 3010000,
        EnableOnlineWalkinManager = 3011000,
    }

    export enum CancellationTypeEnum {
        UserDefined = 2080,
        StandingCancelled = 2081,
        OnlineBookingCancellation = 2449,
        StandingConflictCancelled = 20091,
    }

    export enum ProcessTypeEnum {
        Create = 30210,
        Delete = 30211,
        Refresh = 30212,
        Suspend = 30213,
        Reactivate = 30214,
    }

    export enum SandboxProcessStatusEnum {
        Pending = 30253,
        InProcess = 30254,
        Succeeded = 30255,
        Failed = 30256,
        Cancelled = 30257,
    }

    export enum SandboxStatusEnum {
        Pending = 30246,
        InProcess = 30247,
        Active = 30248,
        Failed = 30249,
        Cancelled = 30250,
        Deleted = 30251,
        Suspended = 30252,
    }

    export enum BISecurityAccessTypeEnum {
        None = 11045021,
        Employee = 11045022,
        FullLocation = 11045023
    }

    export enum BOGOBasedOnTypeEnum {
        Amount = 30286,
        Quantity = 30285,
    }

    export enum EGiftTemplateCatEnum {
        AllOccasion = 30341,
        Anniversary = 30342,
        AnyOccasion = 30343,
        BabyShower = 30344,
        Birthday = 30345,
        Congratulations = 30346,
        FathersDay = 30347,
        Holiday = 30348,
        MothersDay = 30349,
        ThankYou = 30350,
        Valentine = 30351,
        Wedding = 30352,
    }

    export enum ClientsIncludedEnum {
        All = 30437,
        MembersOnly = 30438,
        NonMembersOnly = 30439,
    }

    export enum PointPromotionTypeEnum {
        RebookingNextAppointmentFirst = 20183,
        RebookingNextAppointmentAny = 20184,
        Birthday = 30454,
        BookingAppointmentOnline = 30447,
        ClientFirstVisit = 30455,
        GiftCardPurchase = 30441,
        GiftCardPurchaseTimesPurchasePrice = 11045199,
        MembershipPurchase = 30442,
        NewService = 30449,
        NewServiceCategory = 30448,
        PackagePurchase = 30443,
        RebookingNextAppointment = 30444,
        ReferringAnotherClient = 30440,
        RetailPurchase = 30450,
        RetailPurchaseMinimumAmount = 30445,
        ServiceCategoryPurchaseMinimumTimes = 30453,
        ServicePurchase = 30451,
        ServicePurchaseMinimumAmount = 30446,
        ServicePurchaseMinimumTimes = 30452,
        ServiceCategoryFirstTime = 30558,
        MembershipEFT = 11039713,
    }

    export enum SpecificItemTypeEnum {
        Any = 11045919,
        ServiceCategory = 11045920,
        Service = 11045921,
        BirthMonth = 11045934,
        BirthDay = 11045935,
    }

    export enum CreditCardSignatureFormatEnum {
        Base64PNG = 10022,
        Unknown = 20106,
        PointsLittleEndian = 20107,
        Base64GZippedBMP = 20108,
    }

    export enum DocsAndFormsTriggerEventEnum {
        Booked = 30519,
        CheckedIn = 30520,
        CheckedOut = 30521,
    }

    export enum FilterWildcardType {
        StartsWith,
        EndsWith,
        Contains,
    }

    export enum SessionState {
        LogOut = 0,
        Lockout = 1,
        LogIn = 2,
        Refreshing = 3,
    }

    export enum MerchantTerminalTypeEnum {
        Physical = 30571,
        Hosted = 30572,
    }

    export enum EmployeeEntityTypeEnum {
        Employee = 10034,
        Teacher = 10035,
        Student = 10036
    }

    export enum ReportsCategoryEnum {
        Analytical = 1,
        Appointments = 2,
        Clients = 3,
        Employee = 18,
        FrontDesk = 4,
        Inventory = 6,
        Liabilities = 16,
        Memberships = 17,
        Payroll = 7,
        Products = 8,
        Referrals = 9,
        Register = 10,
        Schedules = 12,
        WalkIns = 15,
    }

    export enum MeevoMessengerAutoRepliesEnum {
        DefaultAutoReplyMessage = 0,
        HolidayAutoReplyMessage = 1,
        OutsideHoursAutoReplyMessage = 2,
        CustomAutoReply
    }

    export enum SmartTileIds {
        EstimatedSales = 4,
        ClientsWithAppointments = 6,
        HoursNotSold = 7,
        NewClientsWithAppointments = 8,
        RebookedClients = 12,
        PercentageBooked = 21,
        NetSales = 22,
        TopServicesPerformed = 38,
        FiveStarRating = 42,
        MonthlyRecurringRevenue = 43,
        MembershipConversion = 44,
        ChurnMonthlyRecurringRevenue = 45,
        ExpansionMonthlyRecurringRevenue = 46,
        GrowthMonthlyRecurringRevenue = 47,
        DateCounter = 49,
        ClientBirthdayAnniversary = 50,
        EmployeeBirthdayAnniversary = 52,
        ServiceCategorySales = 53,
        ScheduledServices = 3,
        AppointmentCount = 37,
        ServiceProviderRequests = 54,
        TopServicesBooked = 55,
        AppointmentsToReschedule = 36,
        ProductCategorySales = 56,
        AverageSales = 57,
        WaitList = 39
    }

    export enum TransferStatusEnum {
        None = 0,
        Saved = 1,
        Deleted = 2,
        Transferred = 4,
        Returned = 8, //ReturnNotReceived
        Recalled = 16,
        Received = 32,
        ReturnReceived = 40
    };
    export enum ReceivalStatusEnum {
        NotReceived = 0,
        Returned = 8,
        Received = 32
    };
   /* export enum TransferStatusEnum {
        Saved = 11045068,
        Transferred = 11045069,
        Deleted = 11045070,
        ReturnNotReceived = 11045071,
        ReturnReceived = 11045072,
        Recall = 11045073
    }

    export enum ReceivalStatusEnum  {
        NotReceived = 11045074,
        Received = 11045075,
        Returned = 11045076
    }*/

    export enum FormTypeEnum {
        Standard = 10017,
        Form = 10018,
        Intake = 20167,
        Charts = 11045861,
    }

    export enum ClientNoteViewTimeEnum {
        AppointmentBooked = 20169,
        CheckIn = 20170,
        CheckOut = 20171,
    }
    export enum StepOverrideStageEnum {
        None,
        Existing,
        Add,
        Update,
        Remove
    }

    export enum SaleNoteTypeEnum {
        Transaction = 50102,
        Discount = 50103,
        Refund = 50104,
    }

    export enum SaleStageEnum {
        Initial,
        PreFinalize,
        Finalize,
        Payment
    }

    export enum ModuleEnum {
        System = 2206,
        AppointmentBook = 2207,
        Client = 2208,
        User = 2209,
        Notification = 2210,
        WalkInManager = 2211,
        OnlineBooking = 2212,
        Register = 2213,
        Tax = 2214,
        Security = 2404,
        Membership = 2405,
        Service = 2406,
        RedirectService = 10016,
        StudentTeacher = 10037,
        AlertManager = 20053,
        ActivityLog = 20054,
        Support = 20055,
        Textel = 20138,
        SalesRanking = 20149,
        ClientIntakeForm = 20155,
        DemandForce = 20174,
        BillingService = 30196,
        Integration = 30197,
        Branding = 30216,
        BusinessType = 30217,
        CallCenter = 30218,
        Discount = 30219,
        Employee = 30220,
        Event = 30221,
        GiftCard = 30222,
        Inventory = 30223,
        IPRestriction = 30224,
        LocationGroup = 30225,
        MarketPricing = 30226,
        MerchantAccount = 30227,
        Package = 30228,
        Payroll = 30229,
        POSHardware = 30230,
        ProactiveAlert = 30231,
        Product = 30232,
        ProductBundle = 30233,
        Report = 30234,
        Resource = 30235,
        Sale = 30236,
        ServiceAddOn = 30237,
        SmartCenter = 30238,
        TimeClock = 30239,
        UDF = 30240,
        WaitList = 30241,
        GroupBooking = 30242,
        ClassBooking = 30243,
        Upsell = 30244,
        FormBuilder = 30245,
        Department = 30282,
        ClientPoints = 30308,
        OverallDepartmentGoals = 30364,
        MeevoStore = 30365,
        EmployeeGoals = 30368,
        MarketingEmma = 30376,
        SandBox = 30377,
        BasicMarketing = 30386,
        eGift = 30430,
        QuickBooks = 30431,
        SalonClouds = 30432,
        GoalsDashboard = 30456,
        DocsAndForms = 30506,
        Paging = 30516,
        Transworld = 30525,
        RatingsControlPanel = 30559,
        TechSphere = 30575,
        SharedEmployee = 50006,
        SalesforceSupport = 50007,
        IntelliBooking = 50020,
        AreaManager = 50047,
        TextelBlast = 11039686,
        Worldpay = 11039893,
        CardConnect = 11039894,
        Octopi = 11044713,
        VanguardID = 11044712,
        PaymentPlans = 11045115,
        SalonCloudsPlusECommerce = 11045149,
        SalonCloudsPlusTele = 11045092,
        SalonCloudsPlusSnapSMSTexting = 11044933,
        SnapLiteBySalonCloudsPlus = 11045028,
        Tippy = 11045197,
        Vish = 11045164,
        ClientApp = 11045205,
        SalonInteractiveEcommercePerfected = 11045446,
        SalonInteractiveDigitizedSalonSolution = 11045447,
        MeetYourStylist = 11045443,
        TrueLark = 11045444,
        SalonScale = 11045445,
        AnalyticalReports = 11045597,
		SalonCloudsMarketing = 11039653,
        PowerBI = 11045614,
        AfterPose = 11045658,
        ImpCOReports = 11045699,
		Glammatic = 11045706,
        Concerto = 11045707,
        ADPWiselyIntegration = 11045359,
        MeevoAiConvobar = 11045740,
        MeevoAiChat = 11045755,
        CustomBrandedApp = 11045777,
        MeevoAiTextarea = 11045822,
        Flossie = 11045832,
        TwoWaySMS = 11045797,
        TwoWaySMSOnboarding = 11045828,
        Mya = 11045901,
        BookingBee = 11045895,
        MyAdvice = 11045900,
        Edge = 11045902,
        ClientRecordManagement = 11045911
    }

    export enum MeevoApiResponseCodeEnum {
        ClientFrequencyOfVisit = 20121,
        EmployeeDoesNotPerformService = 20122,
        EmployeeDoubleBooked = 20123,
        EmployeeNotScheduled = 20124,
        ResourceNotScheduled = 20125,
        AppointmentRequestPayingClientDuplicate = 20126,
        AppointmentRequestExistingAppointmentService = 20127,
        WalkinEmployeeDoubleBooked = 20128,
        AppointmentServiceGenderPreferenceConflict = 20129,
        EmployeeBookingRestrictions = 20130,
        EmployeeRequestedChanged = 20131,
        LocationUnavailable = 20132,
        ResourceUnavailable = 20133,
        EquipmentUnavailable = 20134,
        EmployeeUnavailable = 20135,
        ServiceUnavailable = 20136,
        ServiceNotBookable = 20137
    }

    export enum ComparisonTypeEnum {
        IsEqualOrHigherThan = 11039665,
        IsEqualOrLowerThan = 11039666,
        IsEqualTo = 11039667
    }

    export enum ChargeTypeEnum {
        EmployeeFee = 11039743,
        HouseFee = 11039744,
        EmployeeTip = 11039745,
        HouseTip = 11039746,
        PackageFee = 11039747,
        TicketCharge = 11039884,
    }

    export enum PayrollInputTypesEnum {
        Hourly = 11039715,
        Overtime = 11039716,
        DailyExtOvertime = 11039717,
        SevenDayOvertime = 11039718,
        SevenDayExtOvertime = 11039719,
        EightHourPlusOvertime = 11039720,
        RetailCommAmt = 11039721,
        OverrideRetailCommAmt = 11039722,
        ServiceCommAmt = 11039723,
        RequestedIncentiveAmt = 11039724,
        OverrideServiceCommAmt = 11039725,
        ServiceTicketChargeAmt = 11039726,
        ServiceToRetailBonusAmt = 11039727,
        TipAmount = 11039728,
        PackageComm = 11039729,
        PackageSpiffCommAmt = 11039730,
        MembershipEnrollmentComm = 11039731,
        MembershipReoccurringPaymentComm = 11039732,
        MembershipSpiffCommAmt = 11039733,
        SalaryAmt = 11039734,
        BoothRentalFee = 11039735,
        Holiday = 11039736,
        SickTime = 11039876,
        ServiceDeduction = 11039877
    }

    export enum PayrollAdpRequestStatusEnum {
        NotSent = 11039777,
        Sent = 11039778,
        FailedToSend = 11039779
    }

    export enum SmartTileRebookingType {
        Rebooked,
        NotRebooked
    }

    export enum WorkActivityCategoryEnum {
        None = 11039689,
        Holiday = 11039690,
        SickTime = 11039691,
        Vacation = 11045320
    }

    export enum ApplyServiceDeductionsEnum {
        BeforeCommissionIsCalculated = 11039847,
        AfterCommissionIsCalculated = 11039848
    }

    export enum ShowPromptForRebookEnum {
        UponCheckOut = 11044925,
        AfterSelectingPayNowInRegister = 11044926,
        Never = 11044927
    }

    export enum AdpIntegrationTypeEnum {
        Run = 11045093,
        WorkforceNow = 11045094
    }

    export enum SecurityEntityTypeEnum {
        PaymentType = 1,
        DocsAndForms = 2,
        UdfClient = 3,
        UdfEmployeee = 4,
        UdfProduct = 5,
        UdfService = 6,
    }

    export enum AccessLevelEnum {
        Admin = 11045325,
        Read_Only = 11045326,
        No_Access = 11045327,
    }

    export enum NotificationMessageEnum {
        SelfPayReceipt = 11045351,
        SelfPayReminderReceipt = 11045352,
    }

    export enum ReferralThankYouDateRangeEnum {
        Yesterday = 11045517,
        LastWeek = 11045518,
        LastMonth = 11045519,
    }

    export enum ElementTypeEnum {
        Text = 11045289,
        Button = 11045290,
        Image = 11045291,
    }

    export enum VerticalAlignmentEnum {
        Top = 11045210,
        Middle = 11045211,
        Bottom = 11045212,
    }

    export enum HorizontalAlignmentEnum {
        Left = 11045214,
        Center = 11045215,
        Right = 11045216,
    }

    export enum LiabilitiesMR035Enum {
        GiftCards = 11045563,
        Memberships = 11045564,
        Packages = 11045565,
    }

    export enum ShowThisPromotionUponLoginEnum {
        OnlyOnce = 11045287,
        EveryTimeClientLogsIn = 11045288,
    }

    export enum ClientIndicatorEnum {
		ClientPoints = 11045432,
		ClientMemberships = 11045433,
		ClientAccountBalance = 11045434,
		ClientPackages = 11045435,
		ClientGiftCards = 11045436,
		ClientCancelledOrNoShowAppt = 11045437,
        ClientTransworld = 11045487,
		ClientPaymentPlans = 11045488,
        ClientMinor = 11045489,
        ClientRestricted = 11045490,
        ClientType = 11045491,
        ClientPaymentAccounts = 11045494,
        ClientAlerts = 11045495,
        ClientNotes = 11045503,
	}

    export enum ClientPaymentPlanStatusEnum {
        Active = 11045279,
        Cancelled = 11045280,
        Completed = 11045281,
    }

    export enum ReportDateRangeBasedOnEnum {
        DateServiceCreatedOn = 11045543,
        DateServiceBookedFor = 11045544,
	}

    export enum MassUpdateActionEnum
    {
        Increase = 1,
        Decrease = 2,
        Set = 3
    }

    export enum PriceCategoryEnum
    {
        Wholesale = 1,
        Retail = 2,
        Markup = 3
    }

    export enum SmartMoverHiddenValue {
        Ids = 1,
        EmptyArray = 2
    }

    export enum ImportStatusEnum {
		New = 11045635,
		Processing = 11045636,
		Imported = 11045637,
		Failed = 11045638,
		Published = 11045639,
		PublishedFailed = 11045640,
		InitiatePublishing = 11045642,
		Void = 11045653,
    }

	export enum ExportStatusEnum {
		New = 11045631,
		Processing = 11045632,
		Failed = 11045633,
		FileGenerated = 11045634,
        Void = 11045652,
    }

    export enum SortReportEnum
    {
        Location = 11045588,
        Date = 11045589,
        ItemType = 11045590
    }

    export enum PhoneVerificationStatusEnum {
        Rejected = 11045659,
        Submitted = 11045660,
        PendingVerification = 11045661,
        Verified = 11045662,
        NotSubmitted = 11045663,
    }

    export enum PhoneVerificationWorkFlowEnum {
        NorthAmerica = 11045666,
    }

    export enum AppointmentSaveOriginEnum
    {
        Unspecified = 0,
        AppointmentEditor = 1
    }

    export enum PrimaryAppointmentStyleSelectionEnum
    {
        UseAppointmentCategory = 11045689,
        UseServiceCategory = 11045690
    }

    export enum FundingSourceEnum
    {
        Credit = 11045720,
        Debit = 11045721,
        Prepaid = 11045722,
        Unknown = 11045723,
        NotCaptured = 11045724,
    }

    export enum ReportRenderingEnum {
        Telerik = 11045744,
        Kendo = 11045745,
    }

    // string enum not available in TS 2.3.4
    export type HostedPageIdType =
        'HostedPageAddCreditPaymentAccount' |
        'HostedPageClientCreditCard' |
        'HostedPageCareCreditAdmin' |
        'HostedPageCareCreditPayment' |
        'HostedPageCreditPayment' |
        'HostedPageCardOnFileSecurityCode';

    export enum ProductVariantChargeTypeEnum {
        FlatFee = 11045696,
        PerUnit = 11045697,
        None = 11045719,
    }

    export enum HostedPageIntent
    {
        RetailKeyed = 1,
        ECommerce = 2,
        Recurring = 3,
        Installment = 4,
    }

    export enum EgiftMultiPurchaseFlow {
        Streamlined, // one design allowed with multiple recipients
        Detailed, // always force design selection when adding egifts
        Hybrid // default design selection when adding egifts
    }

    export enum PackageTypeCategoryEnum {
        Standard,
        ProductUsageTracking,
    }
}
